import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  Radio,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  LinearProgress,
  MenuItem,
  Select,
  Skeleton,
  Snackbar,
  Stack,
  Tabs,
  Tab,
  TextField,
  Tooltip,
  Typography,
  Switch,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  useMediaQuery,
  useTheme
} from '@mui/material';
import VideocamIcon from '@mui/icons-material/Videocam';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveIcon from '@mui/icons-material/Save';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import CropFreeIcon from '@mui/icons-material/CropFree';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/EditOutlined';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import CloseIcon from '@mui/icons-material/Close';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import FaceRetouchingNaturalIcon from '@mui/icons-material/FaceRetouchingNatural';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import { getCameraStreams, getDetectionSettings, updateDetectionSettings, getActivityFeatureSettings, updateActivityFeatureSettings, getCameraHealthPreview, getConfiguredCameraLivePreviewMseUrl, getDeepStreamPreviewMseUrl, stopDeepStreamPreviewWatch, getCameraRtspStatus, checkWebViewHealth, checkDetectAppHealth, getStoredToken, getLateEntryTime, updateLateEntryTime, getFrameForROI, saveROIConfig, getROIConfig, addCameraStream, deleteCamera, updateCameraStream, editCameraDetails, getRtspSnapshot } from '../utils/api';
import ConfiguredMsePlayer from './ConfiguredMsePlayer';
import { baseURL, buildApiUrl } from '../config/api';
import { formatDate, formatDateWithSeconds } from '../utils/dateUtils';
import { useConfirmDialog } from '../contexts/ConfirmDialogContext';
import ROIDrawer from './ROIDrawer';
import FaceCropModal from './FaceCropModal';
import PageHeader from './PageHeader';
import { CameraConfigCardShimmer } from './shimmer-ui/CameraConfigCard';
import { getMaxStreamPlayOnCard } from '../utils/getMaxStreamPlayOnCard';

const TRACKER_DEFAULTS = {
  track_thresh: 0.4,
  new_track_thresh: 0.35,
  match_thresh: 0.8,
  track_buffer: 150,
  track_high_thresh: 0.6,
  track_low_thresh: 0.1,
  max_age: 30,
  min_hits: 4,
  iou_thresh: 0.3,
  mot20: false,
  fuse_score: true,
};

const TRACKER_FIELD_CONFIG = [
  // { name: 'track_thresh', label: 'Track Threshold', step: 0.01, type: 'float' },
  // { name: 'new_track_thresh', label: 'New Track Threshold', step: 0.01, type: 'float' },
  // { name: 'match_thresh', label: 'Match Threshold', step: 0.01, type: 'float' },
  // { name: 'track_buffer', label: 'Track Buffer (frames)', step: 1, type: 'int' },
  // { name: 'track_high_thresh', label: 'High Threshold', step: 0.01, type: 'float' },
  // { name: 'track_low_thresh', label: 'Low Threshold', step: 0.01, type: 'float' },
  // { name: 'max_age', label: 'Max Age', step: 1, type: 'int' },
  // { name: 'min_hits', label: 'Min Hits', step: 1, type: 'int' },
  // { name: 'iou_thresh', label: 'IoU Threshold', step: 0.01, type: 'float' },
];

const TRACKER_BOOL_FIELDS = [
  // { name: 'mot20', label: 'Use MOT20 tuning' },
  // { name: 'fuse_score', label: 'Fuse score during matching' },
];

const CONFIDENCE_DEFAULT = 0.83;

const CAMERA_PAGE_SIZE = 6;

const MAX_STREAM_PLAY_ON_CARD = getMaxStreamPlayOnCard(
  process.env.REACT_APP_MAX_STREAM_PLAY_ON_CARD
);
const hasFiniteCardStreamLimit = Number.isFinite(MAX_STREAM_PLAY_ON_CARD);

const getCameraAssignmentLabel = (camera) => {
  switch (String(camera?.camera_type || '').toUpperCase()) {
    case 'IN':
      return 'Assigned-IN';
    case 'OUT':
      return 'Assigned-OUT';
    case 'ACTIVITY':
      return 'Assigned-ACTIVITY';
    default:
      return 'Unassigned';
  }
};

const normalizeRoiConfig = (config) => {
  if (!config) return null;
  const rect = Array.isArray(config.rect) ? config.rect : null;
  const roi = config.roi || (rect && rect.length >= 4
    ? { x: rect[0], y: rect[1], w: rect[2], h: rect[3] }
    : null);
  return {
    ...config,
    roi,
    lane_rois: config.lane_rois || {},
    number_of_lane: config.number_of_lane ?? 0,
  };
};

const isCameraAssigned = (camera) =>
  ['IN', 'OUT', 'ACTIVITY'].includes(String(camera?.camera_type || '').toUpperCase());

const PREVIEW_ZOOM_MIN = 1;
const PREVIEW_ZOOM_MAX = 5;
const PREVIEW_ZOOM_STEP = 0.25;
/** Keep the configured live preview to one active MSE connection. */

const DEFAULT_PREVIEW_QUALITY = 'medium';
const PREVIEW_QUALITY_INDEX = { high: 0, medium: 1, low: 2 };
const PREVIEW_QUALITY_LABELS = { high: 'High', medium: 'Medium', low: 'Low' };
const CAMERA_OFFLINE_PREVIEW_MESSAGE = 'Oops! Camera is offline. Preview not available.';

const isDeepStreamRoiPreviewCamera = (camera) => {
  const cameraType = String(camera?.camera_type || '').toUpperCase();
  return cameraType === 'IN' || cameraType === 'OUT';
};
const DEEPSTREAM_PREVIEW_UNAVAILABLE = {
  title: 'Detect Output Preview is not working',
  body: 'Detect app is not sending this camera’s overlay right now. Please check the Detect app logs for more information.',
};
const DEEPSTREAM_PREVIEW_GO2RTC_FAILED = {
  title: 'Go2rtc MSE preview is not working',
  body: 'Go2rtc is not sending this camera’s overlay right now. Please check the Go2rtc app logs for more information.',
};

const deepStreamPreviewErrorFromClose = (detail = {}) => {
  const reason = String(detail.reason || '');
  if (reason === 'ds-go2rtc-register-failed') return DEEPSTREAM_PREVIEW_GO2RTC_FAILED;
  if (
    reason === 'ds-rtsp-out-down'
    || reason === 'DeepStream preview unavailable'
    || Number(detail.code) === 1011
  ) {
    return DEEPSTREAM_PREVIEW_UNAVAILABLE;
  }
  return DEEPSTREAM_PREVIEW_UNAVAILABLE;
};

const PreviewUnavailableNotice = ({ error }) => {
  const title = error && typeof error === 'object' ? error.title : '';
  const body = error && typeof error === 'object' ? error.body : (error || 'Preview unavailable');
  return (
    <Box sx={{ px: 3, maxWidth: 420, textAlign: 'center' }}>
      {title ? (
        <Typography variant="subtitle1" sx={{ color: 'grey.100', fontWeight: 600, mb: 1 }}>
          {title}
        </Typography>
      ) : null}
      <Typography variant="body2" sx={{ color: title ? 'grey.400' : 'text.secondary' }}>
        {body}
      </Typography>
    </Box>
  );
};

const getOrderedCameraStreams = (camera) => {
  if (!camera) return [];
  if (Array.isArray(camera.streams) && camera.streams.length) {
    return camera.streams
      .map((s) => ({ streamId: s.id || null, streamUrl: (s.stream_url || '').trim() }))
      .filter((s) => s.streamUrl);
  }
  const urls = Array.isArray(camera.stream_urls) && camera.stream_urls.length
    ? camera.stream_urls.filter(Boolean)
    : camera.rtsp_url ? [camera.rtsp_url] : [];
  return urls.map((url) => ({ streamId: null, streamUrl: String(url).trim() })).filter((s) => s.streamUrl);
};

const getPreviewQualityOptions = (streamCount) => {
  if (streamCount <= 1) return ['high'];
  if (streamCount === 2) return ['high', 'medium'];
  return ['high', 'medium', 'low'];
};

const normalizePreviewQuality = (camera, preferred = DEFAULT_PREVIEW_QUALITY) => {
  const options = getPreviewQualityOptions(getOrderedCameraStreams(camera).length);
  return options.includes(preferred) ? preferred : options[options.length - 1];
};

const resolvePreviewStreamForQuality = (camera, quality) => {
  const streams = getOrderedCameraStreams(camera);
  if (!streams.length) return null;
  const idx = Math.min(PREVIEW_QUALITY_INDEX[quality] ?? 1, streams.length - 1);
  return streams[idx];
};

const formatConfidenceValue = (value, fallback = CONFIDENCE_DEFAULT) => {
  if (typeof value === 'number' && !Number.isNaN(value)) {
    return String(value);
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    if (!Number.isNaN(parsed)) {
      return String(parsed);
    }
  }
  return String(fallback);
};

const formatIntervalValue = (value, fallback = '') => {
  if (typeof value === 'number' && !Number.isNaN(value)) {
    return String(value);
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    if (!Number.isNaN(parsed)) {
      return String(parsed);
    }
  }
  return fallback;
};

const parseConfidenceInput = (value, label) => {
  const raw = typeof value === 'string' ? value.trim() : '';
  if (raw === '') {
    throw new Error(`${label} is required.`);
  }
  const parsed = Number(raw);
  if (Number.isNaN(parsed) || parsed < 0 || parsed > 1) {
    throw new Error(`${label} must be between 0 and 1.`);
  }
  return parsed;
};

const parseIntervalInput = (value) => {
  const raw = typeof value === 'string' ? value.trim() : '';
  if (raw === '') {
    throw new Error('Recognition cooldown is required.');
  }
  const parsed = Number(raw);
  if (Number.isNaN(parsed) || parsed < 0) {
    throw new Error('Recognition cooldown must be zero or greater.');
  }
  return parsed;
};

const parsePositiveIntegerInput = (value, label) => {
  const raw = typeof value === 'string' ? value.trim() : '';
  if (raw === '') {
    throw new Error(`${label} is required.`);
  }
  const parsed = Number(raw);
  if (!Number.isInteger(parsed) || parsed < 1) {
    throw new Error(`${label} must be a positive integer.`);
  }
  return parsed;
};

const createTrackerStateFromData = (data = {}) => {
  const state = {};
  TRACKER_FIELD_CONFIG.forEach((field) => {
    const value = data[field.name];
    state[field.name] = value === undefined || value === null
      ? String(TRACKER_DEFAULTS[field.name])
      : String(value);
  });
  TRACKER_BOOL_FIELDS.forEach((field) => {
    const value = data[field.name];
    state[field.name] = value === undefined || value === null
      ? TRACKER_DEFAULTS[field.name]
      : Boolean(value);
  });
  return state;
};

export const PRIMARY_COLOR = '#40518A';

const SharedCameraStreamPreview = ({ cameraId, viewportRefs, onError }) => {
  const canvasRef = useRef(null);
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  useEffect(() => {
    let active = true;
    let animationFrameId = null;
    let errorReported = false;

    const drawFrame = () => {
      if (!active) return;
      const host = viewportRefs.current[cameraId]?.querySelector('eye-preview-rtc');
      const video = host?.video || host?.querySelector?.('video');
      const canvas = canvasRef.current;

      if (video && canvas && video.videoWidth > 0 && video.videoHeight > 0) {
        try {
          if (canvas.width !== video.videoWidth || canvas.height !== video.videoHeight) {
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
          }
          const context = canvas.getContext('2d');
          if (!context) throw new Error('Unable to create a canvas context for the shared camera preview');
          context.drawImage(video, 0, 0, canvas.width, canvas.height);
        } catch (error) {
          if (!errorReported) {
            errorReported = true;
            onErrorRef.current?.(error);
          }
        }
      }

      // Always look up the current <video>. A quality switch replaces the
      // element; a video-frame callback on the old node would freeze the canvas.
      animationFrameId = window.requestAnimationFrame(drawFrame);
    };

    drawFrame();
    return () => {
      active = false;
      if (animationFrameId !== null) window.cancelAnimationFrame(animationFrameId);
    };
  }, [cameraId, viewportRefs]);

  return (
    <canvas
      ref={canvasRef}
      aria-label="Shared live camera preview"
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        objectFit: 'contain',
        background: '#111',
      }}
    />
  );
};

const CameraStreams = ({ viewMode = 'camera-streams' }) => {
  const { confirm } = useConfirmDialog();
  const [cameraStreamState, setCameraStreamState] = useState(null);
  const streams = cameraStreamState || [];
  const cameraStreamsRequestRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cameraStatusMap, setCameraStatusMap] = useState({});
  const [cameraStatusLoading, setCameraStatusLoading] = useState({});
  const cameraRefreshInFlightRef = useRef(new Set());
  const activeCardStreamsRef = useRef({});
  const [visibleCameraCount, setVisibleCameraCount] = useState(CAMERA_PAGE_SIZE);
  const [loadingMoreCameraCount, setLoadingMoreCameraCount] = useState(0);
  const [detectionSettingsTab, setDetectionSettingsTab] = useState('assignments');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // < 600px
  const isAssignmentCompact = useMediaQuery(theme.breakpoints.down('xl'));
  const cameraCardsOnly = true;

  // Separate state for each column's selected camera
  const [leftCameraId, setLeftCameraId] = useState('');
  const [rightCameraId, setRightCameraId] = useState('');

  // State for updating camera type
  const [updatingCameraId, setUpdatingCameraId] = useState(null);
  
  // State for syncing cameras from file
  const [syncingFromFile, setSyncingFromFile] = useState(false);

  // Snackbar state for notifications
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const renderUserTimestampCell = (nameValue, timestampValue) => {
    const name = nameValue || '-';
    return (
      <Stack spacing={0.25}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {name}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {formatDate(timestampValue)}
        </Typography>
      </Stack>
    );
  };

  const renderRoiAssignedManageActions = (
    camera,
    cameraType,
    {
      showFaceCrop = true,
      showDeAssign = false,
      deAssignDisabled = false,
      fullWidth = false,
      showDrawRoi = true,
    } = {},
  ) => {
    const primary = '#40518A';

    return (
      <Stack
        direction={fullWidth ? 'column' : 'row'}
        spacing={0.75}
        useFlexGap
        flexWrap="wrap"
        justifyContent={fullWidth ? 'stretch' : 'flex-end'}
        alignItems={fullWidth ? 'stretch' : 'center'}
      >
        {showDeAssign && (
          <Button
            size="small"
            variant="outlined"
            color="warning"
            startIcon={<LinkOffIcon fontSize="small" />}
            onClick={() => handleOpenDeactivateConfirm(camera)}
            disabled={deAssignDisabled}
            fullWidth={fullWidth}
            sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
          >
            De-Assign
          </Button>
        )}
        <Button
          size="small"
          variant="outlined"
          startIcon={<VisibilityOutlinedIcon fontSize="small" />}
          onClick={() => handleOpenRoiAssignedCameraPreview(camera)}
          fullWidth={fullWidth}
          sx={{ textTransform: 'none', whiteSpace: 'nowrap', borderColor: '#d7ddeb', color: primary }}
        >
          Preview
        </Button>
        {showDrawRoi && <Button
          size="small"
          variant="outlined"
          startIcon={<CropFreeIcon fontSize="small" />}
          onClick={() => handleOpenROIDrawer(cameraType, camera)}
          disabled={loading}
          fullWidth={fullWidth}
          sx={{ textTransform: 'none', whiteSpace: 'nowrap', borderColor: '#d7ddeb', color: primary }}
        >
          Draw ROI
        </Button>}
        {showFaceCrop ? (
          <Button
            size="small"
            variant="outlined"
            startIcon={<FaceRetouchingNaturalIcon fontSize="small" />}
            onClick={() => handleOpenFaceCrop(cameraType, camera)}
            disabled={loading}
            fullWidth={fullWidth}
            sx={{ textTransform: 'none', whiteSpace: 'nowrap', borderColor: '#d7ddeb', color: primary }}
          >
            Face crop
          </Button>
        ) : null}
        {mode !== 'detection-settings' ? (
          <Button
            size="small"
            variant="outlined"
            startIcon={<EditIcon fontSize="small" />}
            onClick={() => handleOpenEditCamera(camera)}
            fullWidth={fullWidth}
            sx={{ textTransform: 'none', whiteSpace: 'nowrap', borderColor: '#d7ddeb', color: primary }}
          >
            Details
          </Button>
        ) : null}
      </Stack>
    );
  };

  const pickPreferredSubstreamUrl = useCallback((urls = []) => {
    const cleaned = (Array.isArray(urls) ? urls : []).map((u) => (u || '').trim()).filter(Boolean);
    if (!cleaned.length) return '';

    // Prefer explicit substream markers first.
    const subByKeyword = cleaned.find((u) => /(^|[\/_-])sub($|[\/_.-])/i.test(u));
    if (subByKeyword) return subByKeyword;

    // Common channel-id convention: main=101, sub=102.
    const subByChannel102 = cleaned.find((u) => /(^|[^\d])102([^\d]|$)/.test(u));
    if (subByChannel102) return subByChannel102;

    // Secondary fallback from "MAIN/SUB" style list ordering.
    if (cleaned.length > 1) return cleaned[1];
    return cleaned[0];
  }, []);

  const getStatusStreamUrl = useCallback((camera) => {
    if (!camera) return '';
    if (Array.isArray(camera.stream_urls) && camera.stream_urls.length > 0) {
      return pickPreferredSubstreamUrl(camera.stream_urls);
    }
    if (Array.isArray(camera.streams) && camera.streams.length > 0) {
      return pickPreferredSubstreamUrl(camera.streams.map((stream) => stream?.stream_url));
    }
    if (camera.rtsp_url) return camera.rtsp_url;
    return '';
  }, [pickPreferredSubstreamUrl]);

  const captureCameraSnapshot = useCallback(async (camera, { onlyIfMissing = false, force = false } = {}) => {
    if (!camera?.id) return;
    if (
      onlyIfMissing
      && (camera.snapshot_image_url || capturedSnapshotCameraIdsRef.current.has(camera.id))
    ) {
      return;
    }
    const existingCapture = snapshotCapturePromisesRef.current.get(camera.id);
    if (existingCapture && !force) return existingCapture;

    const streamUrl = getStatusStreamUrl(camera);
    const snapshotRevision = snapshotRevisionRef.current.get(camera.id) || 0;
    const capturePromise = (async () => {
      try {
        const snapshot = await getRtspSnapshot(camera.id, streamUrl, { refresh: true });
        let image = snapshot?.image;
        if (snapshot?.image_url) {
          const snapshotUrl = buildApiUrl(snapshot.image_url);
          const cacheSeparator = snapshotUrl.includes('?') ? '&' : '?';
          image = `${snapshotUrl}${cacheSeparator}_snapshot=${Date.now()}`;
        }
        if (
          image
          && snapshotRevision === (snapshotRevisionRef.current.get(camera.id) || 0)
        ) {
          capturedSnapshotCameraIdsRef.current.add(camera.id);
          setCameraSnapshots((previous) => ({
            ...previous,
            [camera.id]: image,
          }));
        } else {
          console.warn(`Unable to refresh snapshot for camera ${camera.id}`, snapshot?.message || 'No image returned');
        }
      } catch (snapshotError) {
        console.warn(`Unable to refresh snapshot for camera ${camera.id}`, snapshotError);
      } finally {
        if (snapshotCapturePromisesRef.current.get(camera.id) === capturePromise) {
          snapshotCapturePromisesRef.current.delete(camera.id);
        }
      }
    })();
    snapshotCapturePromisesRef.current.set(camera.id, capturePromise);
    return capturePromise;
  }, [getStatusStreamUrl]);

  const handleRefreshCameraSnapshot = async (camera) => {
    if (!camera?.id || snapshotRefreshInFlightRef.current.has(camera.id)) return;
    const streamUrl = getStatusStreamUrl(camera);
    if (!streamUrl) {
      showSnackbar('No RTSP stream URL is available for this camera', 'error');
      return;
    }

    snapshotRefreshInFlightRef.current.add(camera.id);
    setCameraSnapshotRefreshing((previous) => ({ ...previous, [camera.id]: true }));
    const snapshotRevision = (snapshotRevisionRef.current.get(camera.id) || 0) + 1;
    snapshotRevisionRef.current.set(camera.id, snapshotRevision);
    try {
      const snapshot = await getRtspSnapshot(camera.id, streamUrl, { refresh: true });
      let image = snapshot?.image;
      if (snapshot?.image_url) {
        const snapshotUrl = buildApiUrl(snapshot.image_url);
        const cacheSeparator = snapshotUrl.includes('?') ? '&' : '?';
        image = `${snapshotUrl}${cacheSeparator}_snapshot=${Date.now()}`;
      }
      if (!image) {
        throw new Error(snapshot?.message || 'The camera did not return a snapshot');
      }
      if (snapshotRevision === (snapshotRevisionRef.current.get(camera.id) || 0)) {
        capturedSnapshotCameraIdsRef.current.add(camera.id);
        setCameraSnapshots((previous) => ({
          ...previous,
          [camera.id]: image,
        }));
      }
    } catch (snapshotError) {
      showSnackbar(snapshotError.message || 'Unable to refresh camera snapshot', 'error');
    } finally {
      snapshotRefreshInFlightRef.current.delete(camera.id);
      setCameraSnapshotRefreshing((previous) => ({ ...previous, [camera.id]: false }));
    }
  };

  const handleCardPreviewToggle = (camera) => {
    if (!camera?.id) return;
    if (cardPreviewUrls[camera.id]) {
      if (
        cardPreviewPaused[camera.id]
        && hasFiniteCardStreamLimit
        && Object.keys(cardPreviewUrls).filter((cameraId) => !cardPreviewPaused[cameraId]).length >= MAX_STREAM_PLAY_ON_CARD
      ) {
        showSnackbar(`You are already playing the maximum of ${MAX_STREAM_PLAY_ON_CARD} camera streams`, 'warning');
        return;
      }
      setCardPreviewPaused((previous) => ({
        ...previous,
        [camera.id]: !previous[camera.id],
      }));
      return;
    }

    const stream = resolvePreviewStreamForQuality(
      camera,
      normalizePreviewQuality(camera, DEFAULT_PREVIEW_QUALITY),
    );
    if (!stream?.streamUrl) {
      showSnackbar('No RTSP stream URL is available for this camera', 'error');
      return;
    }
    const activeCount = Object.keys(cardPreviewUrls).filter((cameraId) => !cardPreviewPaused[cameraId]).length;
    if (hasFiniteCardStreamLimit && activeCount >= MAX_STREAM_PLAY_ON_CARD) {
      showSnackbar(`You are already playing the maximum of ${MAX_STREAM_PLAY_ON_CARD} camera streams`, 'warning');
      return;
    }

    pendingCardPreviewRef.current = null;
    startCardPreview(camera, stream);
  };

  const startCardPreview = (camera, stream) => {
    if (!camera?.id || !stream?.streamUrl) return;
    const previewUrl = getConfiguredCameraLivePreviewMseUrl(camera.id, {
      streamUrl: stream.streamUrl,
      streamId: stream.streamId || undefined,
    });
    cardPreviewReadyRef.current.delete(camera.id);
    setCardPreviewPaused((previous) => ({ ...previous, [camera.id]: false }));
    setCardPreviewUrls((previous) => ({
      ...previous,
      [camera.id]: previewUrl,
    }));
  };

  const stopCardPreview = (cameraId) => {
    pendingCardPreviewRef.current = null;
    cardPreviewReadyRef.current.delete(cameraId);
    setCardPreviewUrls((previous) => {
      const next = { ...previous };
      delete next[cameraId];
      return next;
    });
    setCardPreviewPaused((previous) => {
      const next = { ...previous };
      delete next[cameraId];
      return next;
    });
  };

  const handleCardPreviewPause = (camera, streamUrl, frame) => {
    if (!camera?.id) return;
    cardPreviewReadyRef.current.delete(camera.id);
    if (frame) {
      setCameraSnapshots((previous) => ({
        ...previous,
        [camera.id]: frame,
      }));
    }
    setCardPreviewUrls((previous) => {
      const next = { ...previous };
      delete next[camera.id];
      return next;
    });
    setCardPreviewPaused((previous) => {
      const next = { ...previous };
      delete next[camera.id];
      return next;
    });

    const pendingPreview = pendingCardPreviewRef.current;
    pendingCardPreviewRef.current = null;
    if (pendingPreview) startCardPreview(pendingPreview.camera, pendingPreview.stream);

    void (async () => {
      try {
        const snapshot = await getRtspSnapshot(camera.id, streamUrl, { refresh: true });
        let image = snapshot?.image;
        if (snapshot?.image_url) {
          const snapshotUrl = buildApiUrl(snapshot.image_url);
          const cacheSeparator = snapshotUrl.includes('?') ? '&' : '?';
          image = `${snapshotUrl}${cacheSeparator}_snapshot=${Date.now()}`;
        }
        if (!image) {
          throw new Error(snapshot?.message || 'The camera did not return a snapshot');
        }
        setCameraSnapshots((previous) => ({
          ...previous,
          [camera.id]: image,
        }));
      } catch (snapshotError) {
        showSnackbar(snapshotError.message || 'Unable to update the camera snapshot', 'error');
      }
    })();
  };

  const updateCameraStatus = useCallback(async (camera, options = {}) => {
    if (!camera?.id) return null;
    if (!['camera-configuration', 'camera-streams'].includes(viewMode || 'camera-streams')) return null;
    const shouldApply = typeof options.shouldApply === 'function' ? options.shouldApply : () => true;
    setCameraStatusLoading((prev) => ({ ...prev, [camera.id]: true }));
    try {
      const streamUrl = getStatusStreamUrl(camera);
      let entry;
      try {
        const result = await getCameraRtspStatus(camera.id, streamUrl || undefined);
        entry = {
          status: result.status,
          message: result.message || null,
          checked_at: result.checked_at || null,
          latency_ms: result.latency_ms ?? null,
        };
      } catch (err) {
        entry = {
          status: 'error',
          message: err.message || 'Status check failed',
          checked_at: null,
          latency_ms: null,
        };
      }
      if (shouldApply(entry)) {
        setCameraStatusMap((prev) => ({
          ...prev,
          [camera.id]: entry,
        }));
      }
      if (options.refreshSnapshot) {
        await captureCameraSnapshot(camera, { onlyIfMissing: true });
      }
      return entry;
    } finally {
      setCameraStatusLoading((prev) => ({ ...prev, [camera.id]: false }));
    }
  }, [captureCameraSnapshot, getStatusStreamUrl, viewMode]);

  const isCameraStatusOffline = useCallback(
    (camera) => Boolean(camera?.id && cameraStatusMap[camera.id]?.status === 'offline'),
    [cameraStatusMap],
  );

  const refreshAllCameraStatuses = useCallback(async (items, { refreshSnapshots = false } = {}) => {
    const list = Array.isArray(items) ? items : streams;
    if (!list || list.length === 0) return;
    const camerasToRefresh = list.filter((camera) => !activeCardStreamsRef.current[camera.id]);
    await Promise.allSettled(camerasToRefresh.map((camera) => updateCameraStatus(camera, { refreshSnapshot: refreshSnapshots })));
  }, [streams, updateCameraStatus]);

  const visibleStreams = streams.slice(0, visibleCameraCount);
  const hasMoreCameras = streams.length > visibleCameraCount;

  const handleLoadMoreCameras = useCallback(async () => {
    const nextBatch = streams.slice(visibleCameraCount, visibleCameraCount + CAMERA_PAGE_SIZE);
    if (nextBatch.length === 0) return;
    setLoadingMoreCameraCount(nextBatch.length);
    try {
      await refreshAllCameraStatuses(nextBatch, { refreshSnapshots: true });
    } finally {
      setLoadingMoreCameraCount(0);
      setVisibleCameraCount((prev) => prev + nextBatch.length);
    }
  }, [streams, visibleCameraCount, refreshAllCameraStatuses]);

  // Detection configuration state
  const [detectionLoading, setDetectionLoading] = useState(true);
  const [detectionSaving, setDetectionSaving] = useState(false);
  const [detectionCameraError, setDetectionCameraError] = useState('');
  const [detectionConfigError, setDetectionConfigError] = useState(null);
  const [detectionConfigSaving, setDetectionConfigSaving] = useState(false);
  const [trackerSettings, setTrackerSettings] = useState(createTrackerStateFromData());
  const [faceConfidence, setFaceConfidence] = useState(String(CONFIDENCE_DEFAULT));
  const [poseConfidence, setPoseConfidence] = useState(String(CONFIDENCE_DEFAULT));
  const [recognitionInterval, setRecognitionInterval] = useState('');
  const [forwardFaceConfidence, setForwardFaceConfidence] = useState(String(CONFIDENCE_DEFAULT));
  const [minMergeGroupEmbeddingThreshold, setMinMergeGroupEmbeddingThreshold] = useState('0.6');
  const [maxThresholdGroupCreation, setMaxThresholdGroupCreation] = useState('0.3');
  const [possibleMatchThreshold, setPossibleMatchThreshold] = useState('');
  const [possibleMatchSendAttendance, setPossibleMatchSendAttendance] = useState(false);
  const [minCropWidth, setMinCropWidth] = useState('125');
  const [minCropHeight, setMinCropHeight] = useState('125');
  const [detectionState, setDetectionState] = useState({
    inCameraMode: 'rtsp',
    inCameraId: '',
    inDeviceIndex: '0',
    outCameraMode: 'rtsp',
    outCameraId: '',
    outDeviceIndex: '1',
  });
  const [initialDetectionState, setInitialDetectionState] = useState(null);
  const [healthState, setHealthState] = useState({ in: null, out: null });
  const [checkingHealth, setCheckingHealth] = useState(null);
  const hasRtspOptions = streams.length > 0;

  // WebView health check state
  const [webViewHealth, setWebViewHealth] = useState(null);
  const [checkingWebViewHealth, setCheckingWebViewHealth] = useState(false);

  // Detect App health check state
  const [detectAppHealth, setDetectAppHealth] = useState(null);
  const [checkingDetectAppHealth, setCheckingDetectAppHealth] = useState(false);

  // State for face embedding configuration
  const [embeddingConfigs, setEmbeddingConfigs] = useState({
    custom: { id: null, faiss_load: true, updated_by: null, updated_at: null },
    front: { id: null, faiss_load: true, updated_by: null, updated_at: null },
    left_profile: { id: null, faiss_load: true, updated_by: null, updated_at: null },
    right_profile: { id: null, faiss_load: true, updated_by: null, updated_at: null },
  });
  const [embeddingConfigLoading, setEmbeddingConfigLoading] = useState(false);
  const [embeddingStats, setEmbeddingStats] = useState(null);
  const [embeddingConfigNeedsRestart, setEmbeddingConfigNeedsRestart] = useState(false);
  const [embeddingConfigLastUpdate, setEmbeddingConfigLastUpdate] = useState({ updated_by: null, updated_at: null });
  
  // Late Entry Time Configuration state
  const [lateEntryTime, setLateEntryTime] = useState('');
  const [lateEntryActive, setLateEntryActive] = useState(true);
  const [lateEntryLoading, setLateEntryLoading] = useState(true);
  const [lateEntrySaving, setLateEntrySaving] = useState(false);
  const [lateEntryError, setLateEntryError] = useState(null);
  const [lateEntryConfirmOpen, setLateEntryConfirmOpen] = useState(false);
  const [lateEntryMetadata, setLateEntryMetadata] = useState({
    last_updated_by: null,
    last_updated_at: null,
  });

  // ROI Drawer state
  const [roiDrawerOpen, setRoiDrawerOpen] = useState(false);
  const [roiDrawerCameraType, setRoiDrawerCameraType] = useState(null); // "IN" or "OUT" or "ACTIVITY"
  const [roiDrawerCameraId, setRoiDrawerCameraId] = useState(null);
  const [roiDrawerCameraLabel, setRoiDrawerCameraLabel] = useState('');
  const [roiFrameImage, setRoiFrameImage] = useState(null);
  const [roiExisting, setRoiExisting] = useState(null);
  const [roiExistingLaneRois, setRoiExistingLaneRois] = useState({});
  const [roiFetching, setRoiFetching] = useState(false);
  const [roiSaving, setRoiSaving] = useState(false);
  const [faceCropOpen, setFaceCropOpen] = useState(false);
  const [faceCropCameraType, setFaceCropCameraType] = useState(null);
  const [faceCropCameraId, setFaceCropCameraId] = useState(null);
  const [faceCropCameraLabel, setFaceCropCameraLabel] = useState('');
  const [faceCropFrameImage, setFaceCropFrameImage] = useState(null);
  const [faceCropExistingRoi, setFaceCropExistingRoi] = useState(null);
  const [faceCropConfig, setFaceCropConfig] = useState(null);
  const [faceCropLoading, setFaceCropLoading] = useState(false);
  const [faceCropSaving, setFaceCropSaving] = useState(false);

  // ROI Configuration state
  const [roiTab, setRoiTab] = useState('IN');
  const [selectedInCamera, setSelectedInCamera] = useState('');
  const [selectedOutCamera, setSelectedOutCamera] = useState('');
  const [selectedActivityCamera, setSelectedActivityCamera] = useState('');
  const [selectedInLane, setSelectedInLane] = useState('');
  const [selectedOutLane, setSelectedOutLane] = useState('');
  const [savedRoi, setSavedRoi] = useState({ in: null, out: null });
  const [savedLaneRois, setSavedLaneRois] = useState({ in: {}, out: {} });
  const [roiConfigsByCameraId, setRoiConfigsByCameraId] = useState({});
  const [laneCount, setLaneCount] = useState({ IN: '0', OUT: '0', ACTIVITY: '0' });

  const [detectionSettingsConfirmDialog, setDetectionSettingsConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    onConfirm: null,
  });

  // Camera detail modal state
  const [cameraDetailModalOpen, setCameraDetailModalOpen] = useState(false);
  const cameraDetailModalOpenRef = useRef(false);
  const [selectedCamera, setSelectedCamera] = useState(null);
  const [showDetailRtspPassword, setShowDetailRtspPassword] = useState(false);
  /** Detail modal: 'configured' uses sub stream; 'health' uses MAIN/primary for static health preview. */
  const cameraDetailPreviewKindRef = useRef('configured');
  const livePreviewErrorHandlerRef = useRef(() => {});
  const livePreviewLoadHandlerRef = useRef(() => {});
  /** DeepStream IN/OUT overlay SSE — stop when modal closes. */
  const deepStreamWatchCameraRef = useRef(null);
  /** Auto-reconnect attempts for live preview after stream drop. */
  const previewReconnectAttemptRef = useRef(0);
  const previewReconnectTimerRef = useRef(null);
  const previewReconnectInFlightRef = useRef(false);
  /** Always-current quality tier for reconnects (high=main, medium=sub, low=third). */
  const livePreviewQualityRef = useRef(DEFAULT_PREVIEW_QUALITY);
  /** Index into `streams` while Configured Cameras detail modal is open; -1 otherwise. */
  const [configuredNavIndex, setConfiguredNavIndex] = useState(-1);
  /** Up to 3 live MSE connections: prev/current/next neighbors by camera id. */
  const [configuredPreviewWindow, setConfiguredPreviewWindow] = useState({
    streams: {},
    currentId: null,
  });
  const configuredPreviewCurrentIdRef = useRef(null);
  /** True once the live player delivers frames; a late offline probe must not tear it down. */
  const previewLivePlayingRef = useRef(false);
  /** Identifies the newest background status probe so stale results are ignored. */
  const previewStatusCheckTokenRef = useRef(0);

  // Multi-camera assignment modal state
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [assignCameraType, setAssignCameraType] = useState('');
  const [selectedCameras, setSelectedCameras] = useState([]);
  const [selectedCameraStreams, setSelectedCameraStreams] = useState({});
  const [assignLoading, setAssignLoading] = useState(false);
  const [assignError, setAssignError] = useState('');

  // Add camera modal state
  const [addCameraModalOpen, setAddCameraModalOpen] = useState(false);
  const [addCameraLoading, setAddCameraLoading] = useState(false);
  const [newCamera, setNewCamera] = useState({
    label: '',
    description: '',
    stream_urls: [''],
    sub_stream_url: '',
  });
  const [addCameraError, setAddCameraError] = useState('');
  const [addCameraTestLoading, setAddCameraTestLoading] = useState(false);
  const [addCameraTestImage, setAddCameraTestImage] = useState(null);
  const [addCameraTestError, setAddCameraTestError] = useState('');

  // Edit camera modal state
  const [editCameraModalOpen, setEditCameraModalOpen] = useState(false);
  const [editCameraLoading, setEditCameraLoading] = useState(false);
  const [editCameraError, setEditCameraError] = useState('');
  const [editCamera, setEditCamera] = useState({
    id: '',
    label: '',
    description: '',
    sub_stream_url: '',
    sub_stream_id: '',
    streams: [{ stream_id: '', stream_url: '' }],
  });

  // Delete camera confirmation state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [cameraToDelete, setCameraToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Deactivate camera confirmation state
  const [deactivateConfirmOpen, setDeactivateConfirmOpen] = useState(false);
  const [cameraToDeactivate, setCameraToDeactivate] = useState(null);

  // Camera preview modal state
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const [previewCamera, setPreviewCamera] = useState(null);
  const [previewHealth, setPreviewHealth] = useState(null);
  /** Bumped on manual Refresh to remount live MSE players and force reconnect. */
  const [previewSessionKey, setPreviewSessionKey] = useState(0);
  const [previewRoi, setPreviewRoi] = useState(null);
  const [previewLaneRois, setPreviewLaneRois] = useState({});
  const [previewLaneCount, setPreviewLaneCount] = useState(0);
  const [livePreviewQuality, setLivePreviewQualityState] = useState(DEFAULT_PREVIEW_QUALITY);
  const setLivePreviewQuality = (quality) => {
    const next = PREVIEW_QUALITY_INDEX[quality] !== undefined ? quality : DEFAULT_PREVIEW_QUALITY;
    livePreviewQualityRef.current = next;
    setLivePreviewQualityState(next);
  };
  const [previewZoom, setPreviewZoom] = useState(1);
  const [previewPan, setPreviewPan] = useState({ x: 0, y: 0 });
  const [previewFullscreen, setPreviewFullscreen] = useState(false);
  const [previewMuted, setPreviewMuted] = useState(true);
  const previewZoomViewportRef = useRef(null);
  const previewDragRef = useRef({ active: false, startX: 0, startY: 0, originX: 0, originY: 0 });
  const [cameraSnapshots, setCameraSnapshots] = useState({});
  const [cameraSnapshotRefreshing, setCameraSnapshotRefreshing] = useState({});
  const [cardPreviewUrls, setCardPreviewUrls] = useState({});
  const [cardPreviewPaused, setCardPreviewPaused] = useState({});
  activeCardStreamsRef.current = Object.fromEntries(
    Object.keys(cardPreviewUrls)
      .filter((cameraId) => Boolean(cardPreviewUrls[cameraId]) && !cardPreviewPaused[cameraId])
      .map((cameraId) => [cameraId, true]),
  );
  const cardPreviewReadyRef = useRef(new Set());
  const cardPreviewViewportRefs = useRef({});
  const pendingCardPreviewRef = useRef(null);
  const snapshotCapturePromisesRef = useRef(new Map());
  const capturedSnapshotCameraIdsRef = useRef(new Set());
  const snapshotRevisionRef = useRef(new Map());
  const snapshotRefreshInFlightRef = useRef(new Set());

  const mode = viewMode || 'camera-streams';
  const reuseCardPreviewInModal = Boolean(
    cameraDetailModalOpen
    && cameraDetailPreviewKindRef.current === 'configured'
    && selectedCamera?.id
    && cardPreviewUrls[selectedCamera.id],
  );

  const getCachedCameraRoi = (camera) => {
    const cameraType = String(camera?.camera_type || '').toUpperCase();
    if (mode !== 'detection-settings' || !camera?.id || !['IN', 'OUT'].includes(cameraType)) {
      return null;
    }
    return roiConfigsByCameraId[camera.id] || null;
  };

  const normalizeCameraHealth = useCallback((camera) => {
    if (!camera) return null;
    if ((camera.mode || '').toLowerCase() === 'device') {
      return {
        mode: 'device',
        device_index: camera.device_index ?? null,
      };
    }
    return {
      mode: 'rtsp',
      id: camera.id,
      label: camera.label,
      health_status: camera.health_status || camera.status || null,
      latency_ms: typeof camera.latency_ms === 'number' ? camera.latency_ms : null,
      health_message: camera.health_message || camera.message || null,
      image: camera.image || camera.health_image || null,
      resolution: camera.resolution || null,
      roi: camera.roi || null,
      lane_rois: camera.lane_rois || null,
      detect_app_warning: camera.detect_app_warning || null,
    };
  }, []);

  const buildDetectionState = useCallback((data) => {
    const inCam = data?.in_camera || {};
    const outCam = data?.out_camera || {};
    const state = {
      inCameraMode: (inCam.mode || 'rtsp').toLowerCase() === 'device' ? 'device' : 'rtsp',
      inCameraId: inCam.id || '',
      inDeviceIndex: String(
        inCam.device_index !== undefined && inCam.device_index !== null ? inCam.device_index : 0
      ),
      outCameraMode: (outCam.mode || 'rtsp').toLowerCase() === 'device' ? 'device' : 'rtsp',
      outCameraId: outCam.id || '',
      outDeviceIndex: String(
        outCam.device_index !== undefined && outCam.device_index !== null ? outCam.device_index : 1
      ),
    };
    if (state.inCameraMode !== 'rtsp') {
      state.inCameraId = '';
    }
    if (state.outCameraMode !== 'rtsp') {
      state.outCameraId = '';
    }
    return state;
  }, []);

  const loadEmbeddingConfig = useCallback(async () => {
    try {
      const response = await fetch(`${baseURL}/api/v1/settings/face-embedding-config`, {
        headers: {
          'Authorization': `Bearer ${getStoredToken()}`,
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to load embedding configuration');
      }
      
      const data = await response.json();
      const configs = data.configs || [];
      
      // Update state from database configs
      const newConfigs = {
        custom: { id: null, faiss_load: true, updated_by: null, updated_at: null },
        front: { id: null, faiss_load: true, updated_by: null, updated_at: null },
        left_profile: { id: null, faiss_load: true, updated_by: null, updated_at: null },
        right_profile: { id: null, faiss_load: true, updated_by: null, updated_at: null },
      };
      
      configs.forEach(config => {
        const angle = config.face_angle;
        if (newConfigs.hasOwnProperty(angle)) {
          newConfigs[angle] = {
            id: config.id,
            faiss_load: config.faiss_load,
            updated_by: config.last_updated_by,
            updated_at: config.updated_at,
          };
        }
      });
      
      setEmbeddingConfigs(newConfigs);
      
      // Find the most recent update across all configs
      let mostRecentUpdate = { updated_by: null, updated_at: null };
      configs.forEach(config => {
        if (config.updated_at) {
          if (!mostRecentUpdate.updated_at || new Date(config.updated_at) > new Date(mostRecentUpdate.updated_at)) {
            mostRecentUpdate = {
              updated_by: config.last_updated_by,
              updated_at: config.updated_at
            };
          }
        }
      });
      setEmbeddingConfigLastUpdate(mostRecentUpdate);
      
      // Set stats from response
      setEmbeddingStats({
        faiss_index_size: data.faiss_index_size || 0,
      });
      
      // Set restart required flag from backend (not localStorage)
      setEmbeddingConfigNeedsRestart(data.restart_required || false);
    } catch (err) {
      console.error('Error loading embedding configuration:', err);
    }
  }, []);

  const loadDetectionSettings = useCallback(async () => {
    setDetectionLoading(true);
    setDetectionCameraError('');
    setDetectionConfigError(null);
    try {
      // OLD CODE (SLOW - includes health checks on every load):
      // const data = await getDetectionSettings({ includeHealth: true });
      
      // NEW CODE (OPTIMIZED - skip health checks on initial load for faster performance):
      const data = await getDetectionSettings({ includeHealth: false });
      const nextState = buildDetectionState(data);
      setDetectionState(nextState);
      setInitialDetectionState(nextState);
      setHealthState({
        in: normalizeCameraHealth(data?.in_camera),
        out: normalizeCameraHealth(data?.out_camera),
      });
      setTrackerSettings(createTrackerStateFromData(data?.tracker_settings || {}));
      setFaceConfidence(formatConfidenceValue(data?.face_confidence));
      setPoseConfidence(formatConfidenceValue(data?.pose_confidence));
      setForwardFaceConfidence(formatConfidenceValue(data?.forward_face_confidence));
      setMinMergeGroupEmbeddingThreshold(
        data?.min_merge_group_embedding_threshold != null
          ? String(data.min_merge_group_embedding_threshold)
          : '0.6',
      );
      setMaxThresholdGroupCreation(
        data?.max_threshold_group_creation != null
          ? String(data.max_threshold_group_creation)
          : '0.3',
      );
      setPossibleMatchThreshold(data?.possible_match_threshold != null ? String(data.possible_match_threshold) : '');
      setPossibleMatchSendAttendance(Boolean(data?.possible_match_send_attendance));
      const resolvedWidth =
        data?.min_crop_width != null
          ? Number(data.min_crop_width)
          : (data?.min_crop_area != null ? Math.max(1, Math.round(Math.sqrt(Number(data.min_crop_area)))) : 125);
      const resolvedHeight =
        data?.min_crop_height != null
          ? Number(data.min_crop_height)
          : (data?.min_crop_area != null ? Math.max(1, Math.round(Math.sqrt(Number(data.min_crop_area)))) : 125);
      setMinCropWidth(String(resolvedWidth));
      setMinCropHeight(String(resolvedHeight));
      setRecognitionInterval(
        formatIntervalValue(
          data?.recognition_event_interval_seconds ??
            data?.banner_display_interval_seconds ??
            (typeof data?.recognition_cooldown_ms === 'number'
              ? data.recognition_cooldown_ms / 1000
              : ''),
        ),
      );
    } catch (err) {
      const message = err.message || 'Unable to load detection settings';
      setDetectionCameraError(message);
      setDetectionConfigError(message);
    } finally {
      setDetectionLoading(false);
    }
  }, [buildDetectionState, normalizeCameraHealth]);

  const loadLateEntryTime = useCallback(async () => {
    setLateEntryLoading(true);
    setLateEntryError(null);
    try {
      const data = await getLateEntryTime();
      if (data.late_entry_time) {
        setLateEntryTime(data.late_entry_time);
        setLateEntryMetadata({
          last_updated_by: data.last_updated_by,
          last_updated_at: data.last_updated_at,
        });
      }
      setLateEntryActive(data.late_entry_activate !== false);
    } catch (err) {
      setLateEntryError(err.message || 'Unable to load late entry time');
    } finally {
      setLateEntryLoading(false);
    }
  }, []);

  const applyCameraState = (cameraId, updates) => {
    setCameraStreamState((previous) => (
      previous || []
    ).map((camera) => (camera.id === cameraId ? { ...camera, ...updates } : camera)));
    setSelectedCamera((previous) => (
      previous?.id === cameraId ? { ...previous, ...updates } : previous
    ));
  };

  // Handle activating camera with IN/OUT type
  const handleActivateCamera = async (camera, cameraType) => {
    setUpdatingCameraId(camera.id);
    try {
      const response = await fetch(`${baseURL}/api/v1/camera-streams/${camera.id}/activate`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getStoredToken()}`,
        },
        body: JSON.stringify({ camera_type: cameraType }),
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to activate camera');
      }
      const result = await response.json();
      applyCameraState(camera.id, {
        camera_type: result.camera_type || cameraType,
        is_active: result.is_active ?? true,
        rtsp_url: result.rtsp_url || camera.rtsp_url,
      });
      showSnackbar(`Camera activated as ${cameraType}`, 'success');
    } catch (err) {
      showSnackbar(err.message || 'Failed to activate camera', 'error');
    } finally {
      setUpdatingCameraId(null);
    }
  };

  // Handle de-assigning camera (set camera_type to null, is_active to false)
  const handleDeactivateCamera = async (camera) => {
    setUpdatingCameraId(camera.id);
    try {
      const response = await fetch(`${baseURL}/api/v1/camera-streams/${camera.id}/deactivate`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getStoredToken()}`,
        },
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to de-assign camera');
      }
      const result = await response.json();
      applyCameraState(camera.id, {
        camera_type: result.camera_type ?? null,
        is_active: result.is_active ?? false,
      });
    } finally {
      setUpdatingCameraId(null);
    }
  };

  // Fetch streams from Detect App and sync to database
  const handleSyncFromDetectApp = async () => {
    setSyncingFromFile(true);
    try {
      const response = await fetch(`${baseURL}/api/v1/camera-streams/trigger-sync`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getStoredToken()}`,
        },
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to sync cameras from Detect App');
      }
      
      const result = await response.json();
      
      // Show detailed message
      if (result.added > 0) {
        showSnackbar(`Added ${result.added} new camera(s) from Detect App`, 'success');
      } else if (result.existing > 0) {
        showSnackbar(`All ${result.existing} camera(s) already exist`, 'info');
      } else {
        showSnackbar('No cameras found in Detect App', 'warning');
      }
      
      await refreshCameraList();
    } catch (err) {
      showSnackbar(err.message || 'Failed to sync cameras from Detect App', 'error');
    } finally {
      setSyncingFromFile(false);
    }
  };

  const loadCameraStreams = async () => {
    if (cameraStreamState !== null) {
      return cameraStreamState;
    }
    if (cameraStreamsRequestRef.current) {
      return cameraStreamsRequestRef.current;
    }

    const request = (async () => {
      try {
        const payload = await getCameraStreams();
        const items = payload.items || [];
        setCameraStreamState(items);
        setVisibleCameraCount(CAMERA_PAGE_SIZE);
        if (items.length > 0) {
          setLeftCameraId((prev) => prev || items[0]?.id || '');
          setRightCameraId((prev) => prev || items[1]?.id || items[0]?.id || '');
        }
        return items;
      } catch (err) {
        setError(err.message || 'Unable to load camera streams');
        return null;
      } finally {
        cameraStreamsRequestRef.current = null;
      }
    })();
    cameraStreamsRequestRef.current = request;
    return request;
  };

  /** Load only data required for the current sidebar route (viewMode). */
  const loadStreams = async () => {
    const mode = viewMode || 'camera-streams';
    setLoading(true);
    setError('');
    try {
      if (
        mode === 'camera-configuration'
        || mode === 'camera-streams'
        || (mode === 'detection-settings' && detectionSettingsTab === 'assignments')
      ) {
        const items = await loadCameraStreams();
        if (
          Array.isArray(items)
          && ['camera-configuration', 'camera-streams'].includes(mode)
        ) {
          refreshAllCameraStatuses(items.slice(0, CAMERA_PAGE_SIZE), { refreshSnapshots: true });
        }
      } else if (mode === 'system-health-check') {
        setLateEntryLoading(false);
      }

      if (mode === 'detection-settings') {
        setLateEntryLoading(false);
        await loadDetectionSettings();
      } else if (mode === 'face-embedding-config') {
        setLateEntryLoading(false);
        setDetectionLoading(false);
        await loadEmbeddingConfig();
      } else if (mode === 'late-entry-config') {
        setDetectionLoading(false);
        await loadLateEntryTime();
      }
    } catch (err) {
      setError(err.message || 'Unable to load');
    } finally {
      setLoading(false);
    }
  };

  // New function: Refresh only camera list (without other settings)
  const refreshCameraList = async (cameraIdsToCheck = []) => {
    try {
      const payload = await getCameraStreams();
      const items = payload.items || [];
      setCameraStreamState(items);
      // Keep open detail modal in sync with refreshed list.
      setSelectedCamera((prev) => {
        if (!prev?.id) return prev;
        const fresh = items.find((c) => c.id === prev.id);
        return fresh || prev;
      });

      // Auto-select first two cameras if not already selected
      if (items.length > 0) {
        if (!leftCameraId) setLeftCameraId(items[0]?.id || '');
        if (!rightCameraId) setRightCameraId(items[1]?.id || items[0]?.id || '');
      }
      const idsToCheck = new Set(cameraIdsToCheck || []);
      if (idsToCheck.size) {
        const newlyAdded = items.filter((camera) => idsToCheck.has(camera.id));
        refreshAllCameraStatuses(newlyAdded, { refreshSnapshots: true });
      }
    } catch (err) {
      setError(err.message || 'Unable to load camera streams');
    }
  };

  const applyEditedCamera = (camera) => {
    setCameraStreamState((prev) => (prev || []).map((item) => (item.id === camera.id ? camera : item)));
    setSelectedCamera((prev) => (prev?.id === camera.id ? camera : prev));
  };

  useEffect(() => {
    console.log('Loading streams for viewMode:', viewMode);
    loadStreams();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: run once per route (viewMode); loaders are stable
  }, [viewMode]);

  useEffect(() => {
    const mode = viewMode || 'camera-streams';
    if (
      mode === 'detection-settings'
      && detectionSettingsTab === 'assignments'
      && cameraStreamState === null
    ) {
      loadCameraStreams();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load only when entering assignments before streams are available
  }, [viewMode, detectionSettingsTab, cameraStreamState]);

  useEffect(() => {
    return () => {
      if (previewReconnectTimerRef.current) {
        clearTimeout(previewReconnectTimerRef.current);
        previewReconnectTimerRef.current = null;
      }
      if (deepStreamWatchCameraRef.current) {
        void stopDeepStreamPreviewWatch(deepStreamWatchCameraRef.current);
        deepStreamWatchCameraRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (mode !== 'detection-settings') return;
    const inConfig = roiConfigsByCameraId[detectionState.inCameraId];
    const outConfig = roiConfigsByCameraId[detectionState.outCameraId];
    setSavedRoi({
      in: inConfig?.roi || null,
      out: outConfig?.roi || null,
    });
    setSavedLaneRois({
      in: inConfig?.lane_rois || {},
      out: outConfig?.lane_rois || {},
    });
    setLaneCount((prev) => ({
      ...prev,
      IN: String(inConfig?.number_of_lane ?? 0),
      OUT: String(outConfig?.number_of_lane ?? 0),
    }));
  }, [mode, roiConfigsByCameraId, detectionState.inCameraId, detectionState.outCameraId]);

  const handleEmbeddingConfigChange = (angleName) => (event) => {
    const checked = event.target.checked;
    
    setEmbeddingConfigs((prev) => ({
      ...prev,
      [angleName]: {
        ...prev[angleName],
        faiss_load: checked,
        // updated_by and updated_at will be set by backend after save
      },
    }));
  };

  const handleSaveEmbeddingConfig = async () => {
    const approved = await confirm({
      title: 'Confirm Save Configuration',
      message: 'Are you sure you want to save face embedding configuration?',
      confirmLabel: 'Confirm',
      danger: false,
    });
    if (!approved) return;

    setEmbeddingConfigLoading(true);
    try {
      // Build updates array from config state
      const configs = [];
      
      Object.keys(embeddingConfigs).forEach(angle => {
        configs.push({
          face_angle: angle,
          faiss_load: embeddingConfigs[angle].faiss_load,
        });
      });
      
      const response = await fetch(`${baseURL}/api/v1/settings/face-embedding-config`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getStoredToken()}`,
        },
        body: JSON.stringify({
          configs: configs,
        }),
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to update embedding configuration');
      }
      
      const result = await response.json();
      
      // Reload config from server to get fresh state (includes restart_required flag)
      await loadEmbeddingConfig();
      
      showSnackbar(
        'Configuration saved successfully! Please restart the application for changes to take effect.',
        'success'
      );
    } catch (err) {
      showSnackbar(err.message || 'Failed to update embedding configuration', 'error');
    } finally {
      setEmbeddingConfigLoading(false);
    }
  };


  const handleModeChange = (cameraType) => (event) => {
    const value = event.target.value;
    setDetectionState((prev) => {
      const next = { ...prev };
      if (cameraType === 'IN') {
        next.inCameraMode = value;
        if (value === 'device' && (next.inDeviceIndex === '' || next.inDeviceIndex === undefined)) {
          next.inDeviceIndex = '0';
        }
        setHealthState((prevHealth) => ({
          ...prevHealth,
          in:
            value === 'device'
              ? { mode: 'device', device_index: parseInt(next.inDeviceIndex || '0', 10) }
              : null,
        }));
      } else {
        next.outCameraMode = value;
        if (value === 'device' && (next.outDeviceIndex === '' || next.outDeviceIndex === undefined)) {
          next.outDeviceIndex = '1';
        }
        setHealthState((prevHealth) => ({
          ...prevHealth,
          out:
            value === 'device'
              ? { mode: 'device', device_index: parseInt(next.outDeviceIndex || '1', 10) }
              : null,
        }));
      }
      return next;
    });
  };

  const handleDeviceIndexChange = (cameraType) => (event) => {
    const raw = event.target.value;
    const sanitized = raw.replace(/[^0-9]/g, '');
    setDetectionState((prev) => {
      const next = {
        ...prev,
        [cameraType === 'IN' ? 'inDeviceIndex' : 'outDeviceIndex']: sanitized,
      };
      if ((cameraType === 'IN' ? next.inCameraMode : next.outCameraMode) === 'device') {
        const key = cameraType === 'IN' ? 'in' : 'out';
        const defaultIndex = cameraType === 'IN' ? '0' : '1';
        setHealthState((prevHealth) => ({
          ...prevHealth,
          [key]: {
            mode: 'device',
            device_index: parseInt(sanitized || defaultIndex, 10),
          },
        }));
      }
      return next;
    });
  };

  const handleDetectionSelectChange = (cameraType) => (event) => {
    const value = event.target.value;
    setDetectionState((prev) => ({
      ...prev,
      [cameraType === 'IN' ? 'inCameraId' : 'outCameraId']: value,
    }));
  };

  const handleSaveDetectionCameras = async () => {
    const base = initialDetectionState || detectionState;
    const payload = {};

    const cameraChanged = (key) => detectionState[key] !== base[key];

    if (cameraChanged('inCameraMode')) {
      payload.in_camera_mode = detectionState.inCameraMode;
    }
    if (cameraChanged('outCameraMode')) {
      payload.out_camera_mode = detectionState.outCameraMode;
    }

    if (detectionState.inCameraMode === 'rtsp') {
      if (!detectionState.inCameraId) {
        showSnackbar('Select an IN camera when using RTSP mode', 'warning');
        return;
      }
      if (cameraChanged('inCameraId')) {
        payload.in_camera_id = detectionState.inCameraId;
      }
    } else if (cameraChanged('inDeviceIndex')) {
      payload.in_camera_device_index = parseInt(detectionState.inDeviceIndex || '0', 10);
    }

    if (detectionState.outCameraMode === 'rtsp') {
      if (!detectionState.outCameraId) {
        showSnackbar('Select an OUT camera when using RTSP mode', 'warning');
        return;
      }
      if (cameraChanged('outCameraId')) {
        payload.out_camera_id = detectionState.outCameraId;
      }
    } else if (cameraChanged('outDeviceIndex')) {
      payload.out_camera_device_index = parseInt(detectionState.outDeviceIndex || '1', 10);
    }

    if (Object.keys(payload).length === 0) {
      showSnackbar('No detection camera changes to save', 'info');
      return;
    }

    setDetectionSaving(true);
    try {
      const response = await updateDetectionSettings(payload);
      const next = buildDetectionState(response);
      setDetectionState(next);
      setInitialDetectionState(next);
      setHealthState({
        in: normalizeCameraHealth(response?.in_camera),
        out: normalizeCameraHealth(response?.out_camera),
      });
      showSnackbar('Detection camera preferences saved', 'success');
    } catch (err) {
      showSnackbar(err.message || 'Failed to save detection settings', 'error');
    } finally {
      setDetectionSaving(false);
    }
  };

  const handleTrackerInputChange = (name) => (event) => {
    const { value } = event.target;
    setTrackerSettings((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleTrackerToggleChange = (name) => (event) => {
    setTrackerSettings((prev) => ({
      ...prev,
      [name]: event.target.checked,
    }));
  };

  const buildTrackerPayload = () => {
    const payload = {};
    for (const field of TRACKER_FIELD_CONFIG) {
      const raw = trackerSettings[field.name];
      const fallback = TRACKER_DEFAULTS[field.name];
      const candidate = raw === '' || raw === undefined || raw === null ? fallback : raw;
      const parsed = field.type === 'int' ? parseInt(candidate, 10) : parseFloat(candidate);
      if (Number.isNaN(parsed)) {
        throw new Error(`Enter a valid number for ${field.label}`);
      }
      payload[field.name] = parsed;
    }
    TRACKER_BOOL_FIELDS.forEach((field) => {
      payload[field.name] = Boolean(trackerSettings[field.name]);
    });
    return payload;
  };

  const handleSaveDetectionSettings = async () => {
    let trackerPayload;
    let faceValue;
    let poseValue;
    let forwardFaceValue;
    let minMergeThresholdValue;
    let maxCreateThresholdValue;
    let recognitionCooldownValue;
    let pmThresholdValue;
    let minCropWidthValue;
    let minCropHeightValue;
    try {
      trackerPayload = buildTrackerPayload();
      faceValue = parseConfidenceInput(faceConfidence, 'Face confidence');
      poseValue = parseConfidenceInput(poseConfidence, 'Pose confidence');
      forwardFaceValue = parseConfidenceInput(forwardFaceConfidence, 'Forward face confidence');
      minMergeThresholdValue = parseConfidenceInput(
        minMergeGroupEmbeddingThreshold,
        'Min merge group embedding threshold',
      );
      maxCreateThresholdValue = parseConfidenceInput(
        maxThresholdGroupCreation,
        'Max threshold group creation',
      );
      recognitionCooldownValue = parseIntervalInput(recognitionInterval);
      minCropWidthValue = parsePositiveIntegerInput(minCropWidth, 'Min crop width');
      minCropHeightValue = parsePositiveIntegerInput(minCropHeight, 'Min crop height');
      if (possibleMatchThreshold !== '') {
        pmThresholdValue = parseConfidenceInput(possibleMatchThreshold, 'Possible match threshold');
      }
    } catch (err) {
      setDetectionConfigError(err.message);
      return;
    }

    setDetectionConfigSaving(true);
    setDetectionConfigError(null);

    try {
      const payload = {
        tracker_settings: trackerPayload,
        face_confidence: faceValue,
        pose_confidence: poseValue,
        forward_face_confidence: forwardFaceValue,
        min_merge_group_embedding_threshold: minMergeThresholdValue,
        max_threshold_group_creation: maxCreateThresholdValue,
        recognition_event_interval_seconds: recognitionCooldownValue,
        possible_match_send_attendance: possibleMatchSendAttendance,
        min_crop_area: minCropWidthValue * minCropHeightValue,
        min_crop_width: minCropWidthValue,
        min_crop_height: minCropHeightValue,
      };
      if (pmThresholdValue !== undefined) {
        payload.possible_match_threshold = pmThresholdValue;
      }
      const response = await updateDetectionSettings(payload);
      if (response?.tracker_settings) {
        setTrackerSettings(createTrackerStateFromData(response.tracker_settings));
      }
      if (response?.face_confidence !== undefined) {
        setFaceConfidence(formatConfidenceValue(response.face_confidence));
      }
      if (response?.pose_confidence !== undefined) {
        setPoseConfidence(formatConfidenceValue(response.pose_confidence));
      }
      if (response?.forward_face_confidence !== undefined) {
        setForwardFaceConfidence(formatConfidenceValue(response.forward_face_confidence));
      }
      if (response?.min_merge_group_embedding_threshold !== undefined) {
        setMinMergeGroupEmbeddingThreshold(
          response.min_merge_group_embedding_threshold != null
            ? String(response.min_merge_group_embedding_threshold)
            : '0.6',
        );
      }
      if (response?.max_threshold_group_creation !== undefined) {
        setMaxThresholdGroupCreation(
          response.max_threshold_group_creation != null
            ? String(response.max_threshold_group_creation)
            : '0.3',
        );
      }
      if (response?.possible_match_threshold !== undefined) {
        setPossibleMatchThreshold(response.possible_match_threshold != null ? String(response.possible_match_threshold) : '');
      }
      if (response?.possible_match_send_attendance !== undefined) {
        setPossibleMatchSendAttendance(Boolean(response.possible_match_send_attendance));
      }
      if (response?.min_crop_width !== undefined) {
        setMinCropWidth(response.min_crop_width != null ? String(response.min_crop_width) : '125');
      }
      if (response?.min_crop_height !== undefined) {
        setMinCropHeight(response.min_crop_height != null ? String(response.min_crop_height) : '125');
      }
      const intervalValue =
        response?.recognition_event_interval_seconds ??
        response?.banner_display_interval_seconds ??
        (typeof response?.recognition_cooldown_ms === 'number'
          ? response.recognition_cooldown_ms / 1000
          : recognitionCooldownValue);
      setRecognitionInterval(formatIntervalValue(intervalValue));
      showSnackbar('Detection settings updated successfully!', 'success');
    } catch (err) {
      setDetectionConfigError(err.message || 'Failed to save detection settings. Please try again.');
    } finally {
      setDetectionConfigSaving(false);
    }
  };

  const handleOpenSaveDetectionConfirm = () => {
    if (detectionConfigSaving) return;
    setDetectionSettingsConfirmDialog({
      open: true,
      title: 'Save Detection Settings',
      message: 'Are you sure you want to save these detection settings?',
      onConfirm: () => handleSaveDetectionSettings(),
    });
  };

  const handleCheckHealth = async (cameraType) => {
    const mode = cameraType === 'IN' ? detectionState.inCameraMode : detectionState.outCameraMode;
    if (mode !== 'rtsp') {
      showSnackbar('Health checks are only available for RTSP streams', 'info');
      return;
    }
    const cameraId = cameraType === 'IN' ? detectionState.inCameraId : detectionState.outCameraId;
    if (!cameraId) {
      showSnackbar(`Select a ${cameraType} camera first`, 'warning');
      return;
    }
    setCheckingHealth(cameraType);
    try {
      const cameraRow = streams.find((s) => s.id === cameraId);
      const previewStreamUrl = cameraRow ? getPreviewRtspUrl(cameraRow) : '';
      const cachedRoi = getCachedCameraRoi(cameraRow);
      const [healthResult, roiResult] = await Promise.allSettled([
        getCameraHealthPreview(cameraId, previewStreamUrl || undefined),
        Promise.resolve(cachedRoi),
      ]);

      if (healthResult.status === 'fulfilled') {
        const result = healthResult.value;
          const normalized = normalizeCameraHealth({
          id: result.id,
          label: result.label,
          camera_type: result.camera_type,
          health_status: result.status,
          latency_ms: result.latency_ms,
          health_message: result.message,
          image: result.image,
            resolution: result.resolution,
            roi: result.roi,
            lane_rois: result.lane_rois,
            detect_app_warning: result.detect_app_warning,
          });
        setHealthState((prev) => ({
          ...prev,
          [cameraType === 'IN' ? 'in' : 'out']: normalized,
        }));
      } else {
        throw healthResult.reason;
      }

      const key = cameraType === 'IN' ? 'in' : 'out';
      if (roiResult.status === 'fulfilled' && roiResult.value?.roi) {
        const r = roiResult.value.roi;
        setSavedRoi((prev) => ({ ...prev, [key]: { x: r.x, y: r.y, w: r.w, h: r.h } }));
        setSavedLaneRois((prev) => ({ ...prev, [key]: roiResult.value?.lane_rois || {} }));
      }
    } catch (err) {
      showSnackbar(err.message || 'Unable to check camera health', 'error');
    } finally {
      setCheckingHealth(null);
    }
  };

  const handleOpenCameraPreview = async (camera) => {
    if (!camera?.id) {
      showSnackbar('Camera not found', 'warning');
      return;
    }
    if (!camera.is_active) {
      showSnackbar('Activate the camera to view preview', 'info');
      return;
    }
    setPreviewModalOpen(true);
    setPreviewLoading(true);
    setPreviewError('');
    setPreviewCamera(camera);
    setPreviewHealth(null);
    setPreviewRoi(null);
    setPreviewLaneRois({});
    setPreviewLaneCount(0);

    try {
      const previewStreamUrl = getPreviewRtspUrl(camera);
      const cachedRoi = getCachedCameraRoi(camera);
      const roiPromise = cachedRoi
        ? Promise.resolve(cachedRoi)
        : Promise.resolve(null);
      const [healthResult, roiResult] = await Promise.allSettled([
        getCameraHealthPreview(camera.id, previewStreamUrl || undefined),
        roiPromise,
      ]);

      if (healthResult.status === 'fulfilled') {
        const result = healthResult.value;
          const normalized = normalizeCameraHealth({
          id: result.id,
          label: result.label,
          camera_type: result.camera_type,
          health_status: result.status,
          latency_ms: result.latency_ms,
          health_message: result.message,
          image: result.image,
            resolution: result.resolution,
            roi: result.roi,
            lane_rois: result.lane_rois,
            detect_app_warning: result.detect_app_warning,
          });
          setPreviewHealth(normalized);
      } else {
        throw healthResult.reason;
      }

      if (roiResult.status === 'fulfilled' && roiResult.value?.roi) {
        setPreviewRoi(roiResult.value.roi);
        setPreviewLaneRois(roiResult.value.lane_rois || {});
      }
      if (roiResult.status === 'fulfilled' && roiResult.value?.number_of_lane !== undefined) {
        setPreviewLaneCount(Number(roiResult.value.number_of_lane) || 0);
      }
    } catch (err) {
      setPreviewError(err.message || 'Unable to load camera preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleCloseCameraPreview = () => {
    setPreviewModalOpen(false);
    setPreviewLoading(false);
    setPreviewError('');
    setPreviewCamera(null);
    setPreviewHealth(null);
    setPreviewRoi(null);
    setPreviewLaneRois({});
    setPreviewLaneCount(0);
  };

  /** ROI assigned-cameras table: health-preview with MAIN/assigned primary stream (not rtsp-snapshot). */
  const handleOpenRoiAssignedCameraPreview = (camera) => {
    cameraDetailPreviewKindRef.current = 'health';
    setSelectedCamera(camera);
    cameraDetailModalOpenRef.current = true;
    setCameraDetailModalOpen(true);
    setPreviewHealth(null);
    setPreviewRoi(null);
    setPreviewLaneRois({});
    setPreviewLaneCount(0);
    setPreviewError('');
    setPreviewLoading(false);
    resetPreviewZoom();
    setPreviewMuted(true);

    const cameraType = String(camera?.camera_type || '').toUpperCase();
    if (cameraType === 'IN' || cameraType === 'OUT') {
      startRoiLivePreview(camera, cameraType);
      return;
    }
    loadCameraDetailHealthPreview(camera);
  };

  const clearPreviewReconnectTimer = () => {
    if (previewReconnectTimerRef.current) {
      clearTimeout(previewReconnectTimerRef.current);
      previewReconnectTimerRef.current = null;
    }
    previewReconnectInFlightRef.current = false;
  };

  const buildConfiguredPreviewUrl = (camera) => {
    const stream = resolvePreviewStreamForQuality(camera, livePreviewQualityRef.current);
    if (!camera?.id || !stream?.streamUrl) return null;
    return getConfiguredCameraLivePreviewMseUrl(camera.id, {
      streamUrl: stream.streamUrl,
      streamId: stream.streamId || undefined,
    });
  };

  const clearConfiguredPreviewWindow = () => {
    configuredPreviewCurrentIdRef.current = null;
    setConfiguredPreviewWindow({ streams: {}, currentId: null });
  };

  const applyConfiguredPreviewMetadata = (camera) => {
    if (!camera?.id) return;
    setPreviewHealth({
      mode: 'mse',
      id: camera.id,
      label: camera.label || camera.id,
      camera_type: camera.camera_type || null,
      health_status: 'online',
      latency_ms: null,
      health_message: `Live preview (${camera.label || camera.id})`,
      image: buildConfiguredPreviewUrl(camera),
      resolution: null,
      roi: null,
      lane_rois: null,
    });
  };

  const rebuildConfiguredPreviewWindow = (index) => {
    if (index < 0 || !streams.length) {
      clearConfiguredPreviewWindow();
      return;
    }
    const nextStreams = {};
    const cam = streams[index];
    const url = buildConfiguredPreviewUrl(cam);
    if (cam?.id && url) {
      nextStreams[cam.id] = {
        url,
        ready: cardPreviewReadyRef.current.has(cam.id),
      };
    }
    const currentId = streams[index]?.id ?? null;
    configuredPreviewCurrentIdRef.current = currentId;
    setConfiguredPreviewWindow({ streams: nextStreams, currentId });
  };

  const resetPreviewZoom = () => {
    setPreviewZoom(1);
    setPreviewPan({ x: 0, y: 0 });
  };

  const togglePreviewFullscreen = () => {
    const el = previewZoomViewportRef.current;
    if (!el) return;
    if (document.fullscreenElement === el) {
      if (document.exitFullscreen) document.exitFullscreen().catch(() => {});
      else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
    } else if (el.requestFullscreen) {
      el.requestFullscreen().catch(() => {});
    } else if (el.webkitRequestFullscreen) {
      el.webkitRequestFullscreen();
    }
  };

  const handleTogglePreviewMute = useCallback(() => {
    setPreviewMuted((prev) => !prev);
  }, []);

  const isLiveMsePreview =
    previewHealth?.mode === 'mse' ||
    previewHealth?.mode === 'mse-overlay' ||
    previewHealth?.mode === 'ds-mse';

  const clampPreviewZoom = (value) =>
    Math.min(PREVIEW_ZOOM_MAX, Math.max(PREVIEW_ZOOM_MIN, Number(value) || 1));

  const handlePreviewZoomBy = (delta) => {
    setPreviewZoom((prev) => {
      const next = clampPreviewZoom(prev + delta);
      if (next <= 1) {
        setPreviewPan({ x: 0, y: 0 });
      }
      return next;
    });
  };

  const handlePreviewWheel = (event) => {
    if (!previewHealth?.image) return;
    event.preventDefault();
    const viewport = previewZoomViewportRef.current;
    if (!viewport) {
      handlePreviewZoomBy(event.deltaY < 0 ? PREVIEW_ZOOM_STEP : -PREVIEW_ZOOM_STEP);
      return;
    }
    const rect = viewport.getBoundingClientRect();
    const cursorX = event.clientX - rect.left - rect.width / 2;
    const cursorY = event.clientY - rect.top - rect.height / 2;
    const direction = event.deltaY < 0 ? 1 : -1;
    setPreviewZoom((prev) => {
      const next = clampPreviewZoom(prev + direction * PREVIEW_ZOOM_STEP);
      if (next <= 1) {
        setPreviewPan({ x: 0, y: 0 });
        return next;
      }
      const ratio = next / prev;
      setPreviewPan((pan) => ({
        x: cursorX - (cursorX - pan.x) * ratio,
        y: cursorY - (cursorY - pan.y) * ratio,
      }));
      return next;
    });
  };

  const handlePreviewPointerDown = (event) => {
    if (previewZoom <= 1) return;
    if (event.button !== 0) return;
    previewDragRef.current = {
      active: true,
      startX: event.clientX,
      startY: event.clientY,
      originX: previewPan.x,
      originY: previewPan.y,
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const handlePreviewPointerMove = (event) => {
    if (!previewDragRef.current.active) return;
    const dx = event.clientX - previewDragRef.current.startX;
    const dy = event.clientY - previewDragRef.current.startY;
    setPreviewPan({
      x: previewDragRef.current.originX + dx,
      y: previewDragRef.current.originY + dy,
    });
  };

  const handlePreviewPointerUp = (event) => {
    if (!previewDragRef.current.active) return;
    previewDragRef.current.active = false;
    try {
      event.currentTarget.releasePointerCapture?.(event.pointerId);
    } catch (_) {
      /* ignore */
    }
  };

  // Non-passive wheel so zoom can prevent page scroll while hovering the preview.
  useEffect(() => {
    if (!cameraDetailModalOpen) return undefined;
    const node = previewZoomViewportRef.current;
    if (!node) return undefined;
    const onWheel = (event) => {
      handlePreviewWheel(event);
    };
    node.addEventListener('wheel', onWheel, { passive: false });
    return () => node.removeEventListener('wheel', onWheel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameraDetailModalOpen, previewHealth?.image, previewZoom]);

  useEffect(() => {
    if (!cameraDetailModalOpen) return undefined;
    const onChange = () => {
      setPreviewFullscreen(document.fullscreenElement === previewZoomViewportRef.current);
    };
    document.addEventListener('fullscreenchange', onChange);
    return () => {
      document.removeEventListener('fullscreenchange', onChange);
      if (document.fullscreenElement === previewZoomViewportRef.current) {
        if (document.exitFullscreen) document.exitFullscreen().catch(() => {});
        else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
      }
      setPreviewFullscreen(false);
    };
  }, [cameraDetailModalOpen]);

  const restartActiveLivePreview = () => {
    if (!selectedCamera) return;
    if (
      isCameraStatusOffline(selectedCamera)
      && !isDeepStreamRoiPreviewCamera(selectedCamera)
    ) {
      clearConfiguredPreviewWindow();
      setPreviewHealth(null);
      setPreviewError('');
      return;
    }
    if (cameraDetailPreviewKindRef.current === 'configured') {
      if (configuredNavIndex >= 0) {
        rebuildConfiguredPreviewWindow(configuredNavIndex);
        applyConfiguredPreviewMetadata(selectedCamera);
        return;
      }
      startConfiguredLivePreview(selectedCamera, { reconnect: true });
      return;
    }
    const cameraType = String(selectedCamera.camera_type || '').toUpperCase();
    if (cameraType === 'IN' || cameraType === 'OUT') {
      startRoiLivePreview(selectedCamera, cameraType, { reconnect: true });
    }
  };

  const handleLivePreviewError = (detail = {}) => {
    if (!cameraDetailModalOpenRef.current) return;
    if (
      !cameraDetailModalOpen ||
      (previewHealth?.mode !== 'mse' &&
        previewHealth?.mode !== 'mse-overlay' &&
        previewHealth?.mode !== 'ds-mse')
    ) {
      return;
    }
    if (previewReconnectInFlightRef.current) return;

    const attempt = previewReconnectAttemptRef.current;
    const dsPreview = previewHealth?.mode === 'ds-mse' || cameraDetailPreviewKindRef.current === 'health';
    if (dsPreview) {
      setPreviewError(deepStreamPreviewErrorFromClose(detail));
      return;
    }
    if (attempt >= 5) {
      setPreviewError('Stream unavailable. Click Refresh to try again.');
      return;
    }

    previewReconnectInFlightRef.current = true;
    previewReconnectAttemptRef.current = attempt + 1;
    setPreviewError('');
    const delayMs = Math.min(4000, 600 * (2 ** attempt));
    clearPreviewReconnectTimer();
    previewReconnectTimerRef.current = setTimeout(() => {
      previewReconnectInFlightRef.current = false;
      if (!cameraDetailModalOpenRef.current) return;
      restartActiveLivePreview();
    }, delayMs);
  };

  const handleLivePreviewLoad = () => {
    previewReconnectAttemptRef.current = 0;
    previewReconnectInFlightRef.current = false;
    previewLivePlayingRef.current = true;
    setPreviewError((prev) => (prev ? '' : prev));
  };

  livePreviewErrorHandlerRef.current = handleLivePreviewError;
  livePreviewLoadHandlerRef.current = handleLivePreviewLoad;

  const handleConfiguredSlotPlaying = (cameraId) => {
    setConfiguredPreviewWindow((prev) => {
      const slot = prev.streams[cameraId];
      if (!slot) return prev;
      return {
        ...prev,
        streams: {
          ...prev.streams,
          [cameraId]: { ...slot, ready: true },
        },
      };
    });
    if (cameraId === configuredPreviewCurrentIdRef.current) {
      handleLivePreviewLoad();
    }
  };

  const handleConfiguredSlotError = (cameraId) => {
    setConfiguredPreviewWindow((prev) => {
      const slot = prev.streams[cameraId];
      if (!slot) return prev;
      return {
        ...prev,
        streams: {
          ...prev.streams,
          [cameraId]: { ...slot, ready: false },
        },
      };
    });
    if (cameraId === configuredPreviewCurrentIdRef.current) {
      handleLivePreviewError();
    }
  };

  const startRoiLivePreview = (camera, cameraType, options = {}) => {
    clearConfiguredPreviewWindow();
    clearPreviewReconnectTimer();
    deepStreamWatchCameraRef.current = null;
    if (!options.reconnect) {
      previewReconnectAttemptRef.current = 0;
    }
    setConfiguredNavIndex(-1);
    setPreviewLoading(false);
    setPreviewError('');
    if (!camera?.id) {
      setPreviewError(DEEPSTREAM_PREVIEW_UNAVAILABLE);
      setPreviewHealth(null);
      return;
    }
    const mseUrl = getDeepStreamPreviewMseUrl(camera.id);
    if (!mseUrl) {
      setPreviewError(DEEPSTREAM_PREVIEW_UNAVAILABLE);
      setPreviewHealth(null);
      return;
    }
    setPreviewHealth({
      mode: 'ds-mse',
      id: camera.id,
      label: camera?.label || cameraType,
      camera_type: cameraType,
      health_status: 'online',
      latency_ms: null,
      health_message: `DeepStream RTSP-out preview (${cameraType})`,
      image: mseUrl,
      resolution: null,
      roi: null,
      lane_rois: null,
    });
  };

  const stopRoiLivePreview = () => {
    clearPreviewReconnectTimer();
  };

  /** On-demand live preview for any configured camera (single stream, no neighbor window). */
  const startConfiguredLivePreview = (camera, options = {}) => {
    stopRoiLivePreview();
    if (deepStreamWatchCameraRef.current) {
      void stopDeepStreamPreviewWatch(deepStreamWatchCameraRef.current);
      deepStreamWatchCameraRef.current = null;
    }
    clearPreviewReconnectTimer();
    if (!options.reconnect) {
      previewReconnectAttemptRef.current = 0;
    }
    setPreviewLoading(false);
    setPreviewError('');
    const mseUrl = buildConfiguredPreviewUrl(camera);
    if (!camera?.id) {
      setPreviewError('Camera not found');
      return;
    }
    if (!mseUrl) {
      setPreviewError('No stream URL available for preview');
      return;
    }
    setPreviewHealth({
      mode: 'mse',
      id: camera.id,
      label: camera.label || camera.id,
      camera_type: camera.camera_type || null,
      health_status: 'online',
      latency_ms: null,
      health_message: `Live preview (${camera.label || camera.id})`,
      image: mseUrl,
      resolution: null,
      roi: null,
      lane_rois: null,
    });
  };

  const handleCloseCameraDetail = () => {
    if (cameraDetailPreviewKindRef.current === 'configured' && selectedCamera?.id) {
      snapshotRevisionRef.current.set(
        selectedCamera.id,
        (snapshotRevisionRef.current.get(selectedCamera.id) || 0) + 1,
      );
      const preview = previewZoomViewportRef.current;
      const previewHosts = preview?.querySelectorAll('eye-preview-rtc') || [];
      const activeHost = Array.from(previewHosts).find((host) => host.style.opacity !== '0');
      const video = activeHost?.video || activeHost?.querySelector?.('video');
      if (video?.videoWidth && video?.videoHeight) {
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        try {
          const context = canvas.getContext('2d');
          if (!context) throw new Error('Unable to create a canvas context for the preview frame');
          context.drawImage(video, 0, 0, canvas.width, canvas.height);
          const image = canvas.toDataURL('image/jpeg', 0.88);
          setCameraSnapshots((previous) => ({
            ...previous,
            [selectedCamera.id]: image,
          }));
        } catch (snapshotError) {
          console.warn(`Unable to capture preview frame for camera ${selectedCamera.id}`, snapshotError);
        }
      }
      void captureCameraSnapshot(selectedCamera, { force: true });
      if (cardPreviewUrls[selectedCamera.id]) {
        stopCardPreview(selectedCamera.id);
      }
    }
    cameraDetailModalOpenRef.current = false;
    previewLivePlayingRef.current = false;
    previewStatusCheckTokenRef.current += 1;
    clearPreviewReconnectTimer();
    clearConfiguredPreviewWindow();
    if (deepStreamWatchCameraRef.current) {
      void stopDeepStreamPreviewWatch(deepStreamWatchCameraRef.current);
      deepStreamWatchCameraRef.current = null;
    }
    stopRoiLivePreview();
    previewReconnectAttemptRef.current = 0;
    setConfiguredNavIndex(-1);
    setCameraDetailModalOpen(false);
    setSelectedCamera(null);
    // Clear preview data
    setPreviewHealth(null);
    setPreviewRoi(null);
    setPreviewLaneRois({});
    setPreviewLaneCount(0);
    setPreviewError(null);
    setPreviewLoading(false);
    setPreviewSessionKey(0);
    resetPreviewZoom();
    setLivePreviewQuality(DEFAULT_PREVIEW_QUALITY);
    setPreviewMuted(true);
  };

  const navigateConfiguredPreview = (delta) => {
    if (cameraDetailPreviewKindRef.current !== 'configured') return;
    if (!streams.length) return;
    const fromIndex =
      configuredNavIndex >= 0
        ? configuredNavIndex
        : streams.findIndex((c) => c.id === selectedCamera?.id);
    if (fromIndex < 0) return;
    const nextIndex = fromIndex + delta;
    if (nextIndex < 0 || nextIndex >= streams.length) return;
    const nextCamera = streams[nextIndex];
    if (!nextCamera) return;
    const wasReusingCardPreview = Boolean(
      selectedCamera?.id && cardPreviewUrls[selectedCamera.id],
    );
    if (wasReusingCardPreview) {
      stopCardPreview(selectedCamera.id);
    }

    cameraDetailPreviewKindRef.current = 'configured';
    previewLivePlayingRef.current = false;
    previewStatusCheckTokenRef.current += 1;
    setConfiguredNavIndex(nextIndex);
    setSelectedCamera(nextCamera);
    setPreviewRoi(null);
    setPreviewLaneRois({});
    setPreviewLaneCount(0);
    setPreviewError('');
    setPreviewLoading(false);
    resetPreviewZoom();
    const nextQuality = normalizePreviewQuality(nextCamera, livePreviewQualityRef.current);
    const qualityChanged = nextQuality !== livePreviewQualityRef.current;
    setLivePreviewQuality(nextQuality);
    if (isCameraStatusOffline(nextCamera)) {
      clearConfiguredPreviewWindow();
      setPreviewHealth(null);
      setPreviewError('');
      setPreviewLoading(false);
      return;
    }
    if (qualityChanged) {
      rebuildConfiguredPreviewWindow(nextIndex);
      applyConfiguredPreviewMetadata(nextCamera);
      return;
    }
    const alreadyReady = configuredPreviewWindow.streams[nextCamera.id]?.ready;
    rebuildConfiguredPreviewWindow(nextIndex);
    applyConfiguredPreviewMetadata(nextCamera);
    if (alreadyReady) handleLivePreviewLoad();
  };

  // Keyboard ← → while Configured Cameras live preview modal is open.
  useEffect(() => {
    if (!cameraDetailModalOpen || configuredNavIndex < 0) return undefined;
    const onKeyDown = (event) => {
      if (cameraDetailPreviewKindRef.current !== 'configured') return;
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        navigateConfiguredPreview(-1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        navigateConfiguredPreview(1);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- navigate closes over latest streams/index
  }, [cameraDetailModalOpen, configuredNavIndex, streams]);

  // When the tab becomes visible again, soft-reconnect a live MSE preview that may have stalled.
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      if (!cameraDetailModalOpen) return;
      if (
        previewHealth?.mode !== 'mse' &&
        previewHealth?.mode !== 'mse-overlay' &&
        previewHealth?.mode !== 'ds-mse'
      ) {
        return;
      }
      if (!selectedCamera) return;
      previewReconnectAttemptRef.current = 0;
      restartActiveLivePreview();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [cameraDetailModalOpen, previewHealth?.mode, selectedCamera]);

  // Multi-camera assignment handlers
  const handleOpenAssignModal = (cameraType) => {
    setAssignCameraType(cameraType);
    setSelectedCameras([]);
    setSelectedCameraStreams({});
    setAssignError('');
    setAssignModalOpen(true);
  };

  const handleCloseAssignModal = () => {
    setAssignModalOpen(false);
    setAssignCameraType('');
    setSelectedCameras([]);
    setSelectedCameraStreams({});
    setAssignError('');
  };

  const handleCameraSelection = (cameraId) => {
    const camera = streams.find((s) => s.id === cameraId);
    const availableUrls = getCameraStreamUrls(camera);
    setSelectedCameras((prev) => {
      if (prev.includes(cameraId)) {
        setSelectedCameraStreams((prevStreams) => {
          const next = { ...prevStreams };
          delete next[cameraId];
          return next;
        });
        return prev.filter((id) => id !== cameraId);
      }
      if (!selectedCameraStreams[cameraId] && availableUrls.length > 0) {
        setSelectedCameraStreams((prevStreams) => ({
          ...prevStreams,
          [cameraId]: availableUrls[0],
        }));
      }
      return [...prev, cameraId];
    });
  };

  const handleSelectAllCameras = () => {
    const unassignedCameras = streams.filter(s => !s.camera_type);
    setSelectedCameras(unassignedCameras.map(s => s.id));
    const defaults = {};
    unassignedCameras.forEach((camera) => {
      const urls = getCameraStreamUrls(camera);
      if (urls.length > 0) {
        defaults[camera.id] = urls[0];
      }
    });
    setSelectedCameraStreams(defaults);
  };

  const handleClearSelection = () => {
    setSelectedCameras([]);
    setSelectedCameraStreams({});
  };

  const handleOpenRtspSnapshotPreview = async (camera, streamUrl, openPreviewDialog = true) => {
    const cameraIdentifier = camera?.id || camera?.label;
    if (!cameraIdentifier || !streamUrl) return;
    if (openPreviewDialog) {
      setPreviewModalOpen(true);
    }
    setPreviewLoading(true);
    setPreviewError('');
    setPreviewCamera(camera);
    setPreviewHealth(null);
    setPreviewRoi(null);
    setPreviewLaneRois({});
    setPreviewLaneCount(0);

    try {
      const result = await getRtspSnapshot(cameraIdentifier, streamUrl);
      const previewImage = result?.image_url
        ? buildApiUrl(result.image_url)
        : result?.image;
      if (!previewImage) {
        throw new Error(result?.message || 'Unable to fetch preview image');
      }
      setPreviewHealth({
        mode: 'rtsp',
        id: camera?.id || cameraIdentifier,
        label: camera?.label || cameraIdentifier,
        health_status: 'online',
        latency_ms: null,
        health_message: result.message || null,
        image: previewImage,
        resolution: null,
        roi: null,
        lane_rois: null,
      });
    } catch (err) {
      setPreviewError(err.message || 'Failed to fetch preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleConfiguredCameraPreview = async (camera) => {
    if (!camera) return;
    cameraDetailPreviewKindRef.current = 'configured';
    previewLivePlayingRef.current = false;
    previewStatusCheckTokenRef.current += 1;
    const index = streams.findIndex((c) => c.id === camera.id);
    setConfiguredNavIndex(index >= 0 ? index : 0);
    setSelectedCamera(camera);
    cameraDetailModalOpenRef.current = true;
    setCameraDetailModalOpen(true);
    setPreviewHealth(null);
    setPreviewRoi(null);
    setPreviewLaneRois({});
    setPreviewLaneCount(0);
    setPreviewError('');
    setPreviewLoading(false);
    resetPreviewZoom();
    setPreviewMuted(true);
    setLivePreviewQuality(normalizePreviewQuality(camera, DEFAULT_PREVIEW_QUALITY));
    if (isCameraStatusOffline(camera)) {
      clearConfiguredPreviewWindow();
      setPreviewHealth(null);
      setPreviewError('');
      setPreviewLoading(false);
      return;
    }
    if (index >= 0) {
      applyConfiguredPreviewMetadata(camera);
      rebuildConfiguredPreviewWindow(index);
      if (cardPreviewReadyRef.current.has(camera.id)) {
        handleLivePreviewLoad();
      }
    } else {
      clearConfiguredPreviewWindow();
      if (cardPreviewUrls[camera.id]) {
        applyConfiguredPreviewMetadata(camera);
      } else {
        startConfiguredLivePreview(camera);
      }
    }
  };

  const handleSelectedStreamChange = (cameraId, streamUrl) => {
    setSelectedCameraStreams((prev) => ({
      ...prev,
      [cameraId]: streamUrl,
    }));
    setSelectedCameras((prev) => (
      prev.includes(cameraId) ? prev : [...prev, cameraId]
    ));
  };

  const handleAssignCameras = async () => {
    if (selectedCameras.length === 0) {
      setAssignError('Please select at least one camera');
      return;
    }

    setAssignLoading(true);
    setAssignError('');

    try {
      const missingStreams = selectedCameras.filter((cameraId) => !selectedCameraStreams[cameraId]);
      if (missingStreams.length > 0) {
        throw new Error('Select one stream URL for each selected camera');
      }

      const assignedCameras = await Promise.all(
        selectedCameras.map(async (cameraId) => {
          const result = await updateCameraStream(cameraId, {
            camera_type: assignCameraType,
            rtsp_url: selectedCameraStreams[cameraId],
          });
          return {
            cameraId,
            camera_type: result.camera_type || assignCameraType,
            is_active: result.is_active ?? true,
            rtsp_url: result.rtsp_url || selectedCameraStreams[cameraId],
          };
        })
      );
      assignedCameras.forEach(({ cameraId, ...updates }) => applyCameraState(cameraId, updates));

      showSnackbar(
        `${selectedCameras.length} camera(s) assigned as ${assignCameraType} successfully!`,
        'success'
      );
      handleCloseAssignModal();
    } catch (err) {
      setAssignError(err.message || 'Failed to assign cameras');
    } finally {
      setAssignLoading(false);
    }
  };

  const loadCameraDetailHealthPreview = async (camera) => {
    if (!camera?.id) return;

    setPreviewLoading(true);
    setPreviewError('');
    try {
      const previewStreamUrl = getPrimaryRtspUrl(camera) || getCameraStreamUrls(camera)[0];
      const cachedRoi = getCachedCameraRoi(camera);
      const roiPromise = cachedRoi
        ? Promise.resolve(cachedRoi)
        : Promise.resolve(null);
      const [healthResult, roiResult] = await Promise.allSettled([
        getCameraHealthPreview(camera.id, previewStreamUrl || undefined),
        roiPromise,
      ]);

      if (healthResult.status === 'fulfilled') {
        const result = healthResult.value;
        setPreviewHealth(normalizeCameraHealth({
          id: result.id,
          label: result.label,
          camera_type: result.camera_type,
          health_status: result.status,
          latency_ms: result.latency_ms,
          health_message: result.message,
          image: result.image,
          resolution: result.resolution,
          roi: result.roi,
          lane_rois: result.lane_rois,
          detect_app_warning: result.detect_app_warning,
        }));
        if (result.number_of_lane !== undefined && result.number_of_lane !== null) {
          setPreviewLaneCount(Number(result.number_of_lane) || 0);
        }
      } else {
        setPreviewError(healthResult.reason?.message || 'Failed to load camera preview');
      }

      if (roiResult.status === 'fulfilled' && roiResult.value) {
        const rv = roiResult.value;
        let r = rv.roi;
        const rw = Number(r?.w);
        const rh = Number(r?.h);
        if (!r || !(rw > 0 && rh > 0)) {
          const rect = rv.rect;
          if (Array.isArray(rect) && rect.length === 4) {
            const x = Number(rect[0]);
            const y = Number(rect[1]);
            const w = Number(rect[2]);
            const h = Number(rect[3]);
            if (w > 0 && h > 0) r = { x, y, w, h };
            else r = null;
          } else {
            r = null;
          }
        }
        if (r) {
          setPreviewRoi(r);
        }
        if (rv.lane_rois && typeof rv.lane_rois === 'object') {
          setPreviewLaneRois(rv.lane_rois);
        }
        if (rv.number_of_lane !== undefined) {
          setPreviewLaneCount(Number(rv.number_of_lane) || 0);
        }
      }
    } catch (err) {
      setPreviewError(err.message || 'Failed to load camera preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleRefreshPreview = async () => {
    if (!selectedCamera) return;
    clearPreviewReconnectTimer();
    previewReconnectAttemptRef.current = 0;
    setPreviewError('');
    setPreviewSessionKey((k) => k + 1);
    if (cameraDetailPreviewKindRef.current === 'health') {
      const cameraType = String(selectedCamera.camera_type || '').toUpperCase();
      if (cameraType === 'IN' || cameraType === 'OUT') {
        startRoiLivePreview(selectedCamera, cameraType);
        return;
      }
      loadCameraDetailHealthPreview(selectedCamera);
      return;
    }
    if (cameraDetailPreviewKindRef.current === 'configured') {
      const camera = selectedCamera;
      const token = previewStatusCheckTokenRef.current + 1;
      previewStatusCheckTokenRef.current = token;
      previewLivePlayingRef.current = false;
      clearConfiguredPreviewWindow();
      setPreviewHealth(null);
      const statusEntry = await updateCameraStatus(camera);
      const isStale = previewStatusCheckTokenRef.current !== token || !cameraDetailModalOpenRef.current;
      if (isStale) return;
      if (statusEntry?.status === 'offline') {
        clearConfiguredPreviewWindow();
        setPreviewHealth(null);
        return;
      }
    }
    if (configuredNavIndex >= 0) {
      rebuildConfiguredPreviewWindow(configuredNavIndex);
      applyConfiguredPreviewMetadata(selectedCamera);
      return;
    }
    startConfiguredLivePreview(selectedCamera);
  };

  const handleLiveQualityChange = (newQuality) => {
    setLivePreviewQuality(newQuality);
    clearPreviewReconnectTimer();
    previewReconnectAttemptRef.current = 0;
    if (previewHealth?.mode !== 'mse' || !selectedCamera) return;
    if (cameraDetailPreviewKindRef.current !== 'configured') return;
    if (isCameraStatusOffline(selectedCamera)) return;
    if (configuredNavIndex >= 0) {
      if (reuseCardPreviewInModal) {
        const stream = resolvePreviewStreamForQuality(selectedCamera, newQuality);
        if (!stream?.streamUrl) {
          showSnackbar('No RTSP stream URL is available for this preview quality', 'error');
          return;
        }
        startCardPreview(selectedCamera, stream);
      }
      // Remount the MSE player the same way Refresh does, so the new quality
      // opens its own WebSocket instead of waiting on the previous socket.
      setPreviewError('');
      setPreviewSessionKey((k) => k + 1);
      rebuildConfiguredPreviewWindow(configuredNavIndex);
      applyConfiguredPreviewMetadata(selectedCamera);
      return;
    }
    setPreviewError('');
    setPreviewSessionKey((k) => k + 1);
    startConfiguredLivePreview(selectedCamera);
  };

  const handleOpenAddCamera = () => {
    setAddCameraModalOpen(true);
    setNewCamera({
      label: '',
      description: '',
      stream_urls: [''],
      sub_stream_url: '',
    });
    setAddCameraError('');
    setAddCameraTestLoading(false);
    setAddCameraTestImage(null);
    setAddCameraTestError('');
  };

  const handleCloseAddCamera = () => {
    setAddCameraModalOpen(false);
    setNewCamera({
      label: '',
      description: '',
      stream_urls: [''],
      sub_stream_url: '',
    });
    setAddCameraError('');
    setAddCameraTestLoading(false);
    setAddCameraTestImage(null);
    setAddCameraTestError('');
  };

  // Helper function to mask/unmask RTSP URL
  const maskRtspUrl = (url, show) => {
    if (!url || show) return url;
    
    // Find the pattern rtsp://[user:password@]host:port/path
    const rtspPattern = /^(rtsp:\/\/)([^@]*@)?(.+)$/;
    const match = url.match(rtspPattern);
    
    if (match) {
      const protocol = match[1];
      const credentials = match[2] || '';
      const rest = match[3] || '';
      
      if (credentials) {
        // Mask the credentials part
        const maskedCredentials = credentials.replace(/[^@]/g, '*');
        return protocol + maskedCredentials + rest;
      }
    }
    
    return url;
  };

  const getCameraStreamUrls = useCallback((camera) => {
    if (!camera) return [];
    if (Array.isArray(camera.stream_urls) && camera.stream_urls.length > 0) {
      return camera.stream_urls.filter(Boolean);
    }
    if (Array.isArray(camera.streams) && camera.streams.length > 0) {
      return camera.streams.map((stream) => stream?.stream_url).filter(Boolean);
    }
    if (camera.rtsp_url) return [camera.rtsp_url];
    return [];
  }, []);

  const handleNewCameraChange = (field) => (e) => {
    setNewCamera((prev) => ({
      ...prev,
      [field]: e.target.value,
    }));
    setAddCameraError('');
    if (field === 'sub_stream_url') {
      setAddCameraTestImage(null);
      setAddCameraTestError('');
    }
  };

  const handleStreamUrlChange = (index) => (e) => {
    const { value } = e.target;
    setNewCamera((prev) => ({
      ...prev,
      stream_urls: prev.stream_urls.map((url, idx) => (idx === index ? value : url)),
    }));
    setAddCameraError('');
    setAddCameraTestImage(null);
    setAddCameraTestError('');
  };

  const handleAddStreamUrlField = () => {
    setNewCamera((prev) => ({
      ...prev,
      stream_urls: [...prev.stream_urls, ''],
    }));
  };

  const handleRemoveStreamUrlField = (index) => () => {
    setNewCamera((prev) => {
      if (prev.stream_urls.length <= 1) {
        return {
          ...prev,
          stream_urls: [''],
        };
      }
      return {
        ...prev,
        stream_urls: prev.stream_urls.filter((_, idx) => idx !== index),
      };
    });
    setAddCameraError('');
  };

  const handleAddCameraTest = async () => {
    const primaryUrl = (newCamera.stream_urls[0] || '').trim();
    const subUrl = (newCamera.sub_stream_url || '').trim();
    const streamUrl = subUrl || primaryUrl;

    if (!streamUrl) {
      setAddCameraTestError('Enter a stream URL to test');
      setAddCameraTestImage(null);
      return;
    }
    if (!/^rtsps?:\/\//i.test(streamUrl)) {
      setAddCameraTestError("Invalid RTSP URL format. URL must start with 'rtsp://' or 'rtsps://'");
      setAddCameraTestImage(null);
      return;
    }

    setAddCameraTestLoading(true);
    setAddCameraTestError('');
    setAddCameraTestImage(null);

    try {
      const result = await getRtspSnapshot(null, streamUrl);
      if (!result?.image) {
        throw new Error(result?.message || 'Unable to fetch preview image');
      }
      setAddCameraTestImage(result.image);
    } catch (err) {
      setAddCameraTestError(err.message || 'Failed to fetch preview');
    } finally {
      setAddCameraTestLoading(false);
    }
  };

  const handleAddCamera = async () => {
    const label = newCamera.label.trim();
    const description = newCamera.description.trim();
    const streamUrls = newCamera.stream_urls.map((url) => url.trim()).filter(Boolean);
    const subStreamUrl = (newCamera.sub_stream_url || '').trim();

    if (!label) {
      setAddCameraError('Camera label is required');
      showSnackbar('Camera label is required', 'warning');
      return;
    }

    if (streamUrls.length === 0) {
      setAddCameraError('At least one stream URL is required');
      showSnackbar('At least one stream URL is required', 'warning');
      return;
    }

    if (!subStreamUrl) {
      setAddCameraError('Sub stream URL is required');
      showSnackbar('Sub stream URL is required', 'warning');
      return;
    }

    setAddCameraLoading(true);
    setAddCameraError('');
    
    try {
      const mergedUrls = [...streamUrls, subStreamUrl].filter(Boolean);
      const uniqueUrls = [...new Set(mergedUrls)];
      const result = await addCameraStream({
        label,
        description,
        stream_urls: uniqueUrls,
      });
      showSnackbar('Camera added successfully.', 'success');
      handleCloseAddCamera();
      await refreshCameraList(result?.camera?.id ? [result.camera.id] : []);
    } catch (err) {
      setAddCameraError(err.message || 'Failed to add camera');
    } finally {
      setAddCameraLoading(false);
    }
  };

  const handleOpenEditCamera = (camera) => {
    if (!camera?.id) {
      showSnackbar('Camera not found', 'warning');
      return;
    }
    const streamEntries = Array.isArray(camera.streams) && camera.streams.length > 0
      ? camera.streams.map((stream) => ({
          stream_id: stream.id || '',
          stream_url: stream.stream_url || '',
        }))
      : getCameraStreamUrls(camera).map((url) => ({
          stream_id: '',
          stream_url: url,
        }));
    const inferredSubStream = streamEntries.length > 1
      ? streamEntries[1]
      : { stream_id: '', stream_url: '' };

    setEditCamera({
      id: camera.id,
      label: camera.label || '',
      description: camera.description || camera.camera_description || '',
      sub_stream_url: inferredSubStream.stream_url || '',
      sub_stream_id: inferredSubStream.stream_id || '',
      streams: streamEntries.length > 0
        ? streamEntries
        : [{ stream_id: '', stream_url: '' }],
    });
    setEditCameraError('');
    setEditCameraModalOpen(true);
  };

  const handleCloseEditCamera = () => {
    setEditCameraModalOpen(false);
    setEditCameraError('');
    setEditCameraLoading(false);
    setEditCamera({
      id: '',
      label: '',
      description: '',
      sub_stream_url: '',
      sub_stream_id: '',
      streams: [{ stream_id: '', stream_url: '' }],
    });
  };

  const handleEditCameraChange = (field) => (e) => {
    setEditCamera((prev) => ({
      ...prev,
      [field]: e.target.value,
    }));
    setEditCameraError('');
  };

  const handleEditStreamUrlChange = (index) => (e) => {
    const { value } = e.target;
    setEditCamera((prev) => ({
      ...prev,
      streams: prev.streams.map((stream, idx) => (idx === index ? { ...stream, stream_url: value } : stream)),
    }));
    setEditCameraError('');
  };

  const handleAddEditStreamUrlField = () => {
    setEditCamera((prev) => ({
      ...prev,
      streams: [...prev.streams, { stream_id: '', stream_url: '' }],
    }));
  };

  const handleRemoveEditStreamUrlField = (index) => () => {
    setEditCamera((prev) => {
      if (prev.streams.length <= 1) {
        return {
          ...prev,
          streams: [{ stream_id: '', stream_url: '' }],
        };
      }
      return {
        ...prev,
        streams: prev.streams.filter((_, idx) => idx !== index),
      };
    });
    setEditCameraError('');
  };

  const handleSaveEditedCamera = async () => {
    const label = editCamera.label.trim();
    const description = editCamera.description.trim();
    const subStreamUrl = (editCamera.sub_stream_url || '').trim();

    if (!label) {
      setEditCameraError('Camera label is required');
      showSnackbar('Camera label is required', 'warning');
      return;
    }

    const withId = editCamera.streams
      .map((stream) => ({
        stream_id: String(stream.stream_id ?? ''),
        stream_url: (stream.stream_url || '').trim(),
      }))
      .filter((stream) => stream.stream_url && stream.stream_id.trim() !== '');
    const newUrls = editCamera.streams
      .map((stream) => (stream.stream_url || '').trim())
      .filter((url, idx) => url && (editCamera.streams[idx].stream_id ?? '').toString().trim() === '');

    if (!newUrls.length && !withId.length) {
      setEditCameraError('At least one stream URL is required');
      showSnackbar('At least one stream URL is required', 'warning');
      return;
    }

    if (!subStreamUrl) {
      setEditCameraError('Sub stream URL is required');
      showSnackbar('Sub stream URL is required', 'warning');
      return;
    }

    const existsInWithId = withId.some((stream) => stream.stream_url === subStreamUrl);
    const existsInNew = newUrls.includes(subStreamUrl);
    if (!existsInWithId && !existsInNew) {
      const subStreamId = String(editCamera.sub_stream_id || '').trim();
      if (subStreamId) {
        withId.push({
          stream_id: subStreamId,
          stream_url: subStreamUrl,
        });
      } else {
        newUrls.push(subStreamUrl);
      }
    }

    const hasStreamUpdates = withId.length > 0;
    const hasNewUrls = newUrls.length > 0;
    if (!hasStreamUpdates && !hasNewUrls && !label && !(description || '').trim()) {
      setEditCameraError('Enter a label or at least one stream URL (existing or new) to update.');
      return;
    }

    const originalCamera = streams.find((camera) => camera.id === editCamera.id);
    if (!originalCamera) {
      setEditCameraError('Camera not found');
      showSnackbar('Camera not found', 'error');
      return;
    }

    const originalStreams = getOrderedCameraStreams(originalCamera);
    const originalUrls = new Set(originalStreams.map((stream) => stream.streamUrl));
    const streamUrlChanged = withId.some((stream) => {
      const originalStream = originalStreams.find((item) => String(item.streamId || '') === stream.stream_id);
      return originalStream && originalStream.streamUrl !== stream.stream_url;
    }) || newUrls.some((url) => !originalUrls.has(url));

    const updatedUrlsById = new Map(withId.map((stream) => [stream.stream_id, stream.stream_url]));
    const optimisticStreams = originalStreams.map((stream) => ({
      ...(stream.streamId ? { id: stream.streamId } : {}),
      stream_url: updatedUrlsById.get(String(stream.streamId || '')) || stream.streamUrl,
    }));
    newUrls.forEach((url) => {
      if (!optimisticStreams.some((stream) => stream.stream_url === url)) {
        optimisticStreams.push({ id: null, stream_url: url });
      }
    });
    const optimisticCamera = {
      ...originalCamera,
      label,
      description,
      streams: optimisticStreams,
      stream_urls: optimisticStreams.map((stream) => stream.stream_url),
    };

    setEditCameraLoading(true);
    setEditCameraError('');

    try {
      const payload = { label: label || undefined, description: description || undefined };
      if (hasStreamUpdates) {
        payload.streams = withId.map((s) => ({
          stream_id: s.stream_id,
          stream_url: s.stream_url,
        }));
      }
      if (hasNewUrls) {
        payload.new_stream_urls = newUrls;
      }
      applyEditedCamera(optimisticCamera);
      await editCameraDetails(editCamera.id, payload);

      handleCloseEditCamera();
      if (streamUrlChanged) {
        snapshotRevisionRef.current.set(
          editCamera.id,
          (snapshotRevisionRef.current.get(editCamera.id) || 0) + 1,
        );
        await updateCameraStatus(optimisticCamera);
        await captureCameraSnapshot(optimisticCamera, { force: true });
      }
      showSnackbar('Camera updated successfully!', 'success');
    } catch (err) {
      applyEditedCamera(originalCamera);
      setEditCameraError(err.message || 'Failed to update camera');
    } finally {
      setEditCameraLoading(false);
    }
  };

  const handleOpenDeleteConfirm = (camera) => {
    setCameraToDelete(camera);
    setDeleteConfirmOpen(true);
  };

  const handleCloseDeleteConfirm = () => {
    setDeleteConfirmOpen(false);
    setCameraToDelete(null);
  };

  const handleOpenDeactivateConfirm = (camera) => {
    setCameraToDeactivate(camera);
    setDeactivateConfirmOpen(true);
  };

  const handleCloseDeactivateConfirm = () => {
    setDeactivateConfirmOpen(false);
    setCameraToDeactivate(null);
  };

  const handleDeactivateCameraConfirmed = async () => {
    if (!cameraToDeactivate?.id) return;

    try {
      await handleDeactivateCamera(cameraToDeactivate);
      showSnackbar(`Camera "${cameraToDeactivate.label || cameraToDeactivate.id}" de-assigned successfully!`, 'success');
      handleCloseDeactivateConfirm();
      handleCloseCameraDetail();
    } catch (err) {
      showSnackbar(err.message || 'Failed to de-assign camera', 'error');
    }
  };

  const handleDeleteCamera = async () => {
    if (!cameraToDelete?.id) return;

    setDeleteLoading(true);
    try {
      await deleteCamera(cameraToDelete.id);
      showSnackbar('Camera deleted successfully!', 'success');
      handleCloseDeleteConfirm();
      handleCloseCameraDetail();
      await refreshCameraList();
    } catch (err) {
      showSnackbar(err.message || 'Failed to delete camera', 'error');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleCheckWebViewHealth = async () => {
    setCheckingWebViewHealth(true);
    try {
      const result = await checkWebViewHealth();
      setWebViewHealth({
        status: result.status,
        connected_clients: result.connected_clients,
        server_status: result.server_status,
        message: result.message,
      });
      if (result.connected_clients > 0) {
        showSnackbar('WebView is connected', 'success');
      } else {
        showSnackbar('WebView is disconnected', 'warning');
      }
    } catch (err) {
      setWebViewHealth({
        status: 'error',
        connected_clients: 0,
        server_status: 'error',
        message: err.message || 'Unable to check WebView health',
      });
      showSnackbar(err.message || 'Unable to check WebView health', 'error');
    } finally {
      setCheckingWebViewHealth(false);
    }
  };

  const handleCheckDetectAppHealth = async () => {
    setCheckingDetectAppHealth(true);
    try {
      const result = await checkDetectAppHealth();
      setDetectAppHealth({
        status: result.status,
        message: result.message,
        response_time_ms: result.response_time_ms,
      });
      if (result.status === 'healthy') {
        showSnackbar(`Detect App is running (${result.response_time_ms}ms)`, 'success');
      } else {
        showSnackbar(result.message || 'Detect App is not healthy', 'warning');
      }
    } catch (err) {
      setDetectAppHealth({
        status: 'error',
        message: err.message || 'Unable to check Detect App health',
      });
      showSnackbar(err.message || 'Unable to check Detect App health', 'error');
    } finally {
      setCheckingDetectAppHealth(false);
    }
  };

  const handleCloseLateEntryConfirm = () => {
    if (lateEntrySaving) return;
    setLateEntryConfirmOpen(false);
  };

  const handleOpenLateEntryConfirm = () => {
    const timeRegex = /^([0-1][0-9]|2[0-3]):[0-5][0-9]$/;
    if (lateEntryActive && !timeRegex.test(lateEntryTime)) {
      setLateEntryError('Invalid time format. Please use HH:MM format (e.g., 10:30)');
      return;
    }
    setLateEntryError(null);
    setLateEntryConfirmOpen(true);
  };

  const handleConfirmSaveLateEntrySettings = async () => {
    const timeRegex = /^([0-1][0-9]|2[0-3]):[0-5][0-9]$/;
    if (lateEntryActive && !timeRegex.test(lateEntryTime)) {
      setLateEntryError('Invalid time format. Please use HH:MM format (e.g., 10:30)');
      return;
    }

    setLateEntrySaving(true);
    setLateEntryError(null);

    try {
      const response = await updateLateEntryTime({
        late_entry_time: lateEntryTime,
        late_entry_activate: lateEntryActive,
      });
      if (response.status === 'success') {
        setLateEntryMetadata({
          last_updated_by: response.last_updated_by,
          last_updated_at: response.last_updated_at,
        });
        if (typeof response.late_entry_activate === 'boolean') {
          setLateEntryActive(response.late_entry_activate);
        }
        showSnackbar('Late entry settings updated successfully!', 'success');
        setLateEntryConfirmOpen(false);
      } else {
        throw new Error(response.message || 'Failed to update late entry settings');
      }
    } catch (err) {
      setLateEntryError(err.message || 'Failed to update late entry settings');
      showSnackbar(err.message || 'Failed to update late entry settings', 'error');
    } finally {
      setLateEntrySaving(false);
    }
  };

  const handleSaveLaneCount = async (cameraType, laneValueOverride = null, cameraId = null) => {
    const rawValue = laneValueOverride ?? laneCount[cameraType];
    const parsedLane = rawValue === '' ? 0 : parseInt(rawValue, 10);

    if (Number.isNaN(parsedLane) || parsedLane < 0) {
      showSnackbar('Number of lane must be 0 or greater', 'warning');
      return;
    }

    try {
      const result = await saveROIConfig(cameraType, {
        ...(cameraId ? { camera_id: cameraId } : {}),
        number_of_lane: parsedLane,
        ...(parsedLane === 0 ? { lane_rois: {} } : {}),
      });

      setLaneCount((prev) => ({
        ...prev,
        [cameraType]: String(result.number_of_lane ?? parsedLane),
      }));
      const laneKey = cameraType === 'IN' ? 'in' : 'out';
      setSavedLaneRois((prev) => ({
        ...prev,
        [laneKey]: result.lane_rois || {},
      }));
      if (parsedLane === 0) {
        setRoiExistingLaneRois({});
      }
      showSnackbar(`Lane count saved for ${cameraType} camera`, 'success');
    } catch (err) {
      showSnackbar(err.message || 'Failed to save lane count', 'error');
    }
  };

  // ROI Drawing handlers
  const handleOpenROIDrawer = async (cameraType, camera = null) => {
    const cameraId = camera?.id || null;
    setRoiDrawerCameraType(cameraType);
    setRoiDrawerCameraId(cameraId);
    setRoiDrawerCameraLabel(camera?.label || cameraType);
    setRoiFetching(true);
    setRoiFrameImage(null);
    setRoiExisting(null);
    setRoiExistingLaneRois({});
    setRoiDrawerOpen(true);

    try {
      const [frameResult, configResult] = await Promise.allSettled([
        getFrameForROI(cameraType, { cameraId: cameraId || undefined }),
        cameraId && ['IN', 'OUT'].includes(String(cameraType).toUpperCase())
          ? getROIConfig(cameraType, { cameraId })
          : Promise.resolve(null),
      ]);
      if (frameResult.status === 'rejected') throw frameResult.reason;
      const result = frameResult.value;
      const config = configResult.status === 'fulfilled' && configResult.value
        ? normalizeRoiConfig(configResult.value)
        : null;

      if (configResult.status === 'rejected') {
        showSnackbar(configResult.reason?.message || `Failed to load ROI configuration for ${camera?.label || cameraType}`, 'error');
      } else if (config && cameraId) {
        setRoiConfigsByCameraId((previous) => ({
          ...previous,
          [cameraId]: config,
        }));
      }

      if (result.status === 'online' && result.image) {
        setRoiFrameImage(result.image);
      } else {
        showSnackbar(result.message || 'Could not fetch frame from camera', 'warning');
      }
      const roi = config?.roi || result?.roi;
      if (roi) {
        setRoiExisting({
          x: roi.x,
          y: roi.y,
          w: roi.w,
          h: roi.h,
        });
      }
      setRoiExistingLaneRois(config?.lane_rois || result?.lane_rois || {});
      const numberOfLanes = config?.number_of_lane ?? result?.number_of_lane;
      if (numberOfLanes !== undefined) {
        setLaneCount((prev) => ({
          ...prev,
          [cameraType]: String(numberOfLanes),
        }));
      }
    } catch (err) {
      showSnackbar(err.message || 'Failed to fetch frame for ROI', 'error');
    } finally {
      setRoiFetching(false);
    }
  };

  const handleCloseROIDrawer = () => {
    setRoiDrawerOpen(false);
    setRoiDrawerCameraType(null);
    setRoiDrawerCameraId(null);
    setRoiDrawerCameraLabel('');
    setRoiFrameImage(null);
    setRoiExisting(null);
    setRoiExistingLaneRois({});
  };

  const handleOpenFaceCrop = async (cameraType, camera = null) => {
    const normalizedType = String(cameraType || '').toUpperCase();
    if (!['IN', 'OUT'].includes(normalizedType)) {
      showSnackbar('Face crop configuration is available for IN and OUT cameras only', 'info');
      return;
    }

    setFaceCropCameraType(normalizedType);
    setFaceCropCameraId(camera?.id || null);
    setFaceCropCameraLabel(camera?.label || normalizedType);
    setFaceCropFrameImage(null);
    setFaceCropExistingRoi(null);
    setFaceCropConfig(null);
    setFaceCropOpen(true);
    setFaceCropLoading(true);

    try {
      const [frameResult, configResult] = await Promise.allSettled([
        getFrameForROI(normalizedType, { cameraId: camera?.id }),
        camera?.id
          ? getROIConfig(normalizedType, { cameraId: camera.id })
          : Promise.resolve(null),
      ]);

      if (frameResult.status === 'fulfilled') {
        const result = frameResult.value;
        if (result.status === 'online' && result.image) {
          setFaceCropFrameImage(result.image);
        } else {
          showSnackbar(result.message || 'Could not fetch frame from camera', 'warning');
        }
        if (result?.roi) {
          setFaceCropExistingRoi(result.roi);
        }
      } else {
        throw frameResult.reason;
      }

      if (configResult.status === 'rejected') {
        showSnackbar(configResult.reason?.message || `Failed to load ROI configuration for ${camera?.label || normalizedType}`, 'error');
      } else if (configResult.value) {
        const config = configResult.value;
        if (camera?.id) {
          setRoiConfigsByCameraId((previous) => ({
            ...previous,
            [camera.id]: normalizeRoiConfig(config),
          }));
        }
        if (config.roi) {
          setFaceCropExistingRoi(config.roi);
        }
        setFaceCropConfig(config.crop_configuration || null);
        if (config.number_of_lane !== undefined) {
          setLaneCount((prev) => ({
            ...prev,
            [normalizedType]: String(config.number_of_lane),
          }));
        }
      }
    } catch (err) {
      showSnackbar(err.message || 'Failed to load face crop frame', 'error');
    } finally {
      setFaceCropLoading(false);
    }
  };

  const handleFetchFaceCropFrame = async () => {
    if (!faceCropCameraType) return;

    setFaceCropLoading(true);
    try {
      const result = await getFrameForROI(faceCropCameraType, {
        cameraId: faceCropCameraId || undefined,
      });
      if (result.status === 'online' && result.image) {
        setFaceCropFrameImage(result.image);
      } else {
        showSnackbar(result.message || 'Could not fetch frame from camera', 'warning');
      }
      if (result?.roi) {
        setFaceCropExistingRoi(result.roi);
      }
    } catch (err) {
      showSnackbar(err.message || 'Failed to fetch face crop frame', 'error');
    } finally {
      setFaceCropLoading(false);
    }
  };

  const handleCloseFaceCrop = () => {
    setFaceCropOpen(false);
    setFaceCropCameraType(null);
    setFaceCropCameraId(null);
    setFaceCropCameraLabel('');
    setFaceCropFrameImage(null);
    setFaceCropExistingRoi(null);
    setFaceCropConfig(null);
  };

  const handleSaveFaceCrop = async (cropConfiguration) => {
    if (!faceCropCameraType) return;

    const roi = faceCropExistingRoi;
    const roiRect = roi ? [Number(roi.x), Number(roi.y), Number(roi.w), Number(roi.h)] : null;
    if (!roiRect || roiRect.some((value) => Number.isNaN(value)) || roiRect[2] <= 0 || roiRect[3] <= 0) {
      showSnackbar('Draw and save ROI before saving face crop configuration', 'warning');
      return;
    }

    setFaceCropSaving(true);
    try {
      const result = await saveROIConfig(faceCropCameraType, {
        ...(faceCropCameraId ? { camera_id: faceCropCameraId } : {}),
        rect: roiRect,
        number_of_lane: parseInt(laneCount[faceCropCameraType] || '0', 10) || 0,
        crop_configuration: cropConfiguration,
      });
      setFaceCropConfig(result.crop_configuration || cropConfiguration);
      showSnackbar(`Face crop configuration saved for ${faceCropCameraType} camera`, 'success');
      handleCloseFaceCrop();
    } catch (err) {
      showSnackbar(err.message || 'Failed to save face crop configuration', 'error');
    } finally {
      setFaceCropSaving(false);
    }
  };

  // ROI Configuration handlers
  const handleRoiTabChange = (event, newValue) => {
    setRoiTab(newValue);
  };

  const handleFetchROIFrame = async () => {
    if (!roiDrawerCameraType) return;
    
    setRoiFetching(true);
    try {
      const result = await getFrameForROI(roiDrawerCameraType, {
        cameraId: roiDrawerCameraId || undefined,
      });
      if (result.status === 'online' && result.image) {
        setRoiFrameImage(result.image);
      } else {
        showSnackbar(result.message || 'Could not fetch frame from camera', 'warning');
      }
      if (result?.number_of_lane !== undefined) {
        setLaneCount((prev) => ({
          ...prev,
          [roiDrawerCameraType]: String(result.number_of_lane),
        }));
      }
      if (result?.lane_rois) {
        setRoiExistingLaneRois(result.lane_rois || {});
      }
    } catch (err) {
      showSnackbar(err.message || 'Failed to fetch frame', 'error');
    } finally {
      setRoiFetching(false);
    }
  };

  const handleSaveROI = async (roiData) => {
    if (!roiDrawerCameraType) return;

    const laneValue = parseInt(roiData?.number_of_lane ?? laneCount[roiDrawerCameraType] ?? '0', 10);
    if (Number.isNaN(laneValue) || laneValue < 0) {
      showSnackbar('Number of lane must be 0 or greater', 'warning');
      return;
    }

    setRoiSaving(true);
    try {
      const result = await saveROIConfig(roiDrawerCameraType, {
        ...(roiDrawerCameraId ? { camera_id: roiDrawerCameraId } : {}),
        ...roiData,
        number_of_lane: laneValue,
        ...(laneValue === 0 ? { lane_rois: {} } : {}),
      });
      if (roiDrawerCameraId) {
        setRoiConfigsByCameraId((prev) => ({
          ...prev,
          [roiDrawerCameraId]: normalizeRoiConfig({
            ...result,
            camera_id: roiDrawerCameraId,
            camera_type: roiDrawerCameraType,
          }),
        }));
      }
      if (result.roi || result.rect) {
        const roiObj = result.roi || null;
        if (roiObj) {
          const key = roiDrawerCameraType === 'IN' ? 'in' : 'out';
          setSavedRoi((prev) => ({
            ...prev,
            [key]: { x: roiObj.x, y: roiObj.y, w: roiObj.w, h: roiObj.h },
          }));
          setSavedLaneRois((prev) => ({
            ...prev,
            [key]: result.lane_rois || {},
          }));
        }
        setLaneCount((prev) => ({
          ...prev,
          [roiDrawerCameraType]: String(result.number_of_lane ?? laneValue),
        }));
        showSnackbar(`ROI saved successfully for ${roiDrawerCameraType} camera`, 'success');
        handleCloseROIDrawer();
      } else {
        throw new Error(result.message || 'Failed to save ROI');
      }
    } catch (err) {
      showSnackbar(err.message || 'Failed to save ROI', 'error');
    } finally {
      setRoiSaving(false);
    }
  };

  const getAssignedCamerasByType = useCallback((cameraType) => {
    const normalized = String(cameraType || '').toUpperCase();
    return streams.filter((camera) => String(camera.camera_type || '').toUpperCase() === normalized);
  }, [streams]);

  const getPrimaryRtspUrl = useCallback((camera) => {
    if (camera?.rtsp_url) return camera.rtsp_url;
    const urls = getCameraStreamUrls(camera);
    return urls.length > 0 ? urls[0] : '';
  }, [getCameraStreamUrls]);

  const getPreviewRtspUrl = useCallback((camera) => {
    if (!camera) return '';
    const urls = getCameraStreamUrls(camera);
    if (urls.length === 0) return '';
    const preferred = pickPreferredSubstreamUrl(urls);
    if (preferred) return preferred;
    const primary = getPrimaryRtspUrl(camera);
    return primary || urls[0];
  }, [getCameraStreamUrls, getPrimaryRtspUrl, pickPreferredSubstreamUrl]);

  const HealthPreviewWithROI = ({ imageSrc, roi, laneRois, resolution, laneCount: laneCountInput, alt, showOverlay = true }) => {
    const containerRef = useRef(null);
    const canvasRef = useRef(null);
    const imgRef = useRef(null);

    const drawOverlay = useCallback(() => {
      const canvas = canvasRef.current;
      const img = imgRef.current;
      const container = containerRef.current;
      if (!showOverlay || !canvas || !img || !container || !roi) return;

      const naturalW = img.naturalWidth || (resolution ? resolution.width || resolution[0] : 0);
      const naturalH = img.naturalHeight || (resolution ? resolution.height || resolution[1] : 0);
      if (!naturalW || !naturalH) return;

      const containerW = container.clientWidth;
      const containerH = container.clientHeight;

      const imgAspect = naturalW / naturalH;
      const containerAspect = containerW / containerH;
      let renderedW, renderedH, offsetX, offsetY;
      if (imgAspect > containerAspect) {
        renderedW = containerW;
        renderedH = containerW / imgAspect;
      } else {
        renderedH = containerH;
        renderedW = containerH * imgAspect;
      }
      offsetX = (containerW - renderedW) / 2;
      offsetY = (containerH - renderedH) / 2;

      const scaleX = renderedW / naturalW;
      const scaleY = renderedH / naturalH;

      canvas.width = containerW;
      canvas.height = containerH;
      canvas.style.width = containerW + 'px';
      canvas.style.height = containerH + 'px';

      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const rx = offsetX + roi.x * scaleX;
      const ry = offsetY + roi.y * scaleY;
      const rw = roi.w * scaleX;
      const rh = roi.h * scaleY;

      const totalLanes = Number(laneCountInput) || 0;
      const laneRoiEntries = laneRois && typeof laneRois === 'object' ? Object.entries(laneRois) : [];
      if (laneRoiEntries.length > 0 && totalLanes > 0) {
        ctx.lineWidth = 1.6;
        ctx.strokeStyle = '#00ff00';
        ctx.fillStyle = '#00ff00';
        ctx.font = 'bold 10px Arial';
        laneRoiEntries
          .sort((a, b) => parseInt(a[0], 10) - parseInt(b[0], 10))
          .forEach(([laneKey, laneRect]) => {
            if (!laneRect || typeof laneRect !== 'object') return;
            const lx = offsetX + laneRect.x * scaleX;
            const ly = offsetY + laneRect.y * scaleY;
            const lw = laneRect.w * scaleX;
            const lh = laneRect.h * scaleY;
            if (!(lw > 0 && lh > 0)) return;
            ctx.strokeRect(lx, ly, lw, lh);
            const label = `Lane ${laneKey}`;
            const textWidth = ctx.measureText(label).width;
            ctx.fillText(label, lx + Math.max(2, (lw - textWidth) / 2), ly + 14);
          });
      } else if (totalLanes <= 1) {
        ctx.strokeStyle = '#00ff00';
        ctx.lineWidth = 2;
        ctx.strokeRect(rx, ry, rw, rh);

        ctx.fillStyle = 'rgba(0, 255, 0, 0.08)';
        ctx.fillRect(rx, ry, rw, rh);

        ctx.font = 'bold 11px Arial';
        ctx.fillStyle = '#00ff00';
        ctx.fillText('ROI Zone', rx + 4, ry - 4 > 10 ? ry - 4 : ry + 14);
      }

      if (laneRoiEntries.length === 0 && totalLanes > 1) {
        ctx.strokeStyle = '#00ff00';
        ctx.lineWidth = 1.5;
        for (let i = 1; i < totalLanes; i += 1) {
          const lineX = rx + (rw * i) / totalLanes;
          ctx.beginPath();
          ctx.moveTo(lineX, ry);
          ctx.lineTo(lineX, ry + rh);
          ctx.stroke();
        }

        ctx.font = 'bold 10px Arial';
        ctx.fillStyle = '#00ff00';
        for (let i = 0; i < totalLanes; i += 1) {
          const laneCenterX = rx + (rw * (i + 0.5)) / totalLanes;
          const label = `Lane ${i + 1}`;
          const textWidth = ctx.measureText(label).width;
          ctx.fillText(label, laneCenterX - textWidth / 2, ry + 14);
        }
      }
    }, [roi, laneRois, resolution, laneCountInput, showOverlay]);

    useEffect(() => {
      drawOverlay();
    }, [drawOverlay, imageSrc]);

    useEffect(() => {
      const handleResize = () => drawOverlay();
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }, [drawOverlay]);

    const handleImageLoad = useCallback(() => {
      drawOverlay();
      if (!showOverlay || !roi) return;
      const rw = Number(roi.w);
      const rh = Number(roi.h);
      if (!(rw > 0 && rh > 0)) return;
      const laneKeys =
        laneRois && typeof laneRois === 'object' ? Object.keys(laneRois).length : 0;
      console.info('[CameraStreams] preview ROI overlay draw complete', {
        roi: { x: roi.x, y: roi.y, w: rw, h: rh },
        laneRoiKeys: laneKeys,
        laneCount: Number(laneCountInput) || 0,
      });
    }, [drawOverlay, showOverlay, roi, laneRois, laneCountInput]);

    return (
      <Box ref={containerRef} sx={{ position: 'relative', width: '100%', height: '100%' }}>
        <img
          ref={imgRef}
          src={imageSrc}
          alt={alt || 'Camera preview'}
          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
          onLoad={handleImageLoad}
        />
        {showOverlay && roi && (
          <canvas
            ref={canvasRef}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              pointerEvents: 'none',
            }}
          />
        )}
      </Box>
    );
  };

  const renderHealthStatus = (key) => {
    const info = healthState[key];
    if (!info) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Health status unavailable
        </Typography>
      );
    }
    if (info.mode === 'device') {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Using local device {info.device_index ?? 0}
        </Typography>
      );
    }
    const chipColor = info.health_status === 'online' ? 'success' : 'error';
    return (
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1, flexWrap: 'wrap' }}>
        <Chip
          size="small"
          label={info.health_status === 'online' ? 'Online' : 'Offline'}
          color={chipColor}
        />
      </Stack>
    );
  };

  if (loading && mode !== 'camera-streams') {
    return (
      <Box sx={{ width: '100%' }}>
        <LinearProgress />
      </Box>
    );
  }

  if (error && mode !== 'camera-streams') {
    return <Alert severity="error">{error}</Alert>;
  }

  const roiInAssignedCameras = getAssignedCamerasByType('IN');
  const roiOutAssignedCameras = getAssignedCamerasByType('OUT');
  const roiActivityAssignedCameras = getAssignedCamerasByType('ACTIVITY');

  const interactive = true;

  return (
    <Box
      sx={mode === 'camera-streams' || mode === 'detection-settings' ? {
        fontFamily: 'Poppins, sans-serif',
        '& .MuiTypography-root, & .MuiButton-root': { fontFamily: 'inherit' },
      } : undefined}
    >
      {/* Page Header */}
      {mode === 'camera-streams' ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'center' },
            gap: 2,
            flexWrap: 'wrap',
            mb: 3,
            fontFamily: 'Poppins, sans-serif',
          }}
        >
          <Box>
            <Typography component="h1" sx={{ fontSize: { xs: '1.5rem', sm: '1.9rem' }, fontWeight: 700, color: '#303545' }}>
              Camera &amp; Configuration
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5, color: '#858da2' }}>
              Configured RTSP Cameras
            </Typography>
          </Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ width: { xs: '100%', sm: 'auto' } }}>
            <Button
              variant="contained"
              onClick={handleOpenAddCamera}
              startIcon={<AddCircleOutlineIcon />}
              fullWidth={isMobile}
              sx={{ textTransform: 'none', bgcolor: '#4b5d93', '&:hover': { bgcolor: '#3d4d7b' } }}
            >
              Add camera
            </Button>
          </Stack>
        </Box>
      ) : mode === 'detection-settings' ? (
        <Box sx={{ mb: 2 }}>
          <Typography component="h1" sx={{ fontSize: { xs: '1.5rem', sm: '1.9rem' }, fontWeight: 700, color: '#303545' }}>
            Detection Settings
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5, color: '#858da2' }}>
            Configure detection parameters, camera assignments, and regions of interest.
          </Typography>
          <Tabs
            value={detectionSettingsTab}
            onChange={(_, value) => setDetectionSettingsTab(value)}
            aria-label="Detection settings sections"
            sx={{ mt: 2, borderBottom: '1px solid #dfe4ed', '& .MuiTab-root': { minHeight: 52, textTransform: 'none', color: '#858da2', px: 2.5 }, '& .Mui-selected': { color: '#4b5d93 !important', bgcolor: '#eef1f8' }, '& .MuiTabs-indicator': { bgcolor: '#4b5d93', height: 3 } }}
          >
            <Tab label="Detection assignments & ROI" value="assignments" />
            <Tab label="Detection parameters" value="parameters" />
          </Tabs>
        </Box>
      ) : (
        <PageHeader
          title={
            mode === 'system-health-check'
              ? 'System Health Check'
              : mode === 'detection-settings'
                ? 'Detection Setting'
                : mode === 'face-embedding-config'
                  ? 'Face Embedding Config'
                  : mode === 'late-entry-config'
                    ? 'Late Entry Config'
                    : 'Camera & Configuration'
          }
          subtitle={
            mode === 'system-health-check'
              ? 'View current health status of all cameras in the system.'
              : mode === 'detection-settings'
                ? 'Adjust motion and face detection parameters.'
                : mode === 'face-embedding-config'
                  ? 'Manage face embedding and recognition settings.'
                  : mode === 'late-entry-config'
                    ? 'Configure entry and exit time rules for late arrivals.'
                    : 'Configured RTSP Cameras'
          }
          icon={null}
        />
      )}

      {mode === 'camera-streams' && (
        <>
          <Card variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden', mb: 2, borderColor: '#e0e5ef', boxShadow: '0 1px 2px rgba(35, 48, 78, 0.04)', fontFamily: 'Poppins, sans-serif' }}>
            <Box
              sx={{
                px: { xs: 2, sm: 3 },
                py: 2,
                borderBottom: '1px solid #e9edf4',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 2,
                flexWrap: 'wrap',
              }}
            >
              <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
                <Box sx={{ width: 30, height: 30, borderRadius: 1.5, bgcolor: '#eef1f8', color: '#50649a', display: 'grid', placeItems: 'center', fontWeight: 600, fontSize: 13 }}>{streams.length}</Box>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 800, color: '#343a4b', lineHeight: 1.3 }}>
                    Cameras Added
                  </Typography>
                </Box>
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                <Button
                  variant="outlined"
                  onClick={() => refreshAllCameraStatuses(visibleStreams)}
                  disabled={loading || loadingMoreCameraCount > 0 || (visibleStreams.length > 0 && visibleStreams.every((camera) => activeCardStreamsRef.current[camera.id]))}
                  startIcon={loadingMoreCameraCount > 0 ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
                  fullWidth={isMobile}
                  sx={{
                    textTransform: 'none',
                    borderColor: '#d7ddeb',
                    color: '#4b5d93',
                    '&.Mui-disabled': { borderColor: '#d1d5db', color: '#9ca3af', cursor: 'not-allowed' },
                  }}
                >
                  Refresh status
                </Button>
              </Stack>
            </Box>

            {loading ? (
              <Box sx={{ p: { xs: 1.5, sm: 2.25 }, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' }, gap: 2 }}>
                {Array.from({ length: CAMERA_PAGE_SIZE }).map((_, skeletonIndex) => (
                  <CameraConfigCardShimmer key={`initial-camera-skeleton-${skeletonIndex}`} />
                ))}
              </Box>
            ) : error ? (
              <Alert severity="error">{error}</Alert>
            ) : streams.length > 0 ? (
              cameraCardsOnly ? (
                <>
                <Box sx={{ p: { xs: 1.5, sm: 2.25 }, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' }, gap: 2 }}>
                  {visibleStreams.map((camera, index) => {
                    const streamUrls = Array.isArray(camera.stream_urls)
                      ? camera.stream_urls.filter(Boolean)
                      : Array.isArray(camera.streams)
                        ? camera.streams.map((stream) => stream?.stream_url).filter(Boolean)
                        : camera.rtsp_url
                          ? [camera.rtsp_url]
                          : [];
                    const streamCount = streamUrls.length;
                    const dotPalette = ['#14b8a6', '#4f46e5', '#94a3b8', '#f59e0b', '#ef4444'];
                    const statusInfo = cameraStatusMap[camera.id];
                    const isCheckingStatus = Boolean(cameraStatusLoading[camera.id]);
                    const statusValue = statusInfo?.status;
                    const statusLabel = isCheckingStatus
                      ? 'Checking'
                      : statusValue === 'online'
                        ? 'Online'
                        : statusValue === 'offline'
                          ? 'Offline'
                          : statusValue === 'error'
                            ? 'Error'
                            : 'Unknown';
                    const statusStyles = (() => {
                      if (statusLabel === 'Online') return { bgcolor: '#dcfce7', color: '#166534' };
                      if (statusLabel === 'Offline') return { bgcolor: '#e5e7eb', color: '#4b5563' };
                      if (statusLabel === 'Error') return { bgcolor: '#fef2f6', color: '#bb0e1b' };
                      if (statusLabel === 'Checking') return { bgcolor: '#e5e7eb', color: '#4b5563' };
                      return { bgcolor: '#e5e7eb', color: '#4b5563' };
                    })();
                    const statusColor = statusValue === 'online'
                      ? '#22c55e'
                      : statusValue === 'offline'
                        ? '#94a3b8'
                        : statusValue === 'error'
                          ? '#ef4444'
                          : '#94a3b8';

                    const createdAt = formatDate(camera.created_at);
                    const updatedAt = formatDate(camera.updated_at);

                    return (
                      <Card
                        key={camera.id}
                        variant="outlined"
                        onClick={() => handleConfiguredCameraPreview(camera)}
                        sx={{
                          borderRadius: 4,
                          padding: "8px",
                          overflow: 'hidden',
                          width: '100%',
                          height: '100%',
                          bgcolor: '#ffffff',
                          border: `1px solid ${statusColor}`,
                          borderTop: `3px solid ${statusColor}`,
                          display: 'flex',
                          flexDirection: 'column',
                          boxShadow: `0 10px 30px rgba(15,23,42,0.15)`,
                          cursor: interactive ? 'pointer' : 'default',
                          opacity:  1,
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': interactive ? {
                            boxShadow: `0 18px 36px rgba(15,23,42,0.15)`,
                            transform: 'translateY(-4px)',
                            borderColor: statusColor,
                          } : undefined,
                          '&:focus-visible': interactive ? {
                            outline: `3px solid ${PRIMARY_COLOR}`,
                            outlineOffset: 2,
                          } : undefined,
                          '&:active': interactive ? { transform: 'translateY(-2px)' } : undefined,
                        }}
                      >
                        <Box
                          ref={(element) => {
                            if (element) {
                              cardPreviewViewportRefs.current[camera.id] = element;
                            } else {
                              delete cardPreviewViewportRefs.current[camera.id];
                            }
                          }}
                          sx={{
                            aspectRatio: '16/9',
                            bgcolor: '#dce2ec',
                            position: 'relative',
                            overflow: 'hidden',
                            borderRadius: 2,
                            display: 'grid',
                            placeItems: 'center',
                            '&:hover .camera-card-stream-control, &:focus-within .camera-card-stream-control': {
                              opacity: '1 !important',
                              pointerEvents: 'auto !important',
                            },
                            '&:hover .camera-card-hover-overlay': {
                              opacity: '1 !important',
                              pointerEvents: 'auto !important',
                            },
                          }}
                        >
                          {cardPreviewUrls[camera.id] ? (
                            <ConfiguredMsePlayer
                              key={`${camera.id}:${cardPreviewUrls[camera.id]}`}
                              src={cardPreviewUrls[camera.id]}
                              muted={
                                reuseCardPreviewInModal && selectedCamera?.id === camera.id
                                  ? previewMuted
                                  : true
                              }
                              paused={Boolean(cardPreviewPaused[camera.id])}
                              onPauseFrame={(frame) => handleCardPreviewPause(
                                camera,
                                resolvePreviewStreamForQuality(
                                  camera,
                                  normalizePreviewQuality(camera, DEFAULT_PREVIEW_QUALITY),
                                )?.streamUrl,
                                frame,
                              )}
                              onPlaying={() => {
                                cardPreviewReadyRef.current.add(camera.id);
                                if (reuseCardPreviewInModal && selectedCamera?.id === camera.id) {
                                  handleConfiguredSlotPlaying(camera.id);
                                }
                              }}
                              onError={(error) => {
                                if (reuseCardPreviewInModal && selectedCamera?.id === camera.id) {
                                  handleConfiguredSlotError(camera.id);
                                } else {
                                  showSnackbar(
                                    error?.message || `Unable to play ${camera.label || 'camera'} stream`,
                                    'error',
                                  );
                                }
                              }}
                            />
                          ) : (
                            <Box
                              component="img"
                              aria-label='camera-snapshot'
                              src={
                                cameraSnapshots[camera.id]
                                || (camera.snapshot_image_url && buildApiUrl(camera.snapshot_image_url))
                                || camera.health_image
                                || camera.image
                                || '/camera-placeholder.svg'
                              }
                              alt={`${camera.label || 'Camera'} snapshot`}
                              onError={(event) => {
                                if (event.currentTarget.src.endsWith('/camera-placeholder.svg')) return;
                                event.currentTarget.src = '/camera-placeholder.svg';
                              }}
                              sx={{
                                position: 'absolute',
                                inset: 0,
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                borderRadius: 2,
                                filter: [
                                  statusValue === 'offline' || statusValue === 'error' ? 'grayscale(1) blur(2px)' : '',
                                  cameraSnapshotRefreshing[camera.id] ? 'blur(4px)' : '',
                                ].filter(Boolean).join(' ') || 'none',
                                transition: 'filter 150ms ease',
                              }}
                            />
                          )}
                          <Tooltip title={statusInfo?.message || statusLabel}>
                            <Chip
                              className="camera-card-hover-overlay"
                              size="small"
                              icon={<FiberManualRecordIcon sx={{ fontSize: '8px !important', color: 'inherit !important' }} />}
                              label={statusLabel}
                              sx={{ position: 'absolute', top: 10, left: 10, height: 24, fontSize: 11, bgcolor: statusLabel === 'Online' ? '#e2f5eb' : statusStyles.bgcolor, color: statusLabel === 'Online' ? '#39805e' : statusStyles.color, zIndex: 1, opacity: 0, pointerEvents: 'none', transition: 'opacity 150ms ease' }}
                            />
                          </Tooltip>
                          {!cardPreviewUrls[camera.id] || cardPreviewPaused[camera.id] ? (
                            <Tooltip title={`Refresh ${camera.label || 'camera'} status and snapshot`}>
                              <IconButton
                                aria-label={`Refresh ${camera.label || 'camera'} status and snapshot`}
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  void updateCameraStatus(camera);
                                  void handleRefreshCameraSnapshot(camera);
                                }}
                                className="camera-card-hover-overlay"
                                // disabled={isCheckingStatus || Boolean(cameraSnapshotRefreshing[camera.id])}
                                sx={{ 
                                  position: 'absolute', 
                                  top: 7, 
                                  right: 7, 
                                  zIndex: 2, 
                                  bgcolor: 'rgba(255,255,255,0.85)', 
                                  '&:hover': { bgcolor: '#fff' },
                                  opacity: 0,
                                  transition: 'opacity 150ms ease',
                                  pointerEvents: 'none'
                                }}
                              >
                                {isCheckingStatus || cameraSnapshotRefreshing[camera.id]
                                  ? <CircularProgress size={15} />
                                  : <RefreshIcon fontSize="small" />}
                              </IconButton>
                            </Tooltip>
                          ) : null}
                          <Tooltip title={`Play/Pause`}>
                            <IconButton
                              className={cardPreviewUrls[camera.id] ? 'camera-card-stream-control' : 'camera-card-hover-overlay'}
                              aria-label={`${cardPreviewUrls[camera.id] && !cardPreviewPaused[camera.id] ? 'Pause' : 'Play'} ${camera.label || 'camera'} stream`}
                              onClick={(event) => {
                                event.stopPropagation();
                                handleCardPreviewToggle(camera);
                              }}
                              sx={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                zIndex: 1,
                                bgcolor: 'rgba(255,255,255,0.94)',
                                color: '#7183ad',
                                opacity: cardPreviewUrls[camera.id] && !cardPreviewPaused[camera.id]? 0.3 : 1,
                                pointerEvents: 'auto',
                                transition: 'opacity 150ms ease',
                                '&:hover': { bgcolor: '#fff' },
                              }}
                            >
                              {cardPreviewUrls[camera.id] && !cardPreviewPaused[camera.id]
                                ? <PauseCircleOutlineIcon />
                                : <PlayCircleOutlineIcon />}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={`Open ${camera.label || 'camera'} preview`}>
                            <IconButton
                              className="camera-card-hover-overlay"
                              aria-label={`Open fullscreen preview for ${camera.label || 'camera'}`}
                              onClick={(event) => {
                                event.stopPropagation();
                                handleConfiguredCameraPreview(camera);
                              }}
                              sx={{ position: 'absolute', bottom: 7, right: 7, zIndex: 2, bgcolor: 'rgba(255,255,255,0.88)', color: '#7183ad', opacity: 0, pointerEvents: 'none', transition: 'opacity 150ms ease', '&:hover': { bgcolor: '#fff' } }}
                            >
                              <FullscreenIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Typography className="camera-card-hover-overlay" variant="caption" sx={{ position: 'absolute', bottom: 8, left: 10, px: 0.75, py: 0.15, bgcolor: 'rgba(255,255,255,0.84)', color: '#68748a', borderRadius: 0.5, fontSize: 10, opacity: 0, pointerEvents: 'none', transition: 'opacity 150ms ease' }}>
                            Camera snapshot
                          </Typography>
                        </Box>
                        <CardContent sx={{ p: 1.75, '&:last-child': { pb: 1.75 } }}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="subtitle2" noWrap sx={{ fontWeight: 600, flex: 1, color: '#343a4b' }}>
                              {camera.label || `Camera ${index + 1}`}
                            </Typography>
                            <Tooltip title="Edit camera">
                              <IconButton
                                aria-label={`Edit ${camera.label || 'camera'}`}
                                size="small"
                                onClick={(e) => { e.stopPropagation(); handleOpenEditCamera(camera); }}
                                sx={{ bgcolor: '#eef1f8', color: '#697da9', borderRadius: 1 }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete camera">
                              <IconButton
                                aria-label={`Delete ${camera.label || 'camera'}`}
                                size="small"
                                onClick={(e) => { e.stopPropagation(); handleOpenDeleteConfirm(camera); }}
                                sx={{ color: '#929bad' }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                          <Tooltip title={camera.description || camera.camera_description || 'No description'} arrow >
                            <Typography variant="caption" noWrap sx={{ display: 'block', mt: 0.6, color:"text.secondary", fontSize: { xs: '0.8rem', sm: '0.85rem' } }}>
                              {camera.description || camera.camera_description || 'No description'} · {streamCount} stream{streamCount === 1 ? '' : 's'}
                            </Typography>
                          </Tooltip>
                          <Divider sx={{pt: 1, mb: 1}} />
                          {
                            [
                              {
                                label : "Created By :", 
                                value : `${camera.created_by_name}, ${createdAt}`
                              },
                              {
                                label : "Updated By :", 
                                value : `${camera.updated_by_name || "-"}, ${updatedAt}`
                              }
                            ].map((entry)=>(
                                <Box sx={{ display: 'flex' }}>
                                  <Typography variant="caption" sx={{ 
                                    color: '#3a3b3b', 
                                    minWidth: 0,
                                    pr: 2,
                                    fontSize: { xs: '0.8rem', sm: '0.85rem' },
                                    }} 
                                    noWrap
                                  >
                                    {entry.label}
                                  </Typography>
                                  <Tooltip title={entry.value || ''} arrow disableHoverListener={!entry.value || entry.value === '—'}>
                                    <Typography variant="body2" sx={{ 
                                      color: '#28292b', 
                                      minWidth: 0, flex: 1, 
                                      textAlign: "right", 
                                      fontWeight: 700,
                                      fontSize: { xs: '0.8rem', sm: '0.85rem' },
                                      }} 
                                      noWrap
                                    >
                                      {entry.value || '-'  }
                                    </Typography>
                                  </Tooltip>
                                </Box>
                            ))
                          }
                        </CardContent>
                      </Card>
                    );
                  })}
                  {Array.from({ length: loadingMoreCameraCount }).map((_, skeletonIndex) => (
                    <Card key={`camera-skeleton-${skeletonIndex}`} variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                      <Skeleton variant="rectangular" height={122} />
                      <CardContent sx={{ p: 2 }}>
                        <Skeleton variant="text" width="55%" height={26} />
                        <Skeleton variant="text" width="80%" />
                        <Skeleton variant="text" width="40%" />
                      </CardContent>
                    </Card>
                  ))}
                </Box>
                </>
              ) : (
                <Box sx={{ overflowX: 'auto' }}>
                  <Table sx={{ minWidth: 1180 }}>
                    <TableHead sx={{ bgcolor: 'grey.50' }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Camera Label</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Stream URLs</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Created By</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Updated By</TableCell>
                        <TableCell sx={{ fontWeight: 600, textAlign: 'right' }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {visibleStreams.map((camera, index) => {
                        const streamUrls = Array.isArray(camera.stream_urls)
                          ? camera.stream_urls.filter(Boolean)
                          : Array.isArray(camera.streams)
                            ? camera.streams.map((stream) => stream?.stream_url).filter(Boolean)
                            : camera.rtsp_url
                              ? [camera.rtsp_url]
                              : [];
                        const streamCount = streamUrls.length;
                        const dotPalette = ['#14b8a6', '#4f46e5', '#94a3b8', '#f59e0b', '#ef4444'];
                        const statusInfo = cameraStatusMap[camera.id];
                        const isCheckingStatus = Boolean(cameraStatusLoading[camera.id]);
                        const statusValue = statusInfo?.status;
                        const statusLabel = isCheckingStatus
                          ? 'Checking'
                          : statusValue === 'online'
                            ? 'Online'
                            : statusValue === 'offline'
                              ? 'Offline'
                              : statusValue === 'error'
                                ? 'Error'
                                : 'Unknown';
                        const statusStyles = (() => {
                          if (statusLabel === 'Online') return { bgcolor: '#dcfce7', color: '#166534' };
                          if (statusLabel === 'Offline') return { bgcolor: '#fee2e2', color: '#991b1b' };
                          if (statusLabel === 'Error') return { bgcolor: '#fde68a', color: '#92400e' };
                          if (statusLabel === 'Checking') return { bgcolor: '#e5e7eb', color: '#4b5563' };
                          return { bgcolor: '#e5e7eb', color: '#4b5563' };
                        })();

                        return (
                          <TableRow
                            key={camera.id}
                            hover
                            onClick={() => handleConfiguredCameraPreview(camera)}
                            sx={{ cursor: 'pointer' }}
                          >
                            <TableCell>
                              <Stack direction="row" spacing={1.25} alignItems="center">
                                <FiberManualRecordIcon
                                  sx={{
                                    fontSize: 14,
                                    color: dotPalette[index % dotPalette.length],
                                  }}
                                />
                                <Typography sx={{ fontWeight: 600 }}>
                                  {camera.label || `Camera ${index + 1}`}
                                </Typography>
                              </Stack>
                            </TableCell>
                            <TableCell sx={{ color: 'text.secondary' }}>
                              {camera.description || camera.camera_description || '-'}
                            </TableCell>
                            <TableCell>
                              {streamCount > 0 ? (
                                <Chip
                                  size="small"
                                  label={`${streamCount} Stream${streamCount > 1 ? 's' : ''}`}
                                  sx={{ bgcolor: '#e8ecfb', color: '#3359c9', fontWeight: 600 }}
                                  title={streamUrls.join('\n')}
                                />
                              ) : (
                                <Typography variant="body2" color="text.secondary">-</Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Stack direction="row" spacing={0.5} alignItems="center">
                                <Tooltip title={statusInfo?.message || ''}>
                                  <Chip
                                    size="small"
                                    label={statusLabel}
                                    sx={{
                                      minWidth: 86,
                                      ...statusStyles,
                                      fontWeight: 600,
                                    }}
                                  />
                                </Tooltip>
                                <Tooltip title="Refresh status">
                                  <span>
                                    <IconButton
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        updateCameraStatus(camera);
                                      }}
                                      disabled={isCheckingStatus}
                                    >
                                      {isCheckingStatus ? (
                                        <CircularProgress size={16} />
                                      ) : (
                                        <RefreshIcon fontSize="small" />
                                      )}
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </Stack>
                            </TableCell>
                            <TableCell sx={{ minWidth: 180 }}>
                              {renderUserTimestampCell(
                                camera.created_by_name || camera.created_by,
                                camera.created_at
                              )}
                            </TableCell>
                            <TableCell sx={{ minWidth: 180 }}>
                              {renderUserTimestampCell(
                                camera.updated_by_name || camera.updated_by,
                                camera.updated_at
                              )}
                            </TableCell>
                            <TableCell align="right">
                            <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                <Tooltip title="Preview">
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleConfiguredCameraPreview(camera);
                                    }}
                                  >
                                    <PlayCircleOutlineIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Edit">
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleOpenEditCamera(camera);
                                    }}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleOpenDeleteConfirm(camera);
                                    }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {Array.from({ length: loadingMoreCameraCount }).map((_, skeletonIndex) => (
                        <TableRow key={`camera-skeleton-${skeletonIndex}`}>
                          {Array.from({ length: 7 }).map((__, cellIndex) => (
                            <TableCell key={cellIndex}>
                              <Skeleton variant="text" height={24} />
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                No cameras configured. Cameras will appear here when registered from the external service.
              </Typography>
            )}

            {!loading && streams.length > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, px: { xs: 2, sm: 2.5 }, py: 1.5, borderTop: '1px solid #edf0f5' }}>
                <Typography variant="caption" sx={{ color: '#858da2' }}>
                  Showing {visibleStreams.length} of {streams.length} cameras
                </Typography>
                {hasMoreCameras ? (
                  <Button
                    variant="outlined"
                    onClick={handleLoadMoreCameras}
                    disabled={loadingMoreCameraCount > 0}
                    startIcon={loadingMoreCameraCount > 0 ? <CircularProgress size={16} color="inherit" /> : null}
                    sx={{ textTransform: 'none', borderColor: '#d7ddeb', color: '#4b5d93' }}
                  >
                    {loadingMoreCameraCount > 0 ? 'Loading...' : `Load more · ${streams.length - visibleCameraCount}`}
                  </Button>
                ) : null}
              </Box>
            )}
          </Card>
        </>
      )}

      {mode === 'detection-settings' && detectionSettingsTab === 'assignments' && (
        <>
          <Card variant="outlined" sx={{ mt: 3, borderRadius: 3, overflow: 'hidden', borderColor: '#e0e5ef', boxShadow: '0 1px 2px rgba(35, 48, 78, 0.04)', fontFamily: 'Poppins, sans-serif' }}>
          <Box sx={{ px: { xs: 2, sm: 2.5 }, py: 1.75, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#343a4b', lineHeight: 1.3 }}>
                Detection assignments &amp; ROI
              </Typography>
              <Typography variant="caption" sx={{ color: '#858da2' }}>
                Choose where each camera is used, then configure its detection area.
              </Typography>
            </Box>
            <Button
              variant="contained"
              size="small"
              onClick={() => handleOpenAssignModal(roiTab === 'activity' ? 'ACTIVITY' : roiTab)}
              startIcon={<CheckCircleIcon fontSize="small" />}
              sx={{ textTransform: 'none', bgcolor: '#4b5d93', '&:hover': { bgcolor: '#3d4d7b' } }}
            >
              Assign camera
            </Button>
          </Box>

          <Box sx={{ borderBottom: '1px solid #e9edf4', px: { xs: 1, sm: 2 } }}>
            <Tabs
              value={roiTab}
              onChange={handleRoiTabChange}
              aria-label="ROI setup and configuration tabs"
              variant={isAssignmentCompact ? 'scrollable' : 'standard'}
              scrollButtons={isAssignmentCompact ? 'auto' : false}
              allowScrollButtonsMobile
              sx={{ minHeight: 46, '& .MuiTab-root': { minHeight: 46, textTransform: 'none', color: '#858da2' }, '& .Mui-selected': { color: '#4b5d93 !important' }, '& .MuiTabs-indicator': { bgcolor: '#4b5d93' } }}
            >
              <Tab label={`IN · ${roiInAssignedCameras.length}`} value="IN" />
              <Tab label={`OUT · ${roiOutAssignedCameras.length}`} value="OUT" />
              <Tab label={`Activity · ${roiActivityAssignedCameras.length}`} value="activity" />
            </Tabs>
          </Box>

          {/* Tab Content */}
          {roiTab === 'IN' && (
            <Box sx={{ px: { xs: 2, sm: 2.5 }, py: 1.75 }}>
              {roiInAssignedCameras.length === 0 ? (
                <Alert severity="info">No IN cameras assigned yet.</Alert>
              ) : (
                isAssignmentCompact ? (
                  <Stack spacing={1.5}>
                    {roiInAssignedCameras.map((camera) => (
                      <Card key={`roi-in-${camera.id}`} variant="outlined" sx={{ borderRadius: 2 }}>
                        <CardContent sx={{ p: 2 }}>
                          <Stack spacing={1.25}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'flex-start' }}>
                              <Box sx={{ minWidth: 0 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                  {camera.label || `Camera ${camera.id}`}
                                </Typography>
                              </Box>
                            </Box>

                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                RTSP URL
                              </Typography>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                                {getPrimaryRtspUrl(camera) || '-'}
                              </Typography>
                            </Box>

                            <Divider />

                            <Stack spacing={1}>
                              {renderRoiAssignedManageActions(camera, 'IN', {
                                showFaceCrop: true,
                                showDeAssign: roiInAssignedCameras.length > 1,
                                deAssignDisabled: updatingCameraId === camera.id,
                                fullWidth: false,
                              })}
                            </Stack>
                          </Stack>
                        </CardContent>
                      </Card>
                    ))}
                  </Stack>
                ) : (
                  <Box sx={{ overflowX: 'auto' }}>
                    <Table sx={{ minWidth: 760 }}>
                      <TableHead sx={{ bgcolor: 'grey.50' }}>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Camera Label</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>RTSP URL</TableCell>
                          <TableCell sx={{ fontWeight: 600, textAlign: 'right' }}>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {roiInAssignedCameras.map((camera) => (
                          <TableRow key={`roi-in-${camera.id}`} hover>
                            <TableCell>{camera.label || `Camera ${camera.id}`}</TableCell>
                            <TableCell sx={{ maxWidth: 380 }}>
                              <Typography
                                variant="body2"
                                sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}
                              >
                                {getPrimaryRtspUrl(camera) || '-'}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              {renderRoiAssignedManageActions(camera, 'IN', {
                                showFaceCrop: true,
                                showDeAssign: roiInAssignedCameras.length > 1,
                                deAssignDisabled: updatingCameraId === camera.id,
                              })}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                )
              )}
            </Box>
          )}

          {roiTab === 'OUT' && (
            <Box sx={{ px: { xs: 2, sm: 2.5 }, py: 1.75 }}>
              {roiOutAssignedCameras.length === 0 ? (
                <Alert severity="info">No OUT cameras assigned yet.</Alert>
              ) : (
                isAssignmentCompact ? (
                  <Stack spacing={1.5}>
                    {roiOutAssignedCameras.map((camera) => (
                      <Card key={`roi-out-${camera.id}`} variant="outlined" sx={{ borderRadius: 2 }}>
                        <CardContent sx={{ p: 2 }}>
                          <Stack spacing={1.25}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'flex-start' }}>
                              <Box sx={{ minWidth: 0 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                  {camera.label || `Camera ${camera.id}`}
                                </Typography>
                              </Box>
                            </Box>

                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                RTSP URL
                              </Typography>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                                {getPrimaryRtspUrl(camera) || '-'}
                              </Typography>
                            </Box>

                            <Divider />

                            <Stack spacing={1}>
                              {renderRoiAssignedManageActions(camera, 'OUT', {
                                showFaceCrop: true,
                                showDeAssign: roiOutAssignedCameras.length > 1,
                                deAssignDisabled: updatingCameraId === camera.id,
                                fullWidth: false,
                              })}
                            </Stack>
                          </Stack>
                        </CardContent>
                      </Card>
                    ))}
                  </Stack>
                ) : (
                  <Box sx={{ overflowX: 'auto' }}>
                    <Table sx={{ minWidth: 760 }}>
                      <TableHead sx={{ bgcolor: 'grey.50' }}>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Camera Label</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>RTSP URL</TableCell>
                          <TableCell sx={{ fontWeight: 600, textAlign: 'right' }}>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {roiOutAssignedCameras.map((camera) => (
                          <TableRow key={`roi-out-${camera.id}`} hover>
                            <TableCell>{camera.label || `Camera ${camera.id}`}</TableCell>
                            <TableCell sx={{ maxWidth: 380 }}>
                              <Typography
                                variant="body2"
                                sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}
                              >
                                {getPrimaryRtspUrl(camera) || '-'}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              {renderRoiAssignedManageActions(camera, 'OUT', {
                                showFaceCrop: true,
                                showDeAssign: roiOutAssignedCameras.length > 1,
                                deAssignDisabled: updatingCameraId === camera.id,
                              })}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                )
              )}
            </Box>
          )}

          {roiTab === 'activity' && (
            <Box sx={{ px: { xs: 2, sm: 2.5 }, py: 1.75 }}>
              {roiActivityAssignedCameras.length === 0 ? (
                <Alert severity="info">No ACTIVITY cameras assigned yet.</Alert>
              ) : (
                isAssignmentCompact ? (
                  <Stack spacing={1.5}>
                    {roiActivityAssignedCameras.map((camera) => (
                      <Card key={`roi-activity-${camera.id}`} variant="outlined" sx={{ borderRadius: 2 }}>
                        <CardContent sx={{ p: 2 }}>
                          <Stack spacing={1.25}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'flex-start' }}>
                              <Box sx={{ minWidth: 0 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                  {camera.label || `Camera ${camera.id}`}
                                </Typography>
                              </Box>
                            </Box>

                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                RTSP URL
                              </Typography>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                                {getPrimaryRtspUrl(camera) || '-'}
                              </Typography>
                            </Box>

                            <Divider />

                            <Stack spacing={1}>
                              {renderRoiAssignedManageActions(camera, 'ACTIVITY', {
                                showFaceCrop: false,
                                showDeAssign: true,
                                deAssignDisabled: updatingCameraId === camera.id,
                                fullWidth: false,
                                showDrawRoi: false
                              })}
                            </Stack>
                          </Stack>
                        </CardContent>
                      </Card>
                    ))}
                  </Stack>
                ) : (
                  <Box sx={{ overflowX: 'auto' }}>
                    <Table sx={{ minWidth: 760 }}>
                      <TableHead sx={{ bgcolor: 'grey.50' }}>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Camera Label</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>RTSP URL</TableCell>
                          <TableCell sx={{ fontWeight: 600, textAlign: 'right' }}>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {roiActivityAssignedCameras.map((camera) => (
                          <TableRow key={`roi-activity-${camera.id}`} hover>
                            <TableCell>{camera.label || `Camera ${camera.id}`}</TableCell>
                            <TableCell sx={{ maxWidth: 380 }}>
                              <Typography
                                variant="body2"
                                sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}
                              >
                                {getPrimaryRtspUrl(camera) || '-'}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              {renderRoiAssignedManageActions(camera, 'ACTIVITY', {
                                showFaceCrop: false,
                                showDeAssign: true,
                                deAssignDisabled: updatingCameraId === camera.id,
                              })}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                )
              )}
            </Box>
          )}

          <Box sx={{ px: { xs: 2, sm: 2.5 }, py: 1.25, bgcolor: '#f3f5f9', borderTop: '1px solid #edf0f5' }}>
            <Typography variant="caption" sx={{ color: '#858da2' }}>
              Unassign removes the detection assignment. The camera remains in Configured cameras.
            </Typography>
          </Box>
          </Card>
        </>
      )}

      {mode === 'system-health-check' && (
        <>
          {/* System Health Checks */}
          <Card elevation={2} sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            System Health Checks
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Check the health status of system components to ensure proper connectivity.
          </Typography>
          
          <Grid container spacing={3}>
            {/* WebView Health Check */}
            <Grid item xs={12} md={6}>
              <Stack spacing={2}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Button
                    variant="outlined"
                    onClick={handleCheckWebViewHealth}
                    disabled={checkingWebViewHealth}
                    startIcon={checkingWebViewHealth ? <CircularProgress size={20} color="inherit" /> : <FiberManualRecordIcon />}
                  >
                    {checkingWebViewHealth ? 'Checking...' : 'Check WebView Connection'}
                  </Button>
                </Stack>
                
                {webViewHealth && (
                  <Stack spacing={1} sx={{ p: 2, bgcolor: 'rgba(0, 0, 0, 0.02)', borderRadius: 1 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip
                        size="small"
                        label={webViewHealth.connected_clients > 0 ? 'Online' : 'Offline'}
                        color={webViewHealth.connected_clients > 0 ? 'success' : 'error'}
                      />
                    </Stack>
                    {webViewHealth.message && (
                      <Typography variant="caption" color="text.secondary">
                        {webViewHealth.message}
                      </Typography>
                    )}
                  </Stack>
                )}
              </Stack>
            </Grid>

            {/* Detect App Health Check */}
            <Grid item xs={12} md={6}>
              <Stack spacing={2}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Button
                    variant="outlined"
                    onClick={handleCheckDetectAppHealth}
                    disabled={checkingDetectAppHealth}
                    startIcon={checkingDetectAppHealth ? <CircularProgress size={20} color="inherit" /> : <FiberManualRecordIcon />}
                  >
                    {checkingDetectAppHealth ? 'Checking...' : 'Check Detect App'}
                  </Button>
                </Stack>
                
                {detectAppHealth && (
                  <Stack spacing={1} sx={{ p: 2, bgcolor: 'rgba(0, 0, 0, 0.02)', borderRadius: 1 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip
                        size="small"
                        label={detectAppHealth.status === 'healthy' ? 'Online' : 'Offline'}
                        color={detectAppHealth.status === 'healthy' ? 'success' : 'error'}
                      />
                    </Stack>
                    {detectAppHealth.message && (
                      <Typography variant="caption" color="text.secondary">
                        {detectAppHealth.message}
                      </Typography>
                    )}
                  </Stack>
                )}
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
        </>
      )}

      {mode === 'detection-settings' && detectionSettingsTab === 'parameters' && (
        <>
          {/* Detection Settings */}
          <Card elevation={2} sx={{ mb: 4 }}>
        <CardContent>
          {detectionConfigError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {detectionConfigError}
            </Alert>
          )}
          {detectionLoading ? (
            <LinearProgress />
          ) : (
            <>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      p: 2,
                      mb: 3,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      backgroundColor: 'background.paper',
                    }}
                  >
                <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 2 }}>
                  Face Detection Configuration
                </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    type="number"
                    label="Face Confidence (0-1)"
                    value={faceConfidence}
                    onChange={(e) => setFaceConfidence(e.target.value)}
                    fullWidth
                    placeholder={String(CONFIDENCE_DEFAULT)}
                    InputProps={{ inputProps: { step: 0.01, min: 0, max: 1 } }}
                    helperText="Minimum detection confidence before sending a face crop (default 0.83)."
                  />
                </Grid>
                {/* <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    type="number"
                    label="Pose Confidence (0-1)"
                    value={poseConfidence}
                    onChange={(e) => setPoseConfidence(e.target.value)}
                    fullWidth
                    placeholder={String(CONFIDENCE_DEFAULT)}
                    InputProps={{ inputProps: { step: 0.01, min: 0, max: 1 } }}
                    helperText="Confidence required for pose keypoints when gating faces (default 0.83)."
                  />
                </Grid> */}
                {/* <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    type="number"
                    label="Forward Face Confidence (0-1)"
                    value={forwardFaceConfidence}
                    onChange={(e) => setForwardFaceConfidence(e.target.value)}
                    fullWidth
                    placeholder={String(CONFIDENCE_DEFAULT)}
                    InputProps={{ inputProps: { step: 0.01, min: 0, max: 1 } }}
                    helperText="Confidence required for forward-facing faces (default 0.83)."
                  />
                </Grid> */}
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    type="number"
                    label="Recognition Cooldown (seconds)"
                    value={recognitionInterval}
                    onChange={(e) => setRecognitionInterval(e.target.value)}
                    fullWidth
                    placeholder="0.0"
                    InputProps={{ inputProps: { step: 0.1, min: 0 } }}
                    helperText="Minimum seconds between recognition events for banners."
                  />
                </Grid>
                </Grid>
                </Box>

                {/* <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    type="number"
                    label="Possible Match Threshold (0-1)"
                    value={possibleMatchThreshold}
                    onChange={(e) => setPossibleMatchThreshold(e.target.value)}
                    fullWidth
                    placeholder="0.38"
                    InputProps={{ inputProps: { step: 0.01, min: 0, max: 1 } }}
                    helperText="Minimum score for possible match. Score between this and similarity threshold."
                  />
                </Grid> */}
                <Grid item xs={12} sm={12} md={12} sx={{ flexBasis: '100%', maxWidth: '100%' }}>
                  <Box
                    sx={{
                      width: '100%',
                      display: 'block',
                      p: 2,
                      mb: 3,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      backgroundColor: 'background.paper',
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 0.5 }}>
                      Picture quality threshold
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Set the minimum image quality to be captured for recognition.
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                          type="number"
                          label="Min Crop Width (px)"
                          value={minCropWidth}
                          onChange={(e) => setMinCropWidth(e.target.value)}
                          fullWidth
                          placeholder="125"
                          InputProps={{ inputProps: { step: 1, min: 1 } }}
                          helperText="Reject face crops below this width."
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                          type="number"
                          label="Min Crop Height (px)"
                          value={minCropHeight}
                          onChange={(e) => setMinCropHeight(e.target.value)}
                          fullWidth
                          placeholder="125"
                          InputProps={{ inputProps: { step: 1, min: 1 } }}
                          helperText="Reject face crops below this height."
                        />
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>
              </Grid>
              {TRACKER_FIELD_CONFIG.length > 0 && (
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  {TRACKER_FIELD_CONFIG.map((field) => (
                    <Grid item xs={12} sm={6} md={4} key={field.name}>
                      <TextField
                        type="number"
                        label={field.label}
                        value={trackerSettings[field.name] ?? ''}
                        onChange={handleTrackerInputChange(field.name)}
                        fullWidth
                        placeholder={String(TRACKER_DEFAULTS[field.name])}
                        InputProps={{
                          inputProps: {
                            step: field.step,
                          },
                        }}
                      />
                    </Grid>
                  ))}
                </Grid>
              )}
              <Box
                sx={{
                  p: 2,
                  mb: 3,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  backgroundColor: 'background.paper',
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 2 }}>
                  Unmatched Logs Grouping Threshold
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      type="number"
                      label="Min Merge Group Embedding Threshold (0-1)"
                      value={minMergeGroupEmbeddingThreshold}
                      onChange={(e) => setMinMergeGroupEmbeddingThreshold(e.target.value)}
                      fullWidth
                      placeholder="0.8"
                      InputProps={{ inputProps: { step: 0.01, min: 0, max: 1 } }}
                      helperText="If max similarity is >= this, group_id is attached and embedding is merged."
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      type="number"
                      label="Max Threshold Group Creation (0-1)"
                      value={maxThresholdGroupCreation}
                      onChange={(e) => setMaxThresholdGroupCreation(e.target.value)}
                      fullWidth
                      placeholder="0.4"
                      InputProps={{ inputProps: { step: 0.01, min: 0, max: 1 } }}
                      helperText="If max similarity is <= this, a new unmatched group is created."
                    />
                  </Grid>
                </Grid>
              </Box>
              <Box
                sx={{
                  p: 2,
                  mb: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  backgroundColor: 'background.paper',
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 2 }}>
                  Possible Match Threshold
                </Typography>
                <Stack spacing={2} alignItems="flex-start">
                  <Box sx={{ width: { xs: '100%', md: '50%' } }}>
                    <TextField
                      type="number"
                      label="Possible Match Threshold (0-1)"
                      value={possibleMatchThreshold}
                      onChange={(e) => setPossibleMatchThreshold(e.target.value)}
                      fullWidth
                      placeholder="0.38"
                      InputProps={{ inputProps: { step: 0.01, min: 0, max: 1 } }}
                      helperText="Minimum score for possible match. Score between this and similarity threshold."
                    />
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={possibleMatchSendAttendance}
                        onChange={(e) => setPossibleMatchSendAttendance(e.target.checked)}
                      />
                    }
                    label="Send Attendance for Possible Matches"
                  />
                </Stack>
              </Box>
              <Stack direction="row" spacing={2} sx={{ mb: 3, flexWrap: 'wrap', rowGap: 1 }}>
                {TRACKER_BOOL_FIELDS.map((field) => (
                  <FormControlLabel
                    key={field.name}
                    control={
                      <Switch
                        checked={Boolean(trackerSettings[field.name])}
                        onChange={handleTrackerToggleChange(field.name)}
                      />
                    }
                    label={field.label}
                  />
                ))}
              </Stack>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  onClick={handleOpenSaveDetectionConfirm}
                  disabled={detectionConfigSaving}
                  startIcon={detectionConfigSaving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                >
                  {detectionConfigSaving ? 'Saving...' : 'Save Detection Settings'}
                </Button>
              </Box>
            </>
          )}
        </CardContent>
      </Card>
        </>
      )}

      {mode === 'face-embedding-config' && (
        <>
          {/* Face Embedding Configuration */}
          <Card elevation={2} sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Face Embedding Configuration
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select which face angles to load for recognition. 
            You can select multiple angles independently.
          </Typography>
          
          {embeddingConfigLastUpdate.updated_by && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2,}}>
              <strong>Last updated by:</strong> {embeddingConfigLastUpdate.updated_by}
              {embeddingConfigLastUpdate.updated_at && ` ( ${formatDateWithSeconds(embeddingConfigLastUpdate.updated_at)} )`}
            </Typography>
          )}
          
          {embeddingConfigNeedsRestart && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>Note:</strong> Changes to this configuration are saved to the database but require an 
                <strong> application restart</strong> to take effect. This applies 
                system-wide on the next startup.
              </Typography>
            </Alert>
          )}
          
          <Stack spacing={2} sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={true}
                  onChange={handleEmbeddingConfigChange('custom')}
                  disabled={true}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    Custom Faces
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Load all OTHER face angles (non-standard angles like train)
                  </Typography>
                </Box>
              }
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={embeddingConfigs.front.faiss_load}
                  onChange={handleEmbeddingConfigChange('front')}
                  disabled={embeddingConfigLoading}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    Front Faces
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Load all front-facing employee faces
                  </Typography>
                </Box>
              }
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={embeddingConfigs.left_profile.faiss_load}
                  onChange={handleEmbeddingConfigChange('left_profile')}
                  disabled={embeddingConfigLoading}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    Left Profile Faces
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Load all left-facing employee faces
                  </Typography>
                </Box>
              }
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={embeddingConfigs.right_profile.faiss_load}
                  onChange={handleEmbeddingConfigChange('right_profile')}
                  disabled={embeddingConfigLoading}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    Right Profile Faces
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Load all right-facing employee faces
                  </Typography>
                </Box>
              }
            />
          </Stack>
          
          {embeddingStats && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Current Stats:</strong> {embeddingStats.faiss_index_size || embeddingStats.faiss_total || 0} faces loaded.
              </Typography>
              {embeddingStats.total > 0 && (
                <Typography variant="caption" display="block">
                  Total in database: {embeddingStats.total} faces
                  {embeddingStats.filtered > 0 && ` (${embeddingStats.filtered} filtered out)`}
                </Typography>
              )}
            </Alert>
          )}
          
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              onClick={handleSaveEmbeddingConfig}
              disabled={embeddingConfigLoading}
              startIcon={embeddingConfigLoading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            >
              {embeddingConfigLoading ? 'Saving...' : 'Save Configuration'}
            </Button>
          </Box>
        </CardContent>
      </Card>
        </>
      )}

      {mode === 'late-entry-config' && (
        <>
          {/* Late Entry Time Configuration */}
          <Card elevation={2} sx={{ mb: 4 }}>
            <CardContent sx={{ p: 3 }}>
              {/* Header */}
              <Typography variant="h6" sx={{ mb: 1 }}>
                Late Entry Time Configuration
              </Typography>

              {/* Metadata */}
              {lateEntryMetadata.last_updated_by && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 3 }}
                >
                  <strong>Last updated by:</strong>{' '}
                  {lateEntryMetadata.last_updated_by}
                  {lateEntryMetadata.last_updated_at &&
                    ` (${formatDateWithSeconds(
                      lateEntryMetadata.last_updated_at
                    )})`}
                </Typography>
              )}

              {/* Error */}
              {lateEntryError && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {lateEntryError}
                </Alert>
              )}

              {/* Loader */}
              {lateEntryLoading ? (
                <LinearProgress />
              ) : (
                <>
                  {/* Time (left) + Late entry tracking (right) */}
                  <Grid
                    container
                    spacing={2}
                    sx={{ mb: 3 }}
                    alignItems="flex-start"
                  >
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Late Entry Time"
                        type="time"
                        value={lateEntryTime}
                        onChange={(e) => setLateEntryTime(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ step: 300 }} // 5 min interval
                        disabled={!lateEntryActive}
                        helperText={`Select time (24-hour format). ${lateEntryActive ? 'Late entry tracking is enabled and late Entry Card is displayed.' : 'Late entry tracking is disabled and late Entry Card is hidden.'}`}  
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box
                        sx={{
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'flex-start',
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Switch
                              checked={lateEntryActive}
                              onChange={(e) =>
                                setLateEntryActive(e.target.checked)
                              }
                              color="primary"
                            />
                          }
                          label="Late entry tracking"
                        />
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 0.5 }}
                        >
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>

                  {/* Action */}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      pt: 1,
                    }}
                  >
                    <Button
                      variant="contained"
                      onClick={handleOpenLateEntryConfirm}
                      disabled={lateEntrySaving || (lateEntryActive && !lateEntryTime)}
                      startIcon={
                        lateEntrySaving ? (
                          <CircularProgress size={18} color="inherit" />
                        ) : (
                          <SaveIcon />
                        )
                      }
                    >
                      {lateEntrySaving ? 'Saving...' : 'Save late entry settings'}
                    </Button>
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        </>
      )}


      {/* Dynamic Grid of All Cameras - HIDDEN */}
      {/* 
      {streams.length === 0 ? (
        <Typography variant="body1" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
          No cameras configured. Use the form above to add a new camera stream.
        </Typography>
      ) : (
        <Grid container spacing={3}>
          {streams.map((stream) => (
            <Grid item xs={12} md={6} lg={6} key={stream.id}>
              <Card elevation={2} sx={{ height: '100%', minHeight: 600 }}>
                <CardContent sx={{ p: 2 }}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ mb: 1.5 }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {stream.label}
                      </Typography>
                      <Chip
                        label={stream.camera_type || "IN"}
                        size="small"
                        color={stream.camera_type === "OUT" ? "secondary" : "primary"}
                        variant="outlined"
                        sx={{ height: 20, fontSize: '0.65rem' }}
                      />
                      {stream.is_saved && (
                        <Chip
                          label="Saved"
                          size="small"
                          color="success"
                          variant="outlined"
                          sx={{ height: 20, fontSize: '0.65rem' }}
                        />
                      )}
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center">
                      {activeStreams.has(stream.id) ? (
                        <Button
                          size="small"
                          variant="contained"
                          color="error"
                          startIcon={<StopIcon />}
                          onClick={() => handleCancelCamera(stream.id)}
                          sx={{ fontSize: '0.75rem', py: 0.5, px: 1.5 }}
                        >
                          Cancel
                        </Button>
                      ) : (
                        <Button
                          size="small"
                          variant="contained"
                          color="success"
                          startIcon={<PlayArrowIcon />}
                          onClick={() => handleViewCamera(stream.id)}
                          sx={{ fontSize: '0.75rem', py: 0.5, px: 1.5 }}
                        >
                          View
                        </Button>
                      )}
                      {!stream.is_saved && (
                        <Button
                          size="small"
                          variant="contained"
                          color="primary"
                          startIcon={savingCameraId === stream.id ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
                          onClick={() => handleSaveCamera(stream)}
                          disabled={savingCameraId === stream.id}
                          sx={{ fontSize: '0.75rem', py: 0.5, px: 1.5 }}
                        >
                          {savingCameraId === stream.id ? 'Saving...' : 'Save'}
                        </Button>
                      )}
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteCamera(stream)}
                        disabled={deletingCameraId === stream.id}
                        sx={{ p: 0.5 }}
                        title="Delete camera"
                      >
                        {deletingCameraId === stream.id ? (
                          <CircularProgress size={18} color="inherit" />
                        ) : (
                          <DeleteIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Stack>
                  </Stack>

                  <Box sx={{
                    width: '100%',
                    height: 400,
                    maxHeight: 400,
                    minHeight: 400,
                    position: 'relative',
                    overflow: 'hidden',
                    backgroundColor: '#000',
                    borderRadius: 1,
                  }}>
                    <WebRTCPlayer
                      ref={(el) => {
                        if (el) {
                          playerRefs.current[stream.id] = el;
                        }
                      }}
                      cameraId={stream.id}
                      apiBaseUrl={baseURL}
                      fixedHeight={400}
                      autoStart={false}
                    />
                  </Box>

                  <Stack
                    direction="row"
                    spacing={2}
                    sx={{
                      mt: 2,
                      p: 1.5,
                      backgroundColor: 'rgba(0, 0, 0, 0.02)',
                      borderRadius: 1,
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        RTSP URL
                      </Typography>
                      <Typography variant="body2" noWrap title={stream.rtsp_url} sx={{ fontSize: '0.8rem' }}>
                        {stream.rtsp_url}
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
      */}

      {/* ROI Drawer Dialog */}
      <ROIDrawer
        open={roiDrawerOpen}
        onClose={handleCloseROIDrawer}
        cameraType={roiDrawerCameraType}
        cameraLabel={roiDrawerCameraLabel}
        imageUrl={roiFrameImage}
        existingRoi={roiExisting}
        existingLaneRois={roiExistingLaneRois}
        laneCount={roiDrawerCameraType ? laneCount[roiDrawerCameraType] : 0}
        onLaneCountSet={(laneValue) => handleSaveLaneCount(roiDrawerCameraType, laneValue, roiDrawerCameraId)}
        onSave={handleSaveROI}
        onFetchFrame={handleFetchROIFrame}
        loading={roiFetching || roiSaving}
      />

      <FaceCropModal
        open={faceCropOpen}
        onClose={handleCloseFaceCrop}
        cameraType={faceCropCameraType}
        cameraLabel={faceCropCameraLabel}
        imageUrl={faceCropFrameImage}
        existingRoi={faceCropExistingRoi}
        initialConfig={faceCropConfig}
        onFetchFrame={handleFetchFaceCropFrame}
        onSave={handleSaveFaceCrop}
        loading={faceCropLoading}
        saving={faceCropSaving}
      />

      {/* Add Camera Modal */}
      <Dialog
        open={addCameraModalOpen}
        onClose={handleCloseAddCamera}
        maxWidth="sm"
        fullWidth
        fullScreen={false}
        PaperProps={{
          sx: {
            borderRadius: 2,
          }
        }}
      >
        <DialogTitle sx={{ 
          fontSize: { xs: '1.1rem', sm: '1.25rem' },
          py: { xs: 1.5, sm: 2 },
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between' 
        }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Add New Camera
          </Typography>
          <IconButton onClick={handleCloseAddCamera} size="small" sx={{ bgcolor: '#f1f5f9', '&:hover': { bgcolor: '#e2e8f0' } }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        
        <DialogContent dividers sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack spacing={2.5} mt={1}>
            {addCameraError && (
              <Alert severity="error" onClose={() => setAddCameraError('')}>
                {addCameraError}
              </Alert>
            )}

            <Stack spacing={2}>
              <TextField
                label="Camera Label *"
                value={newCamera.label}
                onChange={handleNewCameraChange('label')}
                required
                fullWidth
                size="small"
                placeholder="Enter camera label"
                disabled={addCameraLoading}
              />
              <TextField
                label="Description"
                value={newCamera.description}
                onChange={handleNewCameraChange('description')}
                fullWidth
                size="small"
                placeholder="Enter description (optional)"
                disabled={addCameraLoading}
              />

              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Stream URLs
                </Typography>
                <Button
                  onClick={handleAddStreamUrlField}
                  startIcon={<AddCircleOutlineIcon />}
                  size="small"
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                  disabled={addCameraLoading}
                >
                  Add Stream URL
                </Button>
              </Stack>

              <Stack spacing={1.5}>
                {newCamera.stream_urls.map((streamUrl, index) => (
                  <Stack
                    key={`stream-url-${index}`}
                    direction="row"
                    spacing={1}
                    alignItems="center"
                  >
                    <DragIndicatorIcon sx={{ color: 'text.disabled', fontSize: 20 }} />
                    <TextField
                      label={`Stream URL ${index + 1}`}
                      value={streamUrl}
                      onChange={handleStreamUrlChange(index)}
                      required={index === 0}
                      fullWidth
                      size="small"
                      placeholder={`rtsp://192.168.1.101/stream${index + 1}`}
                      disabled={addCameraLoading}
                    />
                    <IconButton
                      onClick={handleRemoveStreamUrlField(index)}
                      size="small"
                      color="error"
                      disabled={addCameraLoading}
                      title="Remove stream URL"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                ))}
              </Stack>

              <TextField
                label="Sub Stream URL *"
                value={newCamera.sub_stream_url || ''}
                onChange={handleNewCameraChange('sub_stream_url')}
                required
                fullWidth
                size="small"
                placeholder="rtsp://192.168.1.101/substream"
                disabled={addCameraLoading}
              />

              {(addCameraTestLoading || addCameraTestError || addCameraTestImage) && (
                <Box>
                  {addCameraTestLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                      <CircularProgress size={28} />
                    </Box>
                  ) : addCameraTestError ? (
                    <Alert severity="error" onClose={() => setAddCameraTestError('')}>
                      {addCameraTestError}
                    </Alert>
                  ) : (
                    <Box
                      sx={{
                        width: '100%',
                        maxHeight: 200,
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'divider',
                        bgcolor: 'grey.100',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                      }}
                    >
                      <Box
                        component="img"
                        src={addCameraTestImage}
                        alt="RTSP test snapshot"
                        sx={{
                          maxWidth: '100%',
                          maxHeight: 200,
                          objectFit: 'contain',
                          display: 'block',
                        }}
                      />
                    </Box>
                  )}
                </Box>
              )}
            </Stack>
          </Stack>
        </DialogContent>

        <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider', bgcolor: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Button
            onClick={handleAddCameraTest}
            variant="outlined"
            disabled={addCameraLoading || addCameraTestLoading}
            startIcon={addCameraTestLoading ? <CircularProgress size={16} color="inherit" /> : undefined}
            sx={{ textTransform: 'none', px: 3 }}
          >
            {addCameraTestLoading ? 'Testing...' : 'Test'}
          </Button>
          <Stack direction="row" spacing={1}>
            <Button 
              onClick={handleCloseAddCamera} 
              variant="outlined"
              disabled={addCameraLoading}
              sx={{ textTransform: 'none', px: 3 }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddCamera}
              variant="contained"
              disabled={addCameraLoading}
              startIcon={addCameraLoading ? <CircularProgress size={16} color="inherit" /> : undefined}
              sx={{ textTransform: 'none', px: 3 }}
            >
              {addCameraLoading ? 'Saving...' : 'Save'}
            </Button>
          </Stack>
        </Box>
      </Dialog>

      {/* Edit Camera Modal */}
      <Dialog
        open={editCameraModalOpen}
        onClose={handleCloseEditCamera}
        maxWidth="sm"
        fullWidth
        fullScreen={false}
        PaperProps={{
          sx: {
            borderRadius: 2,
          }
        }}
      >
        <DialogTitle sx={{
          fontSize: { xs: '1.1rem', sm: '1.25rem' },
          py: { xs: 1.5, sm: 2 },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Edit Camera
          </Typography>
          <IconButton onClick={handleCloseEditCamera} size="small" sx={{ bgcolor: '#f1f5f9', '&:hover': { bgcolor: '#e2e8f0' } }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack spacing={2.5} mt={1}>
            {editCameraError && (
              <Alert severity="error" onClose={() => setEditCameraError('')}>
                {editCameraError}
              </Alert>
            )}

            <TextField
              label="Camera Label *"
              value={editCamera.label}
              onChange={handleEditCameraChange('label')}
              required
              fullWidth
              size="small"
              placeholder="Enter camera label"
              disabled={editCameraLoading}
            />

            <TextField
              label="Description"
              value={editCamera.description}
              onChange={handleEditCameraChange('description')}
              fullWidth
              size="small"
              placeholder="Enter description (optional)"
              disabled={editCameraLoading}
            />

            <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Stream URLs
              </Typography>
              <Button
                onClick={handleAddEditStreamUrlField}
                startIcon={<AddCircleOutlineIcon />}
                size="small"
                sx={{ textTransform: 'none', fontWeight: 600 }}
                disabled={editCameraLoading}
              >
                Add Stream URL
              </Button>
            </Stack>

            <Stack spacing={1.5}>
              {editCamera.streams.map((stream, index) => (
                <Stack
                  key={`edit-stream-url-${index}`}
                  direction="row"
                  spacing={1}
                  alignItems="center"
                >
                  <DragIndicatorIcon sx={{ color: 'text.disabled', fontSize: 20 }} />
                  <TextField
                    label={`Stream URL ${index + 1}`}
                    value={stream.stream_url}
                    onChange={handleEditStreamUrlChange(index)}
                    required={index === 0}
                    fullWidth
                    size="small"
                    placeholder={`rtsp://192.168.1.101/stream${index + 1}`}
                    disabled={editCameraLoading}
                  />
                  <IconButton
                    onClick={handleRemoveEditStreamUrlField(index)}
                    size="small"
                    color="error"
                    disabled={editCameraLoading}
                    title="Remove stream URL"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Stack>
              ))}
            </Stack>

            <TextField
              label="Sub Stream URL *"
              value={editCamera.sub_stream_url || ''}
              onChange={handleEditCameraChange('sub_stream_url')}
              required
              fullWidth
              size="small"
              placeholder="rtsp://192.168.1.101/substream"
              disabled={editCameraLoading}
            />
          </Stack>
        </DialogContent>

        <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider', bgcolor: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box></Box>
          <Stack direction="row" spacing={1}>
            <Button
              onClick={handleCloseEditCamera}
              variant="outlined"
              disabled={editCameraLoading}
              sx={{ textTransform: 'none', px: 3 }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveEditedCamera}
              variant="contained"
              disabled={editCameraLoading}
              startIcon={editCameraLoading ? <CircularProgress size={16} color="inherit" /> : undefined}
              sx={{ textTransform: 'none', px: 3 }}
            >
              {editCameraLoading ? 'Saving...' : 'Save'}
            </Button>
          </Stack>
        </Box>
      </Dialog>

      {/* Camera Detail Modal */}
      <Dialog
        open={cameraDetailModalOpen}
        onClose={handleCloseCameraDetail}
        maxWidth="md"
        fullWidth
        fullScreen={false}
        PaperProps={{
          sx: {
            borderRadius: 2,
          }
        }}
      >
        {selectedCamera && (
          <>
            <DialogTitle sx={{ 
              fontSize: { xs: '1.1rem', sm: '1.25rem' }, 
              py: { xs: 1.5, sm: 2 }, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between' 
            }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  {!isMobile && (
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: '50%',
                        bgcolor: isCameraAssigned(selectedCamera) ? '#dcfce7' : '#f3f4f6',
                        color: isCameraAssigned(selectedCamera) ? '#166534' : '#6b7280',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <VideocamIcon sx={{ fontSize: 24 }} />
                    </Box>
                  )}
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {selectedCamera.label || `Camera ${selectedCamera.id}`}
                    </Typography>
                    {selectedCamera.rtsp_url && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography 
                          variant="body2" 
                          color="text.secondary"
                          sx={{ 
                            fontFamily: 'monospace',
                            fontSize: '0.75rem',
                            wordBreak: 'break-all',
                            flex: 1,
                          }}
                        >
                          {maskRtspUrl(selectedCamera.rtsp_url, showDetailRtspPassword)}
                        </Typography>
                        <IconButton
                          onClick={() => setShowDetailRtspPassword(!showDetailRtspPassword)}
                          size="small"
                          sx={{ 
                            padding: 0.5,
                            '&:hover': {
                              backgroundColor: 'action.hover'
                            }
                          }}
                        >
                          {showDetailRtspPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </Box>
                    )}
                  </Box>
                </Stack>
              </Stack>
              <IconButton onClick={handleCloseCameraDetail} size="small" sx={{ bgcolor: '#f1f5f9', '&:hover': { bgcolor: '#e2e8f0' }, flexShrink: 0 }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </DialogTitle>
            
            <DialogContent dividers sx={{ p: { xs: 2, sm: 3 } }}>
              <Stack spacing={3}>
                {/* Preview Section */}
                <Box sx={{ position: 'relative' }}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="flex-end"
                    spacing={1}
                    sx={{ mb: 1.5 }}
                  >
                    {previewHealth?.mode === 'mse' && configuredNavIndex >= 0 && selectedCamera && (
                      <FormControl size="small" sx={{ minWidth: 88 }}>
                        <Select
                          value={normalizePreviewQuality(selectedCamera, livePreviewQuality)}
                          onChange={(e) => handleLiveQualityChange(e.target.value)}
                          inputProps={{ 'aria-label': 'Preview quality' }}
                          sx={{ fontSize: '0.75rem', bgcolor: '#fff' }}
                        >
                          {getPreviewQualityOptions(getOrderedCameraStreams(selectedCamera).length).map((q) => (
                            <MenuItem key={q} value={q} sx={{ fontSize: '0.75rem', minHeight: 36 }}>
                              {PREVIEW_QUALITY_LABELS[q]}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={handleRefreshPreview}
                      disabled={
                        previewLoading
                        || (
                          Boolean(selectedCamera?.id && cameraStatusLoading[selectedCamera.id])
                          && !isDeepStreamRoiPreviewCamera(selectedCamera)
                        )
                      }
                      startIcon={
                        selectedCamera?.id
                        && cameraStatusLoading[selectedCamera.id]
                        && !isDeepStreamRoiPreviewCamera(selectedCamera)
                          ? <CircularProgress size={16} color="inherit" />
                          : <RefreshIcon />
                      }
                      sx={{ textTransform: 'none' }}
                    >
                      Refresh
                    </Button>
                  </Stack>
                  <Box
                    ref={previewZoomViewportRef}
                    onPointerDown={handlePreviewPointerDown}
                    onPointerMove={handlePreviewPointerMove}
                    onPointerUp={handlePreviewPointerUp}
                    onPointerCancel={handlePreviewPointerUp}
                    onDoubleClick={resetPreviewZoom}
                    sx={{
                      width: '100%',
                      height: 300,
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                      bgcolor: 'grey.100',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden',
                      position: 'relative',
                      cursor: previewZoom > 1 ? 'grab' : 'zoom-in',
                      touchAction: 'none',
                      userSelect: 'none',
                      '&:active': {
                        cursor: previewZoom > 1 ? 'grabbing' : 'zoom-in',
                      },
                      '&:fullscreen': {
                        width: '100vw',
                        height: '100vh',
                        borderRadius: 0,
                        border: 'none',
                        bgcolor: 'grey.900',
                      },
                    }}
                  >
                    {(previewHealth?.image && !previewError && (previewHealth.mode === 'mse-overlay' || previewHealth.mode === 'mse' || previewHealth.mode === 'ds-mse' || cameraDetailPreviewKindRef.current === 'health')) && (
                      <Stack
                        direction="row"
                        spacing={0.5}
                        onPointerDown={(e) => e.stopPropagation()}
                        sx={{
                          position: 'absolute',
                          bottom: 10,
                          right: 10,
                          zIndex: 3,
                          bgcolor: 'rgba(0,0,0,0.55)',
                          borderRadius: 1,
                          p: 0.25,
                        }}
                      >
                        {isLiveMsePreview && (
                          <Tooltip title={previewMuted ? 'Unmute preview' : 'Mute preview'}>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleTogglePreviewMute();
                              }}
                              aria-label={previewMuted ? 'Unmute preview' : 'Mute preview'}
                              sx={{
                                color: previewMuted ? '#90caf9' : '#fff',
                                bgcolor: previewMuted ? 'rgba(25,118,210,0.35)' : 'transparent',
                                '&:hover': { bgcolor: 'rgba(255,255,255,0.18)' },
                              }}
                            >
                              {previewMuted ? (
                                <VolumeOffIcon fontSize="small" />
                              ) : (
                                <VolumeUpIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Zoom out">
                          <span>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handlePreviewZoomBy(-PREVIEW_ZOOM_STEP);
                              }}
                              disabled={previewZoom <= PREVIEW_ZOOM_MIN}
                              sx={{ color: '#fff' }}
                              aria-label="Zoom out"
                            >
                              <ZoomOutIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Typography
                          variant="caption"
                          sx={{
                            color: '#fff',
                            minWidth: 40,
                            textAlign: 'center',
                            alignSelf: 'center',
                            fontWeight: 600,
                          }}
                        >
                          {Math.round(previewZoom * 100)}%
                        </Typography>
                        <Tooltip title={previewFullscreen ? 'Exit full screen' : 'Full screen'}>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              togglePreviewFullscreen();
                              resetPreviewZoom();
                            }}
                            sx={{ color: '#fff' }}
                            aria-label={previewFullscreen ? 'Exit full screen' : 'Full screen'}
                          >
                            {previewFullscreen ? (
                              <FullscreenExitIcon fontSize="small" />
                            ) : (
                              <FullscreenIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Zoom in">
                          <span>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handlePreviewZoomBy(PREVIEW_ZOOM_STEP);
                              }}
                              disabled={previewZoom >= PREVIEW_ZOOM_MAX}
                              sx={{ color: '#fff' }}
                              aria-label="Zoom in"
                            >
                              <ZoomInIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Reset zoom">
                          <span>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                resetPreviewZoom();
                              }}
                              disabled={previewZoom <= 1 && previewPan.x === 0 && previewPan.y === 0}
                              sx={{ color: '#fff' }}
                              aria-label="Reset zoom"
                            >
                              <RestartAltIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                    )}
                    {selectedCamera?.id
                    && cameraStatusLoading[selectedCamera.id]
                    && !isDeepStreamRoiPreviewCamera(selectedCamera) ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                        <CircularProgress />
                        <Typography variant="body2" color="text.secondary">
                          Checking camera status...
                        </Typography>
                      </Box>
                    ) : isCameraStatusOffline(selectedCamera)
                      && !isDeepStreamRoiPreviewCamera(selectedCamera) ? (
                      <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', px: 2 }}>
                        {CAMERA_OFFLINE_PREVIEW_MESSAGE}
                      </Typography>
                    ) : previewLoading ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                        <CircularProgress />
                        <Typography variant="body2" color="text.secondary">
                          Loading preview...
                        </Typography>
                      </Box>
                    ) : previewError ? (
                      <Box
                        sx={{
                          width: '100%',
                          height: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: 'grey.900',
                        }}
                      >
                        {typeof previewError === 'object' && previewError?.title ? (
                          <PreviewUnavailableNotice error={previewError} />
                        ) : (
                          <Alert severity="error" sx={{ m: 2 }}>
                            {previewError}
                          </Alert>
                        )}
                      </Box>
                    ) : previewHealth?.image && previewHealth.mode === 'ds-mse' ? (
                      <Box
                        sx={{
                          width: '100%',
                          height: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: 'grey.900',
                          transform: `translate(${previewPan.x}px, ${previewPan.y}px) scale(${previewZoom})`,
                          transformOrigin: 'center center',
                          transition: previewDragRef.current.active ? 'none' : 'transform 0.08s ease-out',
                        }}
                      >
                        <ConfiguredMsePlayer
                          key={previewSessionKey}
                          src={previewHealth.image}
                          muted={previewMuted}
                          onPlaying={handleLivePreviewLoad}
                          onError={handleLivePreviewError}
                        />
                      </Box>
                    ) : previewHealth?.mode === 'mse' && configuredNavIndex >= 0
                      && Object.keys(configuredPreviewWindow.streams).length > 0 ? (
                      <Box
                        sx={{
                          width: '100%',
                          height: '100%',
                          position: 'relative',
                          bgcolor: 'grey.900',
                          transform: `translate(${previewPan.x}px, ${previewPan.y}px) scale(${previewZoom})`,
                          transformOrigin: 'center center',
                          transition: previewDragRef.current.active ? 'none' : 'transform 0.08s ease-out',
                        }}
                      >
                        {Object.entries(configuredPreviewWindow.streams).map(([cameraId, slot]) => (
                          cameraId === configuredPreviewWindow.currentId && reuseCardPreviewInModal
                            ? null
                            : (
                              <Box
                                key={`${cameraId}-${livePreviewQuality}-${previewSessionKey}`}
                                sx={{
                                  position: 'absolute',
                                  inset: 0,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                <ConfiguredMsePlayer
                                  src={slot.url}
                                  hidden={cameraId !== configuredPreviewWindow.currentId}
                                  muted={
                                    previewMuted || cameraId !== configuredPreviewWindow.currentId
                                  }
                                  onPlaying={() => handleConfiguredSlotPlaying(cameraId)}
                                  onError={() => handleConfiguredSlotError(cameraId)}
                                />
                              </Box>
                            )
                        ))}
                        {reuseCardPreviewInModal && (
                          <SharedCameraStreamPreview
                            cameraId={selectedCamera.id}
                            viewportRefs={cardPreviewViewportRefs}
                            onError={(previewError) => setPreviewError(
                              previewError.message || 'Unable to display the shared camera preview',
                            )}
                          />
                        )}
                      </Box>
                    ) : previewHealth?.image && previewHealth.mode === 'mse' ? (
                      <Box
                        sx={{
                          width: '100%',
                          height: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: 'grey.900',
                          transform: `translate(${previewPan.x}px, ${previewPan.y}px) scale(${previewZoom})`,
                          transformOrigin: 'center center',
                          transition: previewDragRef.current.active ? 'none' : 'transform 0.08s ease-out',
                        }}
                      >
                        <ConfiguredMsePlayer
                          key={previewSessionKey}
                          src={previewHealth.image}
                          muted={previewMuted}
                          onPlaying={handleLivePreviewLoad}
                          onError={handleLivePreviewError}
                        />
                      </Box>
                    ) : previewHealth?.image && cameraDetailPreviewKindRef.current === 'health' ? (
                      <Stack spacing={1} sx={{ width: '100%', height: '100%' }}>
                        {previewHealth.detect_app_warning && (
                          <Alert severity="warning" sx={{ borderRadius: 1, flexShrink: 0 }}>
                            {previewHealth.detect_app_warning}
                          </Alert>
                        )}
                        <Box
                          sx={{
                            flex: 1,
                            minHeight: 220,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'hidden',
                            transform: `translate(${previewPan.x}px, ${previewPan.y}px) scale(${previewZoom})`,
                            transformOrigin: 'center center',
                          }}
                        >
                          <HealthPreviewWithROI
                            imageSrc={previewHealth.image}
                            roi={previewRoi || previewHealth.roi}
                            laneRois={
                              Object.keys(previewLaneRois || {}).length
                                ? previewLaneRois
                                : previewHealth.lane_rois
                            }
                            resolution={previewHealth.resolution}
                            laneCount={previewLaneCount}
                            alt="Camera preview"
                            showOverlay={Boolean(previewRoi || previewHealth.roi)}
                          />
                        </Box>
                      </Stack>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', px: 2 }}>
                        Preview is not available for this camera.
                      </Typography>
                    )}
                    {configuredNavIndex >= 0 && streams.length > 1 && (
                      <>
                        <Tooltip title={
                          (configuredNavIndex > 0
                            && configuredPreviewWindow.streams[streams[configuredNavIndex - 1]?.id]?.ready)
                            ? 'Previous camera (ready)' : 'Previous camera'
                        }>
                          <span
                            style={{
                              position: 'absolute',
                              left: 12,
                              top: '50%',
                              transform: 'translateY(-50%)',
                              zIndex: 2,
                            }}
                          >
                            <IconButton
                              onClick={() => navigateConfiguredPreview(-1)}
                              disabled={configuredNavIndex <= 0 || previewLoading}
                              aria-label="Previous camera"
                              sx={{
                                bgcolor: 'rgba(0, 0, 0, 0.55)',
                                color: (configuredNavIndex > 0
                                  && configuredPreviewWindow.streams[streams[configuredNavIndex - 1]?.id]?.ready)
                                  ? '#90caf9' : '#fff',
                                '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.75)' },
                                '&.Mui-disabled': {
                                  bgcolor: 'rgba(0, 0, 0, 0.2)',
                                  color: 'rgba(255, 255, 255, 0.35)',
                                },
                              }}
                            >
                              <ChevronLeftIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title={
                          (configuredNavIndex >= 0
                            && configuredNavIndex < streams.length - 1
                            && configuredPreviewWindow.streams[streams[configuredNavIndex + 1]?.id]?.ready)
                            ? 'Next camera (ready)' : 'Next camera'
                        }>
                          <span
                            style={{
                              position: 'absolute',
                              right: 12,
                              top: '50%',
                              transform: 'translateY(-50%)',
                              zIndex: 2,
                            }}
                          >
                            <IconButton
                              onClick={() => navigateConfiguredPreview(1)}
                              disabled={configuredNavIndex >= streams.length - 1 || previewLoading}
                              aria-label="Next camera"
                              sx={{
                                bgcolor: 'rgba(0, 0, 0, 0.55)',
                                color: (configuredNavIndex >= 0
                                  && configuredNavIndex < streams.length - 1
                                  && configuredPreviewWindow.streams[streams[configuredNavIndex + 1]?.id]?.ready)
                                  ? '#90caf9' : '#fff',
                                '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.75)' },
                                '&.Mui-disabled': {
                                  bgcolor: 'rgba(0, 0, 0, 0.2)',
                                  color: 'rgba(255, 255, 255, 0.35)',
                                },
                              }}
                            >
                              <ChevronRightIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Chip
                          size="small"
                          label={`${configuredNavIndex + 1} / ${streams.length}`}
                          sx={{
                            position: 'absolute',
                            bottom: 10,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            zIndex: 2,
                            bgcolor: 'rgba(0, 0, 0, 0.6)',
                            color: '#fff',
                            fontWeight: 600,
                          }}
                        />
                      </>
                    )}
                  </Box>
                </Box>

                {/* Technical + Status Details */}
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                      Technical Details
                    </Typography>
                    <Stack spacing={1}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Camera ID
                        </Typography>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {selectedCamera.id}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>

                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                      Status Information
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      <Chip
                        size="small"
                        label={getCameraAssignmentLabel(selectedCamera)}
                        sx={{
                          height: 28,
                          fontSize: '0.75rem',
                          bgcolor: isCameraAssigned(selectedCamera) ? '#dcfce7' : '#f3f4f6',
                          color: isCameraAssigned(selectedCamera) ? '#166534' : '#6b7280',
                          fontWeight: 600,
                        }}
                      />
                      {selectedCamera.rtsp_url && (
                        <Chip
                          size="small"
                          label="RTSP Stream"
                          variant="outlined"
                          sx={{ height: 28, fontSize: '0.75rem' }}
                        />
                      )}
                    </Stack>
                  </Box>
                </Stack>
              </Stack>
            </DialogContent>

            <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider', bgcolor: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                {selectedCamera.is_active && (
                  <Stack direction="row" spacing={1}>
                    {/* <Button
                      size="small"
                      variant="outlined"
                      color="warning"
                      onClick={() => {
                        handleOpenDeactivateConfirm(selectedCamera);
                      }}
                      sx={{ textTransform: 'none', px: 3 }}
                      disabled={updatingCameraId === selectedCamera.id}
                      startIcon={updatingCameraId === selectedCamera.id ? <CircularProgress size={16} /> : undefined}
                    >
                      Deactivate
                    </Button> */}
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      onClick={() => {
                        handleOpenDeleteConfirm(selectedCamera);
                      }}
                      sx={{ textTransform: 'none', px: 3 }}
                      disabled={updatingCameraId === selectedCamera.id}
                      startIcon={updatingCameraId === selectedCamera.id ? <CircularProgress size={16} /> : undefined}
                    >
                      Delete
                    </Button>
                  </Stack>
                )}
              </Box>
              <Button variant="outlined" size="small" onClick={handleCloseCameraDetail} sx={{ textTransform: 'none', px: 3 }}>
                Close
              </Button>
            </Box>
          </>
        )}
      </Dialog>

      <Dialog
        open={previewModalOpen}
        onClose={handleCloseCameraPreview}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          {previewCamera?.label || 'Camera Preview'}
        </DialogTitle>
        <DialogContent sx={{ py: 2 }}>
          {previewLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : previewError ? (
            typeof previewError === 'object' && previewError?.title ? (
              <Box
                sx={{
                  minHeight: 220,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'grey.900',
                  borderRadius: 1,
                }}
              >
                <PreviewUnavailableNotice error={previewError} />
              </Box>
            ) : (
              <Alert severity="error">{previewError}</Alert>
            )
          ) : previewHealth?.image ? (
            <Stack spacing={2}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip
                  size="small"
                  label={previewHealth.health_status === 'online' ? 'Online' : 'Offline'}
                  color={previewHealth.health_status === 'online' ? 'success' : 'error'}
                />
                {previewHealth.health_message && (
                  <Typography variant="body2" color="text.secondary">
                    {previewHealth.health_message}
                  </Typography>
                )}
              </Stack>
              {previewHealth.detect_app_warning && (
                <Alert severity="warning">{previewHealth.detect_app_warning}</Alert>
              )}
              <Box
                sx={{
                  width: '100%',
                  maxHeight: { xs: '60vh', md: '70vh' },
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'grey.100',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                }}
              >
                <HealthPreviewWithROI
                  imageSrc={previewHealth.image}
                  roi={previewRoi || previewHealth.roi}
                  laneRois={Object.keys(previewLaneRois || {}).length ? previewLaneRois : previewHealth.lane_rois}
                  resolution={previewHealth.resolution}
                  laneCount={previewLaneCount}
                  alt="Camera preview"
                  showOverlay
                />
              </Box>
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Preview is not available because the camera is currently offline.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ pt: 0, mt: -0.5 }}>
          <Button onClick={handleCloseCameraPreview} variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Save Detection Settings — confirmation (same pattern as UserManagement) */}
      <Dialog
        open={detectionSettingsConfirmDialog.open}
        onClose={() =>
          setDetectionSettingsConfirmDialog({ ...detectionSettingsConfirmDialog, open: false })
        }
      >
        <DialogTitle>{detectionSettingsConfirmDialog.title}</DialogTitle>
        <DialogContent>
          <Typography>{detectionSettingsConfirmDialog.message}</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setDetectionSettingsConfirmDialog({ ...detectionSettingsConfirmDialog, open: false })
            }
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (detectionSettingsConfirmDialog.onConfirm) {
                detectionSettingsConfirmDialog.onConfirm();
              }
              setDetectionSettingsConfirmDialog({ ...detectionSettingsConfirmDialog, open: false });
            }}
            variant="contained"
            color="primary"
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Camera Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleCloseDeleteConfirm}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'grey.50'
        }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                bgcolor: '#fef2f2',
                color: '#dc2626',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <DeleteIcon sx={{ fontSize: 24 }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Delete Camera
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This action cannot be undone
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>
        
        <DialogContent sx={{ p: 2 }}>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <Alert severity="warning" sx={{ mt: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Are you sure you want to delete this camera?
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {cameraToDelete?.label || `Camera ${cameraToDelete?.id}`}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                This will permanently remove the camera configuration and all associated data from the system.
              </Typography>
            </Alert>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button 
            onClick={handleCloseDeleteConfirm} 
            variant="outlined"
            disabled={deleteLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteCamera}
            variant="contained"
            color="error"
            disabled={deleteLoading}
            startIcon={deleteLoading ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
          >
            {deleteLoading ? 'Deleting...' : 'Delete Camera'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* De-Assign Camera Confirmation Dialog */}
      <Dialog
        open={deactivateConfirmOpen}
        onClose={handleCloseDeactivateConfirm}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'grey.50'
        }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                bgcolor: '#fef3c7',
                color: '#d97706',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <VideocamIcon sx={{ fontSize: 24 }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                De-Assign Camera
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This action can be reversed later
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>
        
        <DialogContent sx={{ p: 2 }}>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <Alert severity="warning" sx={{ mt: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Are you sure you want to de-assign this camera?
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {cameraToDeactivate?.label || `Camera ${cameraToDeactivate?.id}`}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                This will remove the camera assignment only. The camera will remain in Configured Cameras and no data will be deleted.
              </Typography>
            </Alert>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button 
            onClick={handleCloseDeactivateConfirm} 
            variant="outlined"
            disabled={updatingCameraId}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeactivateCameraConfirmed}
            variant="contained"
            color="warning"
            disabled={updatingCameraId}
            startIcon={updatingCameraId ? <CircularProgress size={16} color="inherit" /> : <VideocamIcon />}
          >
            {updatingCameraId ? 'De-Assigning...' : 'De-Assign Camera'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Late Entry Settings Confirmation Dialog */}
      <Dialog
        open={lateEntryConfirmOpen}
        onClose={handleCloseLateEntryConfirm}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
          }
        }}
      >
        <DialogTitle>
          Update Late Entry Settings
        </DialogTitle>
        <DialogContent>
          <Typography>
            {lateEntryActive
              ? `Are you sure you want to enable late entry tracking with threshold ${lateEntryTime}?`
              : 'Are you sure you want to disable late entry tracking?'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseLateEntryConfirm}
            disabled={lateEntrySaving}
          >
            CANCEL
          </Button>
          <Button
            onClick={handleConfirmSaveLateEntrySettings}
            variant="contained"
            color="primary"
            disabled={lateEntrySaving}
          >
            {lateEntrySaving ? 'SAVING...' : 'CONFIRM'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Multi-Camera Assignment Modal */}
      <Dialog
        open={assignModalOpen}
        onClose={handleCloseAssignModal}
        maxWidth="md"
        fullWidth
        fullScreen={false}
        PaperProps={{
          sx: {
            borderRadius: 2,
          }
        }}
      >
        <DialogTitle sx={{ 
          fontSize: { xs: '1.1rem', sm: '1.25rem' }, 
          py: { xs: 1.5, sm: 2 }, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between' 
        }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                bgcolor: assignCameraType === 'OUT' ? '#fce7f3' : '#e0f2fe',
                color: assignCameraType === 'OUT' ? '#be185d' : '#075985',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <VideocamIcon sx={{ fontSize: 24 }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Assign {assignCameraType} Cameras
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Select one stream URL per camera and assign as {assignCameraType}
              </Typography>
            </Box>
          </Stack>
          <IconButton onClick={handleCloseAssignModal} size="small" sx={{ bgcolor: '#f1f5f9', '&:hover': { bgcolor: '#e2e8f0' } }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: { xs: 2, sm: 3 } }}>
          {assignError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {assignError}
            </Alert>
          )}

          <Box sx={{ mt: 2 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Available Cameras ({streams.filter(s => !s.camera_type).length})
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleSelectAllCameras}
                  disabled={assignLoading}
                >
                  Select All
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleClearSelection}
                  disabled={assignLoading}
                >
                  Clear
                </Button>
              </Stack>
            </Stack>

            <Box sx={{ 
              maxHeight: 400,
              overflowY: 'auto', 
              pr: 1,
              '&::-webkit-scrollbar': {
                width: '6px',
              },
              '&::-webkit-scrollbar-track': {
                background: '#f1f1f1',
                borderRadius: '3px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#c1c1c1',
                borderRadius: '3px',
                '&:hover': {
                  background: '#a8a8a8',
                },
              },
            }}>
              {streams.filter(s => !s.camera_type).length === 0 ? (
                <Box sx={{ 
                  textAlign: 'center', 
                  py: 8,
                  px: 3,
                  mt: 3,
                  bgcolor: 'grey.50',
                  borderRadius: 3,
                  border: '2px dashed',
                  borderColor: 'grey.300'
                }}>
                  <VideocamIcon sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                    No Unassigned Cameras
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    All cameras have been assigned. Add new cameras to assign them types.
                  </Typography>
                </Box>
              ) : (
                <Box
                  sx={{
                    mt: 1.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2.5,
                    overflow: 'hidden',
                    bgcolor: 'background.paper',
                  }}
                >
                  {streams.filter(s => !s.camera_type).map((camera, cameraIndex) => (
                    <Box
                      key={camera.id}
                      onClick={() => handleCameraSelection(camera.id)}
                      sx={{
                        px: { xs: 1.5, sm: 2.5 },
                        py: 2,
                        cursor: 'pointer',
                        bgcolor: selectedCameras.includes(camera.id) ? 'rgba(64, 81, 138, 0.05)' : 'transparent',
                        borderBottom: cameraIndex < streams.filter(s => !s.camera_type).length - 1
                          ? '1px solid'
                          : 'none',
                        borderColor: 'divider',
                        transition: 'background-color 0.2s ease',
                        '&:hover': {
                          bgcolor: selectedCameras.includes(camera.id)
                            ? 'rgba(64, 81, 138, 0.08)'
                            : 'rgba(15, 23, 42, 0.02)',
                        },
                      }}
                    >
                      <Stack direction="row" alignItems="start" spacing={1.25}>
                        <Checkbox
                          checked={selectedCameras.includes(camera.id)}
                          onChange={() => handleCameraSelection(camera.id)}
                          onClick={(e) => e.stopPropagation()}
                          disabled={assignLoading}
                          sx={{
                            '&.Mui-checked': {
                              color: 'primary.main',
                            }
                          }}
                        />

                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant="subtitle1"
                            sx={{
                              fontWeight: 600,
                              fontSize: { xs: '1rem', sm: '1.05rem' },
                              lineHeight: 1.3,
                              color: selectedCameras.includes(camera.id) ? 'primary.dark' : 'text.primary',
                              mb: 0.35,
                              mt: 1.2
                            }}
                          >
                            {camera.label || `Camera ${camera.id}`}
                          </Typography>

                          {(() => {
                            const cameraUrls = getCameraStreamUrls(camera);
                            return cameraUrls.length > 0 ? (
                              <Stack spacing={0.4}>
                                {cameraUrls.map((url, urlIndex) => (
                                  <Stack
                                    key={`${camera.id}-stream-${urlIndex}`}
                                    direction="row"
                                    alignItems="center"
                                    spacing={1}
                                    onClick={(e) => e.stopPropagation()}
                                    sx={{ minHeight: 34 }}
                                  >
                                    <Radio
                                      size="small"
                                      checked={selectedCameraStreams[camera.id] === url}
                                      onChange={() => handleSelectedStreamChange(camera.id, url)}
                                      sx={{ p: 0.5 }}
                                    />
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontFamily: 'monospace',
                                        fontSize: { xs: '0.92rem', sm: '0.95rem' },
                                        lineHeight: 1.4,
                                        wordBreak: 'break-all',
                                        flex: 1,
                                        pr: 1,
                                      }}
                                    >
                                      {url}
                                    </Typography>
                                    <Tooltip title="Preview stream">
                                      <IconButton
                                        size="small"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleOpenRtspSnapshotPreview(camera, url);
                                        }}
                                        sx={{ p: 0.5 }}
                                      >
                                        <PlayCircleOutlineIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </Stack>
                                ))}
                              </Stack>
                            ) : (
                              <Typography variant="body2" color="error.main">
                                No stream URLs available for this camera
                              </Typography>
                            );
                          })()}
                        </Box>
                      </Stack>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>

        <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider', bgcolor: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box></Box>
          <Stack direction="row" spacing={1}>
            <Button 
              onClick={handleCloseAssignModal} 
              variant="outlined"
              disabled={assignLoading}
              sx={{ textTransform: 'none', px: 3 }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAssignCameras}
              variant="contained"
              disabled={selectedCameras.length === 0 || assignLoading}
              startIcon={assignLoading ? <CircularProgress size={16} /> : <CheckCircleIcon fontSize="small" />}
              sx={{ textTransform: 'none', px: 3 }}
            >
              {assignLoading ? 'Assigning...' : `Assign ${selectedCameras.length} Camera${selectedCameras.length !== 1 ? 's' : ''}`}
            </Button>
          </Stack>
        </Box>
      </Dialog>

    </Box>
  );
};

export default CameraStreams;
